import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation, useBlocker, Link } from 'react-router-dom';
import { stylePresets } from '@data/stylePresets';
import { getPublicPlanById, getPublicPlanByIdFromAPI, updatePublicPlan, addPublicPlan, pruneDemoPlans, type QuestionnaireData, type PublicPlan, type AIAnalysis, type AIGeneratedQuestion, type GardenUseKey, type ProjectFocusKey, type RecommendedComponent, type ComponentFeasibility, type DetectedFeature } from '@data/publicPlans';
import { generateAIPlan } from '@services/aiPlanGenerator';
import { stylePresetsByKey } from '@data/stylePresets';
import { analyzeGardenImage } from '@services/gardenAnalysis';
import { getComponentQuestions } from '@data/componentQuestions';
import { COMPONENT_DESCRIPTIONS } from '@data/componentDescriptions';
import { getStyleRecommendations, formatStyleRecommendations } from '@data/styleRecommendations';
import { calculateTotalCost, getBudgetStatus, getComponentCost } from '@data/componentCosts';
import { useAlertDialog } from '../../shared/AlertDialog';
import { createPlan, getPlans, updatePlanContent } from '@services/plans';
import { useAuth } from '@services/auth';
import { createIssue } from '@services/issues';
import { apiGet, apiPost } from '@services/api';
import { getPlanInviteByCode } from '@services/planInvites';
import { trackActivityEvent, usePageActivityTracker } from '@services/activityTracker';
import MagnifierAnimation from '../../shared/MagnifierAnimation';
import { getImageUrl } from '@utils/imageUrl';
import { buildAiQuestionnairePersistenceForPlanApi } from '@utils/planQuestionnairePersistence';
import { GUEST_PENDING_QUESTIONNAIRE_SESSION_KEY } from '@utils/guestPlanResume';
import PlanGenerationPuzzleAnimation from '../../shared/PlanGenerationPuzzleAnimation';
import { useI18n } from '../../i18n';
import { buildApiUrl, getApiBaseUrl } from '@utils/appBase';
const SHOW_BUDGET_QUESTIONS = false;
const SHOW_CONTACT_EMAIL_QUESTION = false;

function normalizeStoredPhotoUrl(rawUrl: string | null | undefined): string | null {
	if (!rawUrl) return null;
	const trimmed = rawUrl.trim();
	if (!trimmed) return null;
	if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;
	let normalized = getImageUrl(trimmed) || trimmed;
	normalized = normalized.replace('://agilos-t.com/w/web/garden-hub/', '://agilos-t.com/web/garden-hub/');
	normalized = normalized.replace('/api/api/', '/api/');
	return normalized;
}

// Helper component for help tooltips
function HelpTooltip({ text }: { text: string }) {
	const { language } = useI18n();
	const translated = (() => {
		if (language === 'en') return text;
		const dict: Record<string, { de: string; hr: string }> = {
			'Upload a photo and we will read visible features and rough size, then ask follow-up questions that fit what we see.': {
				de: 'Laden Sie ein Foto hoch. Wir erkennen sichtbare Merkmale und die ungefähre Größe und stellen dann passende Folgefragen.',
				hr: 'Učitajte fotografiju, a mi ćemo očitati vidljive značajke i približnu veličinu te postaviti odgovarajuća dodatna pitanja.'
			},
			'The style sets the overall aesthetic and guides plant selection, materials, and layout choices for your garden.': {
				de: 'Der Stil definiert die gesamte Ästhetik und steuert Pflanzenauswahl, Materialien und Layout-Entscheidungen für Ihren Garten.',
				hr: 'Stil određuje ukupnu estetiku i vodi odabir biljaka, materijala i rasporeda za vaš vrt.'
			},
			'The size helps us calculate material quantities, plant spacing, and design proportions for your garden.': {
				de: 'Die Größe hilft uns, Materialmengen, Pflanzabstände und Gestaltungsproportionen für Ihren Garten zu berechnen.',
				hr: 'Veličina nam pomaže izračunati količine materijala, razmake sadnje i proporcije dizajna za vaš vrt.'
			},
			'Your location helps us select climate-appropriate plants and understand local growing conditions. It also helps gardening companies find and respond to projects near them in the marketplace.': {
				de: 'Ihr Standort hilft uns, klima-geeignete Pflanzen auszuwählen und lokale Bedingungen zu berücksichtigen. Außerdem hilft er Gartenfirmen, nahe Projekte im Marktplatz zu finden und darauf zu reagieren.',
				hr: 'Vaša lokacija pomaže nam odabrati biljke prikladne klimi i razumjeti lokalne uvjete uzgoja. Također pomaže tvrtkama pronaći i odgovoriti na projekte u blizini na tržištu.'
			},
			'This helps us prioritize features and functions, ensuring the design meets your lifestyle and practical requirements.': {
				de: 'Das hilft uns, Funktionen und Elemente zu priorisieren, damit das Design zu Ihrem Lebensstil und Ihren praktischen Anforderungen passt.',
				hr: 'To nam pomaže dati prioritet značajkama i funkcijama kako bi dizajn odgovarao vašem načinu života i praktičnim potrebama.'
			},
			'Choose which features you\'d like in your garden. You can select multiple components.': {
				de: 'Wählen Sie, welche Funktionen Sie in Ihrem Garten möchten. Sie können mehrere Komponenten auswählen.',
				hr: 'Odaberite koje značajke želite u svom vrtu. Možete odabrati više komponenti.'
			},
			'Select all that apply. This helps us prioritise the right features (e.g. seating and circulation for hosting).': {
				de: 'Wählen Sie alles Zutreffende. Das hilft uns, die richtigen Elemente zu priorisieren (z. B. Sitzbereiche und Wege für Gäste).',
				hr: 'Odaberite sve što vrijedi. To nam pomaže odrediti prioritete pravih značajki (npr. sjedenje i komunikacije za druženje).'
			},
			'Include everyone who will regularly use the garden — e.g. household members, or future family if you\'re planning ahead. This helps us scale the design and choose safe, suitable features.': {
				de: 'Beziehen Sie alle ein, die den Garten regelmäßig nutzen — z. B. Haushaltsmitglieder oder zukünftige Familie. Das hilft uns, das Design richtig zu skalieren und sichere, passende Elemente auszuwählen.',
				hr: 'Uključite sve koji će redovito koristiti vrt — npr. članove kućanstva ili buduću obitelj. To pomaže prilagoditi dizajn i odabrati sigurne, prikladne značajke.'
			},
			'Slide left for more budget-friendly options (value materials, simpler designs). Slide right for premium options (higher-end materials, more elaborate features).': {
				de: 'Schieben Sie nach links für budgetfreundlichere Optionen (preiswerte Materialien, einfachere Designs). Schieben Sie nach rechts für Premium-Optionen (höherwertige Materialien, aufwendigere Elemente).',
				hr: 'Povucite ulijevo za povoljnije opcije (isplativi materijali, jednostavniji dizajn). Povucite udesno za premium opcije (kvalitetniji materijali, složenije značajke).'
			},
			'If you have a specific total budget in mind, enter it in euros. We\'ll use this together with your preference slider to tailor the plan.': {
				de: 'Wenn Sie ein bestimmtes Gesamtbudget haben, geben Sie es in Euro ein. Wir nutzen es zusammen mit Ihrem Präferenz-Schieberegler, um den Plan anzupassen.',
				hr: 'Ako imate određeni ukupni budžet, unesite ga u eurima. Koristit ćemo ga zajedno s klizačem preferencija za prilagodbu plana.'
			},
			'The earliest date you\'re okay for work on the garden to begin. Companies will see this when making offers.': {
				de: 'Das früheste Datum, an dem die Arbeiten im Garten beginnen können. Unternehmen sehen dies bei der Angebotserstellung.',
				hr: 'Najraniji datum kada vam odgovara početak radova u vrtu. Tvrtke će to vidjeti pri slanju ponuda.'
			},
			'We\'ll use this to send your garden plan and any updates.': {
				de: 'Wir verwenden dies, um Ihnen Ihren Gartenplan und alle Aktualisierungen zu senden.',
				hr: 'Koristit ćemo ovo za slanje vašeg vrtnog plana i svih ažuriranja.'
			},
			'This helps us select plants and features that match the time you can commit to garden upkeep.': {
				de: 'Das hilft uns, Pflanzen und Elemente auszuwählen, die zur Zeit passen, die Sie für die Gartenpflege aufwenden können.',
				hr: 'To nam pomaže odabrati biljke i značajke koje odgovaraju vremenu koje možete posvetiti održavanju vrta.'
			},
			'How much planting would you like? This helps us balance plants and flowers with open space in your garden design.': {
				de: 'Wie viel Bepflanzung wünschen Sie? Das hilft uns, Pflanzen und Blumen mit offenen Flächen in Ihrem Gartendesign auszubalancieren.',
				hr: 'Koliko sadnje želite? To nam pomaže uskladiti biljke i cvijeće s otvorenim prostorom u dizajnu vrta.'
			},
		};
		const match = dict[text];
		if (!match) return text;
		return language === 'de' ? match.de : match.hr;
	})();
	return (
		<i 
			className="bi bi-info-circle text-muted ms-1" 
			style={{ cursor: 'help', fontSize: '0.9em' }}
			data-bs-toggle="tooltip" 
			data-bs-placement="top" 
			title={translated}
			aria-label={`Help: ${translated}`}
		></i>
	);
}

/** Example photos for step 1 — files in `frontend/public/questionnaire/`. */
const QUESTIONNAIRE_EXAMPLE_PHOTO_FILES = [
	'emptygarden1.png',
	'emptygarden2.png',
	'emptygarden3.png',
	'emptygarden4.png',
	'emptygarden5.png',
	'emptygarden6.png'
];

function questionnaireExamplePhotoUrl(filename: string) {
	return `${import.meta.env.BASE_URL}questionnaire/${encodeURIComponent(filename)}`;
}

const QUESTIONNAIRE_EXAMPLE_PHOTO_URLS = QUESTIONNAIRE_EXAMPLE_PHOTO_FILES.map(questionnaireExamplePhotoUrl);
const STYLE_PRESET_IMAGE_URLS = stylePresets
	.map((style) => style.imageUrl)
	.filter((url): url is string => Boolean(url));

const COMPONENT_PREVIEW_FILES: Record<string, string[]> = {
	pool: ['pool.webp'],
	fence: ['fence2.webp'],
	bbq: ['bbq1.webp', 'bbq2.webp'],
	lounge: ['lounge-area1.webp', 'lounge-area2.webp', 'lounge-area3.webp', 'lounge-area4.webp'],
	'outdoor-kitchen': ['outdoor-kitchen1.webp', 'outdoor-kitchen2.webp'],
	'fire-pit': ['fire-pit1.webp', 'fire-pit2.webp'],
	pergola: ['pergola1.webp', 'pergola2.webp'],
	deck: ['deck1.webp', 'deck2.webp', 'deck3.webp', 'deck4.webp'],
	shed: ['shed1.webp', 'shed2.webp'],
	playground: ['play-area1.webp', 'play-area2.webp', 'play-area3.webp'],
	'vegetable-garden': ['vegetable-garden1.webp', 'vegetable-garden2.webp'],
	'water-feature': ['water-feature1.webp', 'water-feature2.webp', 'water-feature3.webp'],
	lighting: ['lighting1.webp', 'lighting2.webp', 'lighting3.webp', 'lighting4.webp', 'lighting5.webp'],
	pathway: ['pathway1.webp', 'pathway2.webp'],
	'retaining-wall': ['retaining-wall1.webp', 'retaining-wall2.webp'],
	driveway: ['driveway1.webp', 'driveway2.webp'],
	greenhouse: ['greenhouse1.webp', 'greenhouse2.webp'],
	'flower-beds': ['flower-beds.webp'],
	'hot-tub': ['hot-tub1.webp', 'hot-tub2.webp'],
	'pizza-oven': ['pizza-oven1.webp', 'pizza-oven2.webp'],
	compost: ['compost1.webp']
};

function componentPreviewUrl(filename: string) {
	return `${import.meta.env.BASE_URL}questionnaire/components-optimized/${encodeURIComponent(filename)}`;
}

const COMPONENT_PREVIEW_URLS: Record<string, string[]> = Object.fromEntries(
	Object.entries(COMPONENT_PREVIEW_FILES).map(([componentId, files]) => [
		componentId,
		files.map(componentPreviewUrl)
	])
);

const QUESTIONNAIRE_STEP_COUNT = 5;

function getComponentHeroImageUrl(componentId: string): string | undefined {
	return COMPONENT_PREVIEW_URLS[componentId]?.[0];
}

/** Per-component crop anchor so tall structures (roofs, beams) stay visible in the card header. */
const COMPONENT_HERO_BACKGROUND_POSITION: Record<string, string> = {
	// Tall structures: anchor toward top of photo so roofs / beams are not cropped off
	greenhouse: 'center 18%',
	shed: 'center 22%',
	pergola: 'center 12%'
};

function getComponentHeroBackgroundPosition(componentId: string): string {
	return COMPONENT_HERO_BACKGROUND_POSITION[componentId] ?? 'center';
}

/** Map saved draft steps from the old 3-step flow to the current 5-step flow. */
function migrateQuestionnaireDraftStep(savedStep: number, form: QuestionnaireData): number {
	if (savedStep <= 1) return savedStep;
	if (savedStep >= QUESTIONNAIRE_STEP_COUNT) return Math.min(savedStep, QUESTIONNAIRE_STEP_COUNT);
	// Legacy: step 3 was optional follow-up details
	if (savedStep === 3) return 5;
	// Legacy: step 2 bundled style, basics, components, and vision
	if (savedStep === 2) {
		const hasStyle = Boolean(form.styleKey);
		const hasBasics = (form.gardenSize ?? 0) > 0 && Boolean(form.location?.trim());
		const hasComponents = (form.selectedComponents?.length ?? 0) > 0;
		if (hasBasics && hasComponents) return 4;
		if (hasBasics) return 4;
		if (hasStyle) return 3;
		return 2;
	}
	return 2;
}

const STYLE_TEXT_LOCALIZATION: Record<
	string,
	{ deTitle: string; hrTitle: string; deDescription: string; hrDescription: string }
> = {
	modern: {
		deTitle: 'Modern',
		hrTitle: 'Moderan',
		deDescription: 'Klare Linien, minimalistische Bepflanzung.',
		hrDescription: 'Čiste linije, minimalistička sadnja.'
	},
	mediterranean: {
		deTitle: 'Mediterran',
		hrTitle: 'Mediteranski',
		deDescription: 'Sonnige Wärme: Terrakotta, Oliven, trockenheitsresistente Pflanzen, Schmiedeeisen.',
		hrDescription: 'Osunčana toplina: terakota, masline, biljke otporne na sušu, kovano željezo.'
	},
	minimalist: {
		deTitle: 'Minimalistisch',
		hrTitle: 'Minimalistički',
		deDescription: 'Schlichte Paletten, ruhige Räume.',
		hrDescription: 'Jednostavne palete, mirni prostori.'
	},
	cottage: {
		deTitle: 'Landhausstil',
		hrTitle: 'Cottage',
		deDescription: 'Informell, farbenfrohe Stauden.',
		hrDescription: 'Neformalno, šareno trajno bilje.'
	},
	japanese: {
		deTitle: 'Japanischer Zen',
		hrTitle: 'Japanski Zen',
		deDescription: 'Kontemplativ: geharkter Kies, Steine, Bambus, Wasser, minimale Bepflanzung.',
		hrDescription: 'Kontemplativno: grabljani šljunak, kamenje, bambus, voda, minimalna sadnja.'
	},
	tropical: {
		deTitle: 'Tropisch',
		hrTitle: 'Tropski',
		deDescription: 'Üppiges Laub, Palmen, markante Blätter und lebendige Akzente.',
		hrDescription: 'Bujno lišće, palme, izražajni listovi i živi naglasci.'
	},
	rustic: {
		deTitle: 'Rustikal',
		hrTitle: 'Rustikalni',
		deDescription: 'Verwittertes Holz, Naturstein, ländischer Charme.',
		hrDescription: 'Istrošeno drvo, prirodni kamen, seoski šarm.'
	},
	coastal: {
		deTitle: 'Küstenstil',
		hrTitle: 'Obalni',
		deDescription: 'Strand-/Ozean-Stimmung: Treibholz, Dünen, Ziergräser, sanfte Blau- und Weißtöne.',
		hrDescription: 'Ugođaj plaže/mora: naplavljeno drvo, dine, ukrasne trave, nježne plave i bijele nijanse.'
	},
	scandinavian: {
		deTitle: 'Skandinavisch',
		hrTitle: 'Skandinavski',
		deDescription: 'Helles Holz, Hygge, schlichte Formen und natürliches Licht.',
		hrDescription: 'Svijetlo drvo, hygge, jednostavne forme i prirodno svjetlo.'
	},
	traditional: {
		deTitle: 'Traditionell',
		hrTitle: 'Tradicionalni',
		deDescription: 'Klassische Rabatten, Hecken, Rosen und formelle Akzente.',
		hrDescription: 'Klasične gredice, živice, ruže i formalni detalji.'
	}
};

const COMPONENT_GROUP_LABELS: Record<string, { de: string; hr: string }> = {
	'water-recreation': { de: 'Wasser & Erholung', hr: 'Voda i rekreacija' },
	planting: { de: 'Pflanzen & Anbau', hr: 'Biljke i uzgoj' },
	'structures-surfaces': { de: 'Strukturen & Oberflächen', hr: 'Strukture i površine' },
	'cooking-entertaining': { de: 'Kochen & Unterhaltung', hr: 'Kuhanje i druženje' },
	'living-play': { de: 'Wohnen & Spiel', hr: 'Boravak i igra' },
	practical: { de: 'Praktisch & Stil', hr: 'Praktično i stil' }
};

const COMPONENT_LABELS: Record<string, { de: string; hr: string }> = {
	pool: { de: 'Pool / Schwimmbad', hr: 'Bazen / Swimming Pool' },
	'water-feature': { de: 'Wasserelement (Brunnen, Teich, Bach)', hr: 'Vodeni element (fontana, jezerce, potok)' },
	'hot-tub': { de: 'Whirlpool / Jacuzzi', hr: 'Hidromasažna kada / Jacuzzi' },
	'vegetable-garden': { de: 'Gemüsegarten / Hochbeete', hr: 'Povrtnjak / Povišene gredice' },
	'flower-beds': { de: 'Blumenbeete & Rabatten', hr: 'Cvjetne gredice i rubovi' },
	greenhouse: { de: 'Gewächshaus / Frühbeet', hr: 'Staklenik / Hladni okvir' },
	shed: { de: 'Gartenhaus / Stauraum', hr: 'Vrtna kućica / Spremište' },
	pergola: { de: 'Pergola / Pavillon', hr: 'Pergola / Sjenica' },
	deck: { de: 'Deck / Terrasse', hr: 'Deck / Terasa' },
	pathway: { de: 'Weg / Gehweg', hr: 'Staza / Pješačka staza' },
	driveway: { de: 'Einfahrt / Parkbereich', hr: 'Prilaz / Parkirni prostor' },
	'retaining-wall': { de: 'Stützmauer', hr: 'Potporni zid' },
	bbq: { de: 'BBQ / Grillbereich', hr: 'BBQ / Zona roštilja' },
	'outdoor-kitchen': { de: 'Außenküche', hr: 'Vanjska kuhinja' },
	'fire-pit': { de: 'Feuerstelle / Kamin', hr: 'Vatrište / Kamin' },
	'pizza-oven': { de: 'Pizzaofen', hr: 'Peć za pizzu' },
	lounge: { de: 'Lounge / Sitzbereich', hr: 'Lounge / Prostor za sjedenje' },
	playground: { de: 'Spielbereich', hr: 'Igralište / Zona za igru' },
	lighting: { de: 'Außenbeleuchtung', hr: 'Vanjska rasvjeta' },
	fence: { de: 'Zaun / Sichtschutzzaun', hr: 'Ograda / Ograda za privatnost' },
	compost: { de: 'Kompostbereich', hr: 'Kompost zona' }
};

// Component groups for organized selection (order = display order)
const COMPONENT_GROUPS = [
	{ id: 'structures-surfaces', label: 'Structures & surfaces', icon: '🏛️' },
	{ id: 'planting', label: 'Plants & growing', icon: '🌱' },
	{ id: 'living-play', label: 'Living & play', icon: '🪑' },
	{ id: 'practical', label: 'Practical & style', icon: '💡' },
	{ id: 'water-recreation', label: 'Water & recreation', icon: '💧' },
	{ id: 'cooking-entertaining', label: 'Cooking & entertainment', icon: '🍳' }
];

// Available garden components by group (can be extended later with images)
const GARDEN_COMPONENTS: Array<{ id: string; label: string; icon: string; group: string }> = [
	// Structures & surfaces
	{ id: 'deck', label: 'Deck / Patio (Terrace)', icon: '🪵', group: 'structures-surfaces' },
	{ id: 'driveway', label: 'Driveway / Parking Area', icon: '🚗', group: 'structures-surfaces' },
	{ id: 'pathway', label: 'Path / Walkway', icon: '🛤️', group: 'structures-surfaces' },
	{ id: 'retaining-wall', label: 'Retaining Wall', icon: '🧱', group: 'structures-surfaces' },
	{ id: 'pergola', label: 'Pergola / Pavilion', icon: '🏛️', group: 'structures-surfaces' },
	{ id: 'shed', label: 'Garden Shed / Storage', icon: '🧺', group: 'structures-surfaces' },
	// Plants & growing
	{ id: 'flower-beds', label: 'Flower Beds & Borders', icon: '🌸', group: 'planting' },
	{ id: 'vegetable-garden', label: 'Vegetable Garden / Raised Beds', icon: '🥕', group: 'planting' },
	{ id: 'greenhouse', label: 'Greenhouse / Cold Frame', icon: '🪴', group: 'planting' },
	// Living & play
	{ id: 'lounge', label: 'Lounge / Seating Area', icon: '🪑', group: 'living-play' },
	{ id: 'playground', label: 'Play Area', icon: '🎪', group: 'living-play' },
	// Practical & style
	{ id: 'lighting', label: 'Outdoor Lighting', icon: '💡', group: 'practical' },
	{ id: 'fence', label: 'Fence / Privacy Fence', icon: '🏡', group: 'practical' },
	{ id: 'compost', label: 'Compost Area', icon: '♻️', group: 'practical' },
	// Water & recreation
	{ id: 'hot-tub', label: 'Hot Tub / Jacuzzi', icon: '🛁', group: 'water-recreation' },
	{ id: 'water-feature', label: 'Water Feature (Fountain, Pond, Stream)', icon: '💧', group: 'water-recreation' },
	{ id: 'pool', label: 'Pool / Swimming Pool', icon: '🏊', group: 'water-recreation' },
	// Cooking & entertainment
	{ id: 'bbq', label: 'BBQ / Grill Area', icon: '🍔', group: 'cooking-entertaining' },
	{ id: 'fire-pit', label: 'Fire Pit / Fireplace', icon: '🔥', group: 'cooking-entertaining' },
	{ id: 'pizza-oven', label: 'Pizza Oven', icon: '🍕', group: 'cooking-entertaining' },
	{ id: 'outdoor-kitchen', label: 'Outdoor Kitchen', icon: '🍽️', group: 'cooking-entertaining' }
];

const MAX_AI_FOLLOW_UP_QUESTIONS = 6;
const COMPONENT_KEYWORD_ALIASES: Record<string, string[]> = {
	pool: ['pool', 'swimming pool'],
	'water-feature': ['water feature', 'fountain', 'pond', 'stream', 'waterfall'],
	'hot-tub': ['hot tub', 'jacuzzi', 'spa'],
	'vegetable-garden': ['vegetable garden', 'veg garden', 'raised bed', 'raised beds', 'herb garden'],
	'flower-beds': ['flower bed', 'flower beds', 'borders', 'planting bed'],
	greenhouse: ['greenhouse', 'cold frame'],
	shed: ['shed', 'storage'],
	pergola: ['pergola', 'gazebo', 'pavilion'],
	deck: ['deck', 'patio', 'terrace'],
	pathway: ['pathway', 'walkway', 'path', 'paths'],
	driveway: ['driveway', 'parking area', 'parking space'],
	'retaining-wall': ['retaining wall'],
	bbq: ['bbq', 'grill', 'grill area'],
	'outdoor-kitchen': ['outdoor kitchen'],
	'fire-pit': ['fire pit', 'fireplace'],
	'pizza-oven': ['pizza oven'],
	lounge: ['lounge', 'seating area', 'sitting area', 'seating'],
	playground: ['playground', 'play area'],
	lighting: ['lighting', 'outdoor lighting', 'lights'],
	fence: ['fence', 'privacy fence', 'privacy screen'],
	compost: ['compost', 'compost area']
};

// Options for "How you'll use the garden" (stored as garden_uses in backend)
const GARDEN_USE_OPTIONS: Array<{ id: GardenUseKey; label: string; icon: string; shortLabel: string }> = [
	{ id: 'relaxing', label: 'Relaxing / quiet time', icon: 'bi bi-sun', shortLabel: 'Relaxing' },
	{ id: 'growing_food', label: 'Growing food (vegetables, herbs)', icon: 'bi bi-flower1', shortLabel: 'Growing food' },
	{ id: 'kids_pets', label: 'Kids / pets', icon: 'bi bi-heart', shortLabel: 'Kids & pets' },
	{ id: 'hosting', label: 'Hosting / entertaining (dinners, parties, friends)', icon: 'bi bi-people', shortLabel: 'Hosting' },
	{ id: 'dining_outdoors', label: 'Dining outdoors', icon: 'bi bi-cup-straw', shortLabel: 'Dining outdoors' }
];

// Style-based component recommendations (visual style / aesthetic)
// Keys match stylePresets "key" values
const STYLE_COMPONENT_RECOMMENDATIONS: Record<string, string[]> = {
	modern: ['deck', 'lounge', 'fire-pit', 'pathway', 'lighting'],
	mediterranean: ['deck', 'pergola', 'outdoor-kitchen', 'bbq', 'pizza-oven', 'flower-beds'],
	minimalist: ['deck', 'lounge', 'pathway', 'water-feature', 'lighting'],
	cottage: ['flower-beds', 'pathway', 'vegetable-garden', 'greenhouse', 'shed', 'lounge'],
	japanese: ['water-feature', 'pathway', 'lounge', 'flower-beds', 'lighting'],
	tropical: ['deck', 'lounge', 'pool', 'hot-tub', 'flower-beds'],
	rustic: ['fire-pit', 'pathway', 'vegetable-garden', 'shed', 'lounge', 'compost'],
	coastal: ['deck', 'lounge', 'fire-pit', 'pathway', 'water-feature'],
	scandinavian: ['deck', 'lounge', 'fire-pit', 'hot-tub', 'lighting'],
	traditional: ['flower-beds', 'pathway', 'shed', 'lounge', 'fence']
};

// Use-based component recommendations (how the garden will be used)
const GARDEN_USE_COMPONENT_RECOMMENDATIONS: Record<GardenUseKey, string[]> = {
	relaxing: ['lounge', 'deck', 'water-feature', 'fire-pit', 'lighting'],
	growing_food: ['vegetable-garden', 'greenhouse', 'compost', 'shed'],
	kids_pets: ['playground', 'lounge', 'fence', 'pathway'],
	hosting: ['deck', 'lounge', 'outdoor-kitchen', 'bbq', 'fire-pit', 'pizza-oven'],
	dining_outdoors: ['deck', 'outdoor-kitchen', 'bbq', 'pergola', 'lounge']
};

/** Emoji for Step 3 question group cards (matches GARDEN_COMPONENTS where possible). */
const STEP3_GROUP_EMOJI: Record<string, string> = {
	ai: '✨',
	pool: '🏊',
	'water-feature': '💧',
	'flower-beds': '🌸',
	'vegetable-garden': '🥕',
	playground: '🛝',
	lounge: '🛋️',
	'outdoor-kitchen': '🍽️',
	'pizza-oven': '🍕',
	'fire-pit': '🔥',
	pergola: '⛱️',
	deck: '🪵',
	lighting: '💡',
	'retaining-wall': '🧱',
	driveway: '🚗',
	shed: '🏚️',
	greenhouse: '🏡',
	compost: '♻️',
	pathway: '🚶',
	bbq: '🍔',
	fence: '🏡',
	'hot-tub': '🛁'
};

const MAJOR_COMPONENT_IDS = new Set([
	'pool',
	'outdoor-kitchen',
	'pergola',
	'greenhouse',
	'playground',
	'deck',
	'driveway',
	'hot-tub'
]);

function getFallbackMaxMajorComponents(gardenSize?: number, spaceType?: string): number | null {
	const size = typeof gardenSize === 'number' ? gardenSize : 0;
	const st = (spaceType || '').toLowerCase();
	if (st === 'balcony') return 1;
	if (st === 'terrace') {
		if (size > 0 && size <= 20) return 1;
		if (size > 20 && size <= 40) return 2;
		if (size > 40 && size <= 70) return 3;
		return 4;
	}
	if (size <= 0) return null;
	if (size <= 20) return 1;
	if (size <= 35) return 2;
	if (size <= 55) return 3;
	if (size <= 80) return 4;
	return 5;
}

const SEEKER_SAVED_LOCATION_KEY = 'seekerPreferredLocation';
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
const PHOTO_HARD_MAX_BYTES = 40 * 1024 * 1024;
const SUPPORTED_INPUT_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const HEIC_MIME_TYPES = ['image/heic', 'image/heif'];
const PHOTO_UPLOAD_MAX_DIMENSION = 2560;

function inferPhotoMime(file: File): string {
	const mime = (file.type || '').toLowerCase();
	if (mime) return mime;
	const name = (file.name || '').toLowerCase();
	if (name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'image/jpeg';
	if (name.endsWith('.png')) return 'image/png';
	if (name.endsWith('.webp')) return 'image/webp';
	if (name.endsWith('.heic')) return 'image/heic';
	if (name.endsWith('.heif')) return 'image/heif';
	return '';
}

function getPhotoValidationError(file: File | null): string | null {
	if (!file) return null;
	if (file.size > PHOTO_HARD_MAX_BYTES) {
		return 'Photo is too large to process on this device. Please choose one below 40 MB.';
	}
	const mime = inferPhotoMime(file);
	if (!SUPPORTED_INPUT_MIME_TYPES.includes(mime)) {
		return 'Unsupported format. Please upload JPG, PNG, WebP, HEIC, or HEIF.';
	}
	return null;
}

function estimateDataUrlBytes(dataUrl: string): number {
	const commaIndex = dataUrl.indexOf(',');
	if (commaIndex === -1) return 0;
	const base64 = dataUrl.slice(commaIndex + 1);
	if (!base64) return 0;
	const paddingMatch = base64.match(/=+$/);
	const padding = paddingMatch ? paddingMatch[0].length : 0;
	return Math.max(0, Math.floor((base64.length * 3) / 4) - padding);
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => {
				if (!blob) {
					reject(new Error('Canvas export failed'));
					return;
				}
				resolve(blob);
			},
			type,
			quality
		);
	});
}

async function loadImageFromFile(file: File): Promise<HTMLImageElement> {
	const objectUrl = URL.createObjectURL(file);
	try {
		return await new Promise<HTMLImageElement>((resolve, reject) => {
			const img = new Image();
			img.onload = () => resolve(img);
			img.onerror = () => reject(new Error('Photo processing failed: unable to decode image'));
			img.src = objectUrl;
		});
	} finally {
		URL.revokeObjectURL(objectUrl);
	}
}

async function optimizePhotoFileForUpload(file: File): Promise<File> {
	if (file.size <= PHOTO_MAX_BYTES) return file;
	const mime = inferPhotoMime(file);
	if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(mime)) return file;

	const image = await loadImageFromFile(file);
	let width = image.naturalWidth || image.width;
	let height = image.naturalHeight || image.height;
	if (!width || !height) {
		throw new Error('Photo processing failed: image dimensions missing');
	}

	const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');
	if (!ctx) {
		throw new Error('Photo processing failed: canvas unavailable');
	}

	const largestSide = Math.max(width, height);
	if (largestSide > PHOTO_UPLOAD_MAX_DIMENSION) {
		const scale = PHOTO_UPLOAD_MAX_DIMENSION / largestSide;
		width = Math.max(1, Math.round(width * scale));
		height = Math.max(1, Math.round(height * scale));
	}

	let currentWidth = width;
	let currentHeight = height;
	let quality = 0.9;
	for (let attempt = 0; attempt < 6; attempt += 1) {
		canvas.width = currentWidth;
		canvas.height = currentHeight;
		ctx.clearRect(0, 0, currentWidth, currentHeight);
		ctx.drawImage(image, 0, 0, currentWidth, currentHeight);

		const blob = await canvasToBlob(canvas, 'image/jpeg', quality);
		if (blob.size <= PHOTO_MAX_BYTES) {
			return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
		}

		quality = Math.max(0.55, quality - 0.1);
		currentWidth = Math.max(1, Math.round(currentWidth * 0.85));
		currentHeight = Math.max(1, Math.round(currentHeight * 0.85));
	}

	throw new Error('Photo is too large');
}

function getPhotoProcessingErrorMessage(error: unknown): string {
	const message = error instanceof Error ? error.message : String(error || '');
	if (/HEIC|HEIF/i.test(message)) {
		return 'We could not convert this HEIC/HEIF photo. Please retake it as "Most Compatible" (JPG) or choose another photo.';
	}
	if (/too large|size/i.test(message)) {
		return 'This photo is still too large after optimization. Please crop it a bit or retake at a lower resolution.';
	}
	if (/decode|read|FileReader|processing/i.test(message)) {
		return 'Could not process this photo on your device. Try another photo or retake it with lower resolution.';
	}
	return 'Failed to process photo. Please try another image.';
}

/** Serializable snapshot for “unsaved questionnaire” leave guard (steps 2–3). */
function buildQuestionnaireLeaveSnapshot(input: {
	form: QuestionnaireData;
	contactEmail: string;
	aiAnswers: Record<string, unknown>;
	aiQuestionComments: Record<string, string>;
	hasLocalPhotoFile: boolean;
	photoPreviewUrl: string | null;
}): string {
	const photoPreviewIsBlob = Boolean(input.photoPreviewUrl?.startsWith('blob:'));
	return JSON.stringify({
		form: input.form,
		contactEmail: input.contactEmail,
		aiAnswers: input.aiAnswers,
		aiQuestionComments: input.aiQuestionComments,
		hasLocalPhotoFile: input.hasLocalPhotoFile,
		photoPreviewIsBlob
	});
}

async function uploadPlanBeforeImageFromDataUrl(dataUrl: string): Promise<string | undefined> {
	try {
		const response = await fetch(dataUrl);
		const blob = await response.blob();
		const file = new File([blob], 'photo.jpg', { type: blob.type });
		const formData = new FormData();
		formData.append('image', file);
		formData.append('type', 'plan');
		formData.append('subtype', 'before');
		const uploadResponse = await fetch(buildApiUrl('/upload'), {
			method: 'POST',
			body: formData
		});
		if (uploadResponse.ok) {
			const uploadData = await uploadResponse.json();
			if (uploadData.success && uploadData.data?.url) {
				const relativeUrl = uploadData.data.url;
				return relativeUrl.startsWith('http') ? relativeUrl : (getImageUrl(relativeUrl) ?? relativeUrl);
			}
		}
	} catch (e) {
		console.error('Failed to upload before image:', e);
	}
	return undefined;
}

export default function GardenQuestionnaire({ guestMode = false }: { guestMode?: boolean }) {
	usePageActivityTracker('garden_questionnaire');
	const navigate = useNavigate();
	const location = useLocation();
	const { showAlert } = useAlertDialog();
	const { user } = useAuth();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const localizeStyleTitle = useCallback((styleKey?: string, fallbackTitle?: string) => {
		const key = String(styleKey || '');
		const localized = STYLE_TEXT_LOCALIZATION[key];
		if (!localized) return fallbackTitle || key;
		if (language === 'de') return localized.deTitle;
		if (language === 'hr') return localized.hrTitle;
		return fallbackTitle || key;
	}, [language]);
	const localizeStyleDescription = useCallback((styleKey?: string, fallbackDescription?: string) => {
		const key = String(styleKey || '');
		const localized = STYLE_TEXT_LOCALIZATION[key];
		if (!localized) return fallbackDescription || '';
		if (language === 'de') return localized.deDescription;
		if (language === 'hr') return localized.hrDescription;
		return fallbackDescription || '';
	}, [language]);
	const localizeComponentLabel = useCallback((componentId?: string, fallbackLabel?: string) => {
		const key = String(componentId || '');
		const localized = COMPONENT_LABELS[key];
		if (!localized) return fallbackLabel || key;
		if (language === 'de') return localized.de;
		if (language === 'hr') return localized.hr;
		return fallbackLabel || key;
	}, [language]);
	const localizeComponentGroupLabel = useCallback((groupId?: string, fallbackLabel?: string) => {
		const key = String(groupId || '');
		const localized = COMPONENT_GROUP_LABELS[key];
		if (!localized) return fallbackLabel || key;
		if (language === 'de') return localized.de;
		if (language === 'hr') return localized.hr;
		return fallbackLabel || key;
	}, [language]);
	const localizeGardenUseShortLabel = useCallback((useId: GardenUseKey, fallback: string) => {
		switch (useId) {
			case 'relaxing':
				return copy('Relaxing', 'Entspannen', 'Opuštanje');
			case 'growing_food':
				return copy('Growing food', 'Lebensmittel anbauen', 'Uzgoj hrane');
			case 'kids_pets':
				return copy('Kids & pets', 'Kinder & Haustiere', 'Djeca i ljubimci');
			case 'hosting':
				return copy('Hosting', 'Gastgeben', 'Druženje');
			case 'dining_outdoors':
				return copy('Dining outdoors', 'Essen im Freien', 'Objedovanje na otvorenom');
			default:
				return fallback;
		}
	}, [copy]);
	const localizeProjectFocusLabel = useCallback((focus: ProjectFocusKey) => {
		switch (focus) {
			case 'planting_only':
				return copy('Planting only', 'Nur Bepflanzung', 'Samo sadnja');
			case 'construction_hardscape_only':
				return copy('Construction / hardscape only', 'Nur Bau / Hardscape', 'Samo gradnja / hardscape');
			case 'mixed_planting_construction':
				return copy('Mixed (planting + construction)', 'Gemischt (Bepflanzung + Bau)', 'Mješovito (sadnja + gradnja)');
			default:
				return focus;
		}
	}, [copy]);
	const localizeStyleRecommendationText = useCallback((text?: string) => {
		const raw = String(text || '');
		if (!raw) return '';
		if (language === 'en') return raw;
		return raw
			.replace(/Recommended shapes:/gi, copy('Recommended shapes:', 'Empfohlene Formen:', 'Preporučeni oblici:'))
			.replace(/Recommended colors:/gi, copy('Recommended colors:', 'Empfohlene Farben:', 'Preporučene boje:'))
			.replace(/Recommended materials:/gi, copy('Recommended materials:', 'Empfohlene Materialien:', 'Preporučeni materijali:'))
			.replace(/style emphasizes/gi, copy('style emphasizes', 'Stil betont', 'stil naglašava'));
	}, [language, copy]);
	const formatAnalysisValue = useCallback((value?: string) => {
		const normalized = String(value || '').toLowerCase();
		const labels: Record<string, string> = {
			garden: copy('Garden', 'Garten', 'Vrt'),
			terrace: copy('Terrace', 'Terrasse', 'Terasa'),
			balcony: copy('Balcony', 'Balkon', 'Balkon'),
			unknown: copy('Unknown', 'Unbekannt', 'Nepoznato'),
			none: copy('None', 'Kein', 'Nema'),
			slight: copy('Slight', 'Leicht', 'Blag'),
			moderate: copy('Moderate', 'Mittel', 'Umjeren'),
			steep: copy('Steep', 'Stark', 'Strm'),
			full_sun: copy('Full sun', 'Volle Sonne', 'Puno sunca'),
			partial_sun: copy('Partial sun', 'Halbschatten', 'Djelomično sunce'),
			shade: copy('Shade', 'Schatten', 'Sjena'),
			good: copy('Good', 'Gut', 'Dobro'),
			poor: copy('Poor', 'Schlecht', 'Loše'),
		};
		return labels[normalized] || String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
	}, [language]);
	const formatAnalysisText = useCallback((value?: string) => {
		const raw = String(value || '').trim();
		if (!raw) return '';

		const replacements: Array<[RegExp, string]> = [
			[/\bfire[\s-]?pit\b/gi, copy('fire pit', 'Feuerstelle', 'vatrište')],
			[/\bfence\b/gi, copy('fence', 'Zaun', 'ograda')],
			[/\bpool\b/gi, copy('pool', 'Pool', 'bazen')],
			[/\bpergola\b/gi, copy('pergola', 'Pergola', 'pergola')],
			[/\bdeck\b/gi, copy('deck', 'Terrasse', 'deck')],
			[/\bpathway\b/gi, copy('pathway', 'Weg', 'staza')],
			[/\blighting\b/gi, copy('lighting', 'Beleuchtung', 'rasvjeta')],
			[/\bshed\b/gi, copy('shed', 'Gartenhaus', 'vrtna kućica')],
			[/\bbbq\b/gi, copy('BBQ', 'Grill', 'roštilj')],
			[/\blounge\b/gi, copy('lounge area', 'Lounge-Bereich', 'prostor za odmor')],
			[/\bretaining[\s-]?wall\b/gi, copy('retaining wall', 'Stützmauer', 'potporni zid')],
			[/\bwater[\s-]?feature\b/gi, copy('water feature', 'Wasserelement', 'vodeni element')],
			[/\bvegetable[\s-]?garden\b/gi, copy('vegetable garden', 'Gemüsegarten', 'povrtnjak')],
			[/\bflower[\s-]?beds?\b/gi, copy('flower beds', 'Blumenbeete', 'cvjetne gredice')],
			[/\bplayground\b/gi, copy('playground', 'Spielbereich', 'dječje igralište')],
			[/\bhot[\s-]?tub\b/gi, copy('hot tub', 'Whirlpool', 'hidromasažna kada')],
			[/\bpizza[\s-]?oven\b/gi, copy('pizza oven', 'Pizzaofen', 'peć za pizzu')],
			[/\bcompost\b/gi, copy('compost area', 'Kompostbereich', 'kompostište')],
			[/\blawn\b/gi, copy('lawn', 'Rasen', 'travnjak')],
			[/\bhouse\b/gi, copy('house', 'Haus', 'kuća')],
			[/\bpatio\b/gi, copy('patio', 'Terrasse', 'terasa')],
			[/\bdriveway\b/gi, copy('driveway', 'Einfahrt', 'prilaz')],
			[/\btree(s)?\b/gi, copy('trees', 'Bäume', 'drveća')],
			[/\bshrub(s)?\b/gi, copy('shrubs', 'Sträucher', 'grmlje')],
			[/\battached to the house\b/gi, copy('attached to the house', 'am Haus angeschlossen', 'pričvršćeno uz kuću')],
			[/\bfront garden area\b/gi, copy('front garden area', 'Vorgartenbereich', 'prednji dio vrta')],
			[/\bback garden\b/gi, copy('back garden', 'Hintergarten', 'stražnji vrt')],
			[/\bside yard\b/gi, copy('side yard', 'Seitenbereich', 'bočni dio dvorišta')],
			[/\bin the background\b/gi, copy('in the background', 'im Hintergrund', 'u pozadini')],
			[/\bnear the house\b/gi, copy('near the house', 'nahe am Haus', 'blizu kuće')]
		];

		let next = raw;
		for (const [pattern, replacement] of replacements) {
			next = next.replace(pattern, replacement);
		}

		return next;
	}, [language]);
	const steps = useMemo(
		() => [
			{
				id: 1,
				title: copy('Upload Photo', 'Foto hochladen', 'Učitaj fotografiju'),
				shortTitle: copy('Photo', 'Foto', 'Foto'),
				description: copy('Upload garden photo', 'Gartenfoto hochladen', 'Učitaj fotografiju vrta'),
				icon: 'bi-image'
			},
			{
				id: 2,
				title: copy('Preferred Style', 'Bevorzugter Stil', 'Preferirani stil'),
				shortTitle: copy('Style', 'Stil', 'Stil'),
				description: copy('Choose your garden aesthetic', 'Wählen Sie Ihre Gartenästhetik', 'Odaberite estetiku vrta'),
				icon: 'bi-palette'
			},
			{
				id: 3,
				title: copy('Garden basics', 'Garten-Grundlagen', 'Osnove vrta'),
				shortTitle: copy('Basics', 'Grundlagen', 'Osnove'),
				description: copy('Size, location, and how you will use the space', 'Größe, Standort und Nutzung', 'Veličina, lokacija i način korištenja'),
				icon: 'bi-journal-text'
			},
			{
				id: 4,
				title: copy('Garden components', 'Gartenkomponenten', 'Komponente vrta'),
				shortTitle: copy('Features', 'Elemente', 'Značajke'),
				description: copy('Pick the features you want', 'Wählen Sie gewünschte Elemente', 'Odaberite željene značajke'),
				icon: 'bi-grid-3x3-gap'
			},
			{
				id: 5,
				title: copy('Follow-up questions', 'Zusatzfragen', 'Dodatna pitanja'),
				shortTitle: copy('Details', 'Details', 'Detalji'),
				description: copy('Answer questions shaped for your space', 'Beantworten Sie Fragen, die zu Ihrem Raum passen', 'Odgovorite na pitanja prilagođena vašem prostoru'),
				icon: 'bi-stars'
			}
		],
		[language]
	);

	useEffect(() => {
		if (guestMode && user) {
			navigate('/app/seeker/create-plan', { replace: true });
		}
	}, [guestMode, user, navigate]);

	const getSavedPreferredLocation = () => {
		const savedLocation = localStorage.getItem(SEEKER_SAVED_LOCATION_KEY);
		return savedLocation?.trim() || '';
	};
	const [inviteInfo, setInviteInfo] = useState<{ code: string; companyName: string } | null>(null);
	const [manualInviteCode, setManualInviteCode] = useState('');
	const [isCheckingInvite, setIsCheckingInvite] = useState(false);
	const [inviteInputError, setInviteInputError] = useState<string | null>(null);
	const [showInviteCodeInput, setShowInviteCodeInput] = useState(false);
	const [currentStep, setCurrentStep] = useState(1);
	const stepEnteredAtRef = useRef<number>(Date.now());
	useEffect(() => {
		// Step 1 is covered by garden_questionnaire_view from usePageActivityTracker.
		if (currentStep > 1) {
			void trackActivityEvent({
				event_name: 'questionnaire_step_view',
				properties: { step: currentStep, guest_mode: guestMode }
			});
		}
		stepEnteredAtRef.current = Date.now();
	}, [currentStep, guestMode]);
	useEffect(() => {
		// Ensure each new step starts at absolute top.
		scrollQuestionnaireToTop();
	}, [currentStep]);

	useEffect(() => {
		return () => {
			const elapsedSeconds = Math.max(0, Math.round((Date.now() - stepEnteredAtRef.current) / 1000));
			void trackActivityEvent({
				event_name: 'questionnaire_step_exit',
				duration_seconds: elapsedSeconds,
				properties: { step: currentStep, guest_mode: guestMode }
			});
		};
	}, [currentStep, guestMode]);

	const [isSaving, setIsSaving] = useState(false);
	
	// Initialize form state - check if we're editing first
	const getInitialFormState = (): QuestionnaireData => {
		const editingId = guestMode ? null : localStorage.getItem('editingPlanId');
		if (editingId) {
			// Try to get plan data synchronously for initial state
			const plan = getPublicPlanById(editingId, { includeHidden: true });
			if (plan?.questionnaire) {
			return {
				gardenSize: plan.questionnaire.gardenSize ?? 0,
				gardenShape: plan.questionnaire.gardenShape ?? undefined,
				selectedComponents: plan.questionnaire.selectedComponents ?? [],
				photoBeforeUrl: plan.questionnaire.photoBeforeUrl ?? undefined,
				styleKey: plan.questionnaire.styleKey || (stylePresets[0]?.key ?? 'modern'),
				needsWants: plan.questionnaire.needsWants || '',
				maintenanceLevel: plan.questionnaire.maintenanceLevel ?? undefined,
				childrenPets: plan.questionnaire.childrenPets ?? undefined,
				budgetRange: plan.questionnaire.budgetRange ?? undefined,
				budgetPreference: plan.questionnaire.budgetPreference ?? undefined,
				timeline: plan.questionnaire.timeline || '',
				projectStartDate: plan.questionnaire.projectStartDate ?? undefined,
				location: plan.questionnaire.location || '',
				householdSize: plan.questionnaire.gardenUsers ?? plan.questionnaire.householdSize ?? undefined,
				gardenUsers: plan.questionnaire.gardenUsers ?? plan.questionnaire.householdSize ?? undefined,
				plantDensityPreference: plan.questionnaire.plantDensityPreference ?? undefined,
				projectFocus: plan.questionnaire.projectFocus ?? undefined,
				gardenUses: plan.questionnaire.gardenUses ?? [],
				hostingMaxPeople: plan.questionnaire.hostingMaxPeople ?? undefined,
				kidsCount: plan.questionnaire.kidsCount ?? undefined,
				kidsAges: plan.questionnaire.kidsAges ?? undefined,
				petsDescription: plan.questionnaire.petsDescription ?? undefined,
				aiAnalysis: plan.questionnaire.aiAnalysis ?? undefined,
				aiAnalysisComments: plan.questionnaire.aiAnalysisComments ?? undefined,
				aiAnswers: plan.questionnaire.aiAnswers ?? undefined,
				aiQuestionComments: plan.questionnaire.aiQuestionComments ?? undefined,
				userCorrections: plan.questionnaire.userCorrections ?? undefined
			};
			}
		}
		// Default empty form
		return {
			gardenSize: 0,
			gardenShape: undefined,
			selectedComponents: [],
			photoBeforeUrl: undefined,
			styleKey: stylePresets[0]?.key ?? 'modern',
			needsWants: '',
			maintenanceLevel: undefined,
			childrenPets: undefined,
			budgetRange: undefined,
			timeline: '',
			projectStartDate: undefined,
			location: getSavedPreferredLocation(),
			householdSize: undefined,
			gardenUsers: undefined,
			plantDensityPreference: undefined,
			projectFocus: undefined,
			gardenUses: [],
			hostingMaxPeople: undefined,
			kidsCount: undefined,
			kidsAges: undefined,
			petsDescription: undefined
		};
	};
	
	const [form, setForm] = useState<QuestionnaireData>(getInitialFormState());
	
	const [contactEmail, setContactEmail] = useState(() => {
		// Initialize with logged-in user's email if available
		return user?.email || '';
	});
	const [errors, setErrors] = useState<Record<string, string>>({});
	// Report "garden not detected" modal
	const [showReportModal, setShowReportModal] = useState(false);
	const [reportMessage, setReportMessage] = useState('');
	const [isSubmittingReport, setIsSubmittingReport] = useState(false);
	
	// Initialize photo preview and editingPlanId from plan if editing
	const getInitialPhotoPreview = (): string | null => {
		if (guestMode) return null;
		const editingId = localStorage.getItem('editingPlanId');
		if (editingId) {
			const plan = getPublicPlanById(editingId, { includeHidden: true });
			if (plan) {
				return plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl || null;
			}
		}
		return null;
	};
	
	const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(getInitialPhotoPreview());
	const [photoFile, setPhotoFile] = useState<File | null>(null);
	const resolvedPhotoPreviewUrl = normalizeStoredPhotoUrl(photoPreviewUrl);
	const [editingPlanId, setEditingPlanId] = useState<string | null>(() =>
		guestMode ? null : localStorage.getItem('editingPlanId')
	);
	const resolvedCurrentAfterPreviewUrl = useMemo(() => {
		if (!editingPlanId) return null;
		const plan = getPublicPlanById(editingPlanId, { includeHidden: true });
		return normalizeStoredPhotoUrl(plan?.aiGenerated?.photoAfterUrl || null);
	}, [editingPlanId]);
	const generationAnimationBackgroundUrl = editingPlanId
		? (resolvedCurrentAfterPreviewUrl || resolvedPhotoPreviewUrl || undefined)
		: (resolvedPhotoPreviewUrl || undefined);
	const [isGettingLocation, setIsGettingLocation] = useState(false);
	const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied' | 'unknown'>('unknown');
	const firstInvalidRef = useRef<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null>(null);
	const isEditingRef = useRef<boolean>(false); // Ref to track editing state synchronously
	const photoInputRef = useRef<HTMLInputElement>(null);
	const cameraInputRef = useRef<HTMLInputElement>(null);
	const projectStartDateInputRef = useRef<HTMLInputElement | null>(null);
	const preferredStyleSectionRef = useRef<HTMLDivElement | null>(null);
	const questionnaireScrollAreaRef = useRef<HTMLDivElement | null>(null);
	const scrollQuestionnaireToTop = () => {
		questionnaireScrollAreaRef.current?.scrollTo({ top: 0, behavior: 'auto' });
		window.scrollTo({ top: 0, behavior: 'auto' });
	};
	const [uploadZoneDrag, setUploadZoneDrag] = useState(false);
	const [showPhotoExamplesModal, setShowPhotoExamplesModal] = useState(false);
	const [examplePhotoIndex, setExamplePhotoIndex] = useState(0);
	const [previewingComponentId, setPreviewingComponentId] = useState<string | null>(null);
	const [componentPreviewIndex, setComponentPreviewIndex] = useState(0);
	
	// AI Analysis state - initialize from form if available
	const [aiAnalysis, setAiAnalysis] = useState<AIAnalysis | null>(form.aiAnalysis || null);
	const [isAnalyzing, setIsAnalyzing] = useState(false);
	const [isPreparingPhoto, setIsPreparingPhoto] = useState(false);
	const [aiAnswers, setAiAnswers] = useState<Record<string, any>>(form.aiAnswers || {});
	const [aiQuestionComments, setAiQuestionComments] = useState<Record<string, string>>(form.aiQuestionComments || {});
	const [allQuestions, setAllQuestions] = useState<AIGeneratedQuestion[]>([]); // Combined AI + component questions
	const [translatedQuestions, setTranslatedQuestions] = useState<AIGeneratedQuestion[]>([]);
	const [translatedAnalysisText, setTranslatedAnalysisText] = useState<{
		gardenRecognitionNote?: string;
		layoutComplexityWarning?: string;
		detectedFeatures?: DetectedFeature[];
		recommendedComponents?: RecommendedComponent[];
		componentFeasibility?: ComponentFeasibility[];
	} | null>(null);
	const [isTranslatingQuestions, setIsTranslatingQuestions] = useState(false);
	const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
	const [isGeneratingPlan, setIsGeneratingPlan] = useState(false);
	const [isPreparingGeneration, setIsPreparingGeneration] = useState(false);
	const isQuestionnaireInteractionLocked = isPreparingPhoto || isPreparingGeneration;
	const [expandedQuestionGroups, setExpandedQuestionGroups] = useState<Record<string, boolean>>({});
	const [feasibilityModal, setFeasibilityModal] = useState<{
		componentId: string;
		componentLabel: string;
		reasoning: string;
		alternativeId?: string;
		alternativeLabel?: string;
	} | null>(null);
	const [showGenerateFitModal, setShowGenerateFitModal] = useState(false);
	const suppressLeaveGuardRef = useRef(false);
	const suppressQuestionnaireLeaveRef = useRef(false);
	const questionnaireLeaveBaselineRef = useRef<string | null>(null);
	const [questionnaireLeaveBaselineTick, setQuestionnaireLeaveBaselineTick] = useState(0);
	const [pendingSuccessRedirect, setPendingSuccessRedirect] = useState<string | null>(null);
	const lastQuestionBuildSignatureRef = useRef<string | null>(null);
	const analysisTranslationCacheRef = useRef<Record<string, {
		gardenRecognitionNote?: string;
		layoutComplexityWarning?: string;
		recommendedComponents?: RecommendedComponent[];
		componentFeasibility?: ComponentFeasibility[];
	}>>({});
	/** Logged-out try flow: teaser animation then auth CTA (no plan/photo AI until after sign-in). */
	const [guestAuthGate, setGuestAuthGate] = useState<'idle' | 'teaser' | 'modal'>('idle');
	const guestTeaserDoneRef = useRef(false);
	/** Full-screen overlays on this page: lower the progress chrome and pause "active" step styling (matches example-photos behavior). */
	const questionnaireFullscreenModalOpen =
		showPhotoExamplesModal ||
		Boolean(previewingComponentId) ||
		showReportModal ||
		Boolean(feasibilityModal) ||
		showGenerateFitModal;
	const hasCostEstimate = (form.selectedComponents?.length ?? 0) > 0 && (form.gardenSize ?? 0) > 0;
	const topBarCostEstimate = hasCostEstimate
		? calculateTotalCost(form.selectedComponents || [], form.gardenSize || 0)
		: null;
	const shouldShowExpandedInviteInput =
		showInviteCodeInput || Boolean(manualInviteCode.trim()) || Boolean(inviteInputError) || isCheckingInvite;

	function commitQuestionnaireLeaveBaseline(input: {
		form: QuestionnaireData;
		contactEmail: string;
		aiAnswers: Record<string, unknown>;
		aiQuestionComments: Record<string, string>;
		hasLocalPhotoFile: boolean;
		photoPreviewUrl: string | null;
	}) {
		questionnaireLeaveBaselineRef.current = buildQuestionnaireLeaveSnapshot(input);
		setQuestionnaireLeaveBaselineTick((t) => t + 1);
	}

	const questionnaireLeaveSnapshot = useMemo(
		() =>
			buildQuestionnaireLeaveSnapshot({
				form,
				contactEmail,
				aiAnswers,
				aiQuestionComments,
				hasLocalPhotoFile: !!photoFile,
				photoPreviewUrl
			}),
		[form, contactEmail, aiAnswers, aiQuestionComments, photoFile, photoPreviewUrl]
	);

	const questionnaireLeaveDirty = useMemo(() => {
		return (
			questionnaireLeaveBaselineRef.current !== null &&
			questionnaireLeaveSnapshot !== questionnaireLeaveBaselineRef.current
		);
	}, [questionnaireLeaveSnapshot, questionnaireLeaveBaselineTick]);

	const warnQuestionnaireLeave =
		currentStep >= 2 &&
		questionnaireLeaveDirty &&
		!suppressQuestionnaireLeaveRef.current;

	const aiWorkInProgress = (isAnalyzing || isGeneratingQuestions || isGeneratingPlan) && !suppressLeaveGuardRef.current;
	const shouldBlockQuestionnaireNavigation = aiWorkInProgress || warnQuestionnaireLeave;
	/** Allow forward navigation into plan generation / plan view without a false “leaving” prompt (data is carried in state or storage). */
	const questionnaireBlocker = useCallback(
		({ nextLocation }: { nextLocation: { pathname: string } }) => {
			if (!shouldBlockQuestionnaireNavigation) return false;
			const p = nextLocation.pathname;
			if (p === '/login' || p.endsWith('/login')) return false;
			if (p === '/app/seeker/generate-plan') return false;
			if (p.startsWith('/app/seeker/plan/')) {
				const rest = p.slice('/app/seeker/plan/'.length);
				if (rest.length > 0 && !rest.includes('/')) return false;
			}
			return true;
		},
		[shouldBlockQuestionnaireNavigation]
	);
	const navigationLeaveBlocker = useBlocker(questionnaireBlocker);

	async function handleSelectedPhoto(file: File | null) {
		const validationError = getPhotoValidationError(file);
		if (validationError) {
			setPhotoFile(null);
			setErrors(prev => ({ ...prev, photoFile: validationError }));
			showAlert(validationError, { title: 'Photo not supported' });
			return;
		}
		if (!file) {
			setPhotoFile(null);
			return;
		}
		setErrors(prev => {
			if (!prev.photoFile) return prev;
			const { photoFile: _ignore, ...rest } = prev;
			return rest;
		});
		try {
			setIsPreparingPhoto(true);
			// Pre-compress once at selection time to reduce upload and generation latency.
			const optimizedDataUrl = await fileToDataURL(file);
			const optimizedBlob = await fetch(optimizedDataUrl).then((res) => res.blob());
			const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
			const extension = optimizedBlob.type === 'image/png' ? 'png' : 'jpg';
			const optimizedFile = new File([optimizedBlob], `${baseName}.${extension}`, {
				type: optimizedBlob.type || 'image/jpeg'
			});
			setPhotoFile(optimizedFile);
		} catch (error) {
			setPhotoFile(null);
			const message = getPhotoProcessingErrorMessage(error);
			setErrors(prev => ({ ...prev, photoFile: message }));
			showAlert(message, { title: 'Photo not supported' });
		} finally {
			setIsPreparingPhoto(false);
		}
	}

	useEffect(() => {
		if (!shouldBlockQuestionnaireNavigation) return;
		const onBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', onBeforeUnload);
		return () => window.removeEventListener('beforeunload', onBeforeUnload);
	}, [shouldBlockQuestionnaireNavigation]);

	useEffect(() => {
		if (navigationLeaveBlocker.state !== 'blocked') return;
		const aiBlocking =
			(isAnalyzing || isGeneratingQuestions || isGeneratingPlan) && !suppressLeaveGuardRef.current;
		const message = aiBlocking
			? copy(
				'Are you sure you want to leave? Any photo review or plan generation in progress will be cancelled and you may lose progress.',
				'Sind Sie sicher, dass Sie die Seite verlassen möchten? Eine laufende Fotoanalyse oder Plangenerierung wird abgebrochen und Ihr Fortschritt kann verloren gehen.',
				'Jeste li sigurni da želite izaći? Svaka analiza fotografije ili generiranje plana u tijeku bit će prekinuti i možete izgubiti napredak.'
			)
			: guestMode
				? copy(
					'Are you sure you want to leave? Your answers are only kept on this device until you create an account and generate a plan.',
					'Sind Sie sicher, dass Sie die Seite verlassen möchten? Ihre Antworten werden nur auf diesem Gerät gespeichert, bis Sie ein Konto erstellen und einen Plan generieren.',
					'Jeste li sigurni da želite izaći? Vaši odgovori čuvaju se samo na ovom uređaju dok ne izradite račun i generirate plan.'
				)
				: copy(
					'Are you sure you want to leave? You have unsaved answers on this questionnaire. Save a draft first if you want to keep them.',
					'Sind Sie sicher, dass Sie die Seite verlassen möchten? Sie haben ungespeicherte Antworten in diesem Fragebogen. Speichern Sie zuerst einen Entwurf, wenn Sie sie behalten möchten.',
					'Jeste li sigurni da želite izaći? Imate nespremljene odgovore u ovom upitniku. Prvo spremite skicu ako ih želite zadržati.'
				);
		const shouldLeave = window.confirm(message);
		if (shouldLeave) {
			if (!aiBlocking) {
				suppressQuestionnaireLeaveRef.current = true;
			}
			navigationLeaveBlocker.proceed();
			queueMicrotask(() => {
				suppressQuestionnaireLeaveRef.current = false;
			});
		} else {
			navigationLeaveBlocker.reset();
		}
	}, [navigationLeaveBlocker.state, isAnalyzing, isGeneratingQuestions, isGeneratingPlan, guestMode]);

	useEffect(() => {
		if (!pendingSuccessRedirect) return;
		if (isGeneratingPlan || isAnalyzing || isGeneratingQuestions) return;
		const timer = window.setTimeout(() => {
			navigate(pendingSuccessRedirect);
			setPendingSuccessRedirect(null);
		}, 0);
		return () => window.clearTimeout(timer);
	}, [pendingSuccessRedirect, isGeneratingPlan, isAnalyzing, isGeneratingQuestions, navigate]);

	// Preload example questionnaire photos so the modal carousel is instant
	useEffect(() => {
		QUESTIONNAIRE_EXAMPLE_PHOTO_URLS.forEach((url) => {
			const img = new Image();
			img.src = url;
		});

		STYLE_PRESET_IMAGE_URLS.forEach((url) => {
			const img = new Image();
			img.src = url;
		});

		Object.values(COMPONENT_PREVIEW_URLS).flat().forEach((url) => {
			const img = new Image();
			img.src = url;
		});
	}, []);

	useEffect(() => {
		if (!showPhotoExamplesModal) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setShowPhotoExamplesModal(false);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [showPhotoExamplesModal]);

	useEffect(() => {
		if (!previewingComponentId) return;
		const previewCount = COMPONENT_PREVIEW_URLS[previewingComponentId]?.length ?? 0;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setPreviewingComponentId(null);
				return;
			}
			if (previewCount <= 1) return;
			if (e.key === 'ArrowLeft') {
				setComponentPreviewIndex((i) => (i - 1 + previewCount) % previewCount);
			}
			if (e.key === 'ArrowRight') {
				setComponentPreviewIndex((i) => (i + 1) % previewCount);
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, [previewingComponentId]);

	const examplePhotoCount = QUESTIONNAIRE_EXAMPLE_PHOTO_URLS.length;
	const examplePhotoSafeIndex =
		examplePhotoCount === 0
			? 0
			: ((examplePhotoIndex % examplePhotoCount) + examplePhotoCount) % examplePhotoCount;
	const examplePhotoCurrentUrl = QUESTIONNAIRE_EXAMPLE_PHOTO_URLS[examplePhotoSafeIndex] ?? '';
	const previewComponent = previewingComponentId
		? GARDEN_COMPONENTS.find((component) => component.id === previewingComponentId) ?? null
		: null;
	const previewComponentDetails = previewingComponentId ? COMPONENT_DESCRIPTIONS[previewingComponentId] : null;
	const componentPreviewUrls = previewingComponentId ? COMPONENT_PREVIEW_URLS[previewingComponentId] ?? [] : [];
	const componentPreviewCount = componentPreviewUrls.length;
	const componentPreviewSafeIndex =
		componentPreviewCount === 0
			? 0
			: ((componentPreviewIndex % componentPreviewCount) + componentPreviewCount) % componentPreviewCount;
	const componentPreviewCurrentUrl = componentPreviewUrls[componentPreviewSafeIndex] ?? '';
	const [translatedPreviewComponentDetails, setTranslatedPreviewComponentDetails] = useState<LocalizedComponentDescription | null>(null);
	const [translatedStyleRecommendationText, setTranslatedStyleRecommendationText] = useState<string>('');
	const previewTranslationCacheRef = useRef<Record<string, LocalizedComponentDescription>>({});
	const styleRecommendationTranslationCacheRef = useRef<Record<string, string>>({});
	const questionTranslationCacheRef = useRef<Record<string, AIGeneratedQuestion[]>>({});

	// Derived recommendation sets for components
	const photoRecommendedComponentIds = aiAnalysis?.recommendedComponents?.map((rec) => rec.componentId) ?? [];
	const styleRecommendedComponentIds =
		(form.styleKey && STYLE_COMPONENT_RECOMMENDATIONS[form.styleKey]) || [];
	const useRecommendedComponentIds =
		(form.gardenUses ?? []).flatMap((useId) => GARDEN_USE_COMPONENT_RECOMMENDATIONS[useId] ?? []);
	const allRecommendedComponentIds = Array.from(
		new Set([...photoRecommendedComponentIds, ...styleRecommendedComponentIds, ...useRecommendedComponentIds])
	);
	const hasPhotoRecommendations = photoRecommendedComponentIds.length > 0;
	const hasAnyRecommendations = allRecommendedComponentIds.length > 0;
	const hasStyleRecommendations = styleRecommendedComponentIds.length > 0;
	const hasUseRecommendations = useRecommendedComponentIds.length > 0;
	const componentFeasibilityMap = useMemo(() => {
		const map = new Map<string, NonNullable<AIAnalysis['componentFeasibility']>[number]>();
		(aiAnalysis?.componentFeasibility ?? []).forEach((item) => {
			if (item?.componentId) {
				map.set(item.componentId, item);
			}
		});
		return map;
	}, [aiAnalysis?.componentFeasibility]);
	const selectedComponentIds = form.selectedComponents ?? [];
	const selectedMajorComponentsCount = selectedComponentIds.filter((id) => MAJOR_COMPONENT_IDS.has(id)).length;
	const recommendedMaxMajorComponents =
		aiAnalysis?.layoutCapacity?.maxMajorComponents ??
		getFallbackMaxMajorComponents(form.gardenSize, aiAnalysis?.spaceType);
	const isOverMajorCapacity =
		recommendedMaxMajorComponents !== null && selectedMajorComponentsCount > recommendedMaxMajorComponents;
	const isOverTotalDensity =
		form.gardenSize > 0 && selectedComponentIds.length >= 8 && form.gardenSize <= 40;
	const hasRedundantWaterCombo =
		selectedComponentIds.includes('pool') && selectedComponentIds.includes('hot-tub');
	const selectedNotRecommendedComponents = selectedComponentIds
		.map((id) => ({ id, feasibility: componentFeasibilityMap.get(id) }))
		.filter((entry) => entry.feasibility?.fit === 'not_recommended');
	const needsGenerationFitConfirmation =
		isOverMajorCapacity || isOverTotalDensity || hasRedundantWaterCombo || selectedNotRecommendedComponents.length > 0;

	const addSelectedComponent = (componentId: string) => {
		setForm((prev) => {
			const current = prev.selectedComponents ?? [];
			if (current.includes(componentId)) return prev;
			return { ...prev, selectedComponents: [...current, componentId] };
		});
	};

	function scoreSelectedComponent(componentId: string): number {
		const feasibility = componentFeasibilityMap.get(componentId);
		const fit = feasibility?.fit;
		const fitScore =
			fit === 'good'
				? 3
				: fit === 'tight'
					? 1
					: fit === 'not_recommended'
						? componentId === 'driveway'
							? -1
							: -6
						: 1;
		const rec = aiAnalysis?.recommendedComponents?.find((r) => r.componentId === componentId);
		const recScore = rec?.priority === 'high' ? 3 : rec?.priority === 'medium' ? 2 : rec?.priority === 'low' ? 1 : 0;
		const styleScore = styleRecommendedComponentIds.includes(componentId) ? 1 : 0;
		const useScore = useRecommendedComponentIds.includes(componentId) ? 1 : 0;
		const photoScore = photoRecommendedComponentIds.includes(componentId) ? 1 : 0;
		return fitScore + recScore + styleScore + useScore + photoScore;
	}

	function optimizeSelectedComponentsForFit(selected: string[]): string[] {
		let next = Array.from(new Set(selected));

		// Keep driveway available in compact front-yard cases even if the AI fit came back too pessimistic.
		next = next.filter((id) => id === 'driveway' || componentFeasibilityMap.get(id)?.fit !== 'not_recommended');

		// Resolve common overlap in compact gardens.
		if (next.includes('pool') && next.includes('hot-tub') && form.gardenSize > 0 && form.gardenSize <= 45) {
			const poolScore = scoreSelectedComponent('pool');
			const hotTubScore = scoreSelectedComponent('hot-tub');
			if (poolScore >= hotTubScore) {
				next = next.filter((id) => id !== 'hot-tub');
			} else {
				next = next.filter((id) => id !== 'pool');
			}
		}

		// Keep major components within space capacity.
		const maxMajor =
			aiAnalysis?.layoutCapacity?.maxMajorComponents ??
			getFallbackMaxMajorComponents(form.gardenSize, aiAnalysis?.spaceType);
		if (maxMajor !== null) {
			const majors = next.filter((id) => MAJOR_COMPONENT_IDS.has(id));
			if (majors.length > maxMajor) {
				const sortedMajors = [...majors].sort((a, b) => scoreSelectedComponent(b) - scoreSelectedComponent(a));
				const keepMajors = new Set(sortedMajors.slice(0, maxMajor));
				next = next.filter((id) => !MAJOR_COMPONENT_IDS.has(id) || keepMajors.has(id));
			}
		}

		// Prevent extreme density for small spaces.
		const size = form.gardenSize || 0;
		const targetTotal = size <= 20 ? 5 : size <= 40 ? 7 : size <= 60 ? 9 : 12;
		if (next.length > targetTotal) {
			next = [...next]
				.sort((a, b) => scoreSelectedComponent(b) - scoreSelectedComponent(a))
				.slice(0, targetTotal);
		}

		return next;
	}

	const openComponentPreview = (componentId: string) => {
		const urls = COMPONENT_PREVIEW_URLS[componentId] ?? [];
		if (urls.length === 0) return;
		setPreviewingComponentId(componentId);
		setComponentPreviewIndex(0);
	};

	const [activeRecommendationFilters, setActiveRecommendationFilters] = useState<{
		photo: boolean;
		style: boolean;
		use: boolean;
	}>({
		photo: false,
		style: false,
		use: false
	});
	const isAllRecommendationFilterActive =
		activeRecommendationFilters.photo && activeRecommendationFilters.style && activeRecommendationFilters.use;

	const removeRecommendationsOnlyFromDisabledSource = (
		current: string[],
		disabledSourceIds: string[],
		stillActiveSourceIds: string[]
	): string[] => {
		const disabledSet = new Set(disabledSourceIds);
		const stillActiveSet = new Set(stillActiveSourceIds);
		return current.filter((id) => !disabledSet.has(id) || stillActiveSet.has(id));
	};

	const handleSelectAllRecommendedComponents = () => {
		if (!hasAnyRecommendations) return;

		setForm((prev) => {
			const current = prev.selectedComponents ?? [];
			const allSet = new Set(allRecommendedComponentIds);
			let next: string[];

			if (isAllRecommendationFilterActive) {
				// Turn ALL off: remove all recommended, keep any purely manual selections
				next = current.filter((id) => !allSet.has(id));
			} else {
				// Turn ALL on: add all recommended (keep existing manual selections)
				next = Array.from(new Set([...current, ...allRecommendedComponentIds]));
			}

			return {
				...prev,
				selectedComponents: next
			};
		});

		setActiveRecommendationFilters((prev) =>
			isAllRecommendationFilterActive
				? { photo: false, style: false, use: false }
				: {
						photo: hasPhotoRecommendations || prev.photo,
						style: hasStyleRecommendations || prev.style,
						use: hasUseRecommendations || prev.use
				  }
		);
	};

	const handleSelectPhotoRecommendedComponents = () => {
		if (!hasPhotoRecommendations) return;

		setForm((prev) => {
			const current = prev.selectedComponents ?? [];
			let next: string[];

			if (activeRecommendationFilters.photo) {
				// Toggle off: remove photo recommendations only if no other active source still recommends them
				const stillActiveIds = [
					...(activeRecommendationFilters.style ? styleRecommendedComponentIds : []),
					...(activeRecommendationFilters.use ? useRecommendedComponentIds : [])
				];
				next = removeRecommendationsOnlyFromDisabledSource(current, photoRecommendedComponentIds, stillActiveIds);
			} else {
				// Toggle on: add photo-based recommendations
				next = Array.from(new Set([...current, ...photoRecommendedComponentIds]));
			}

			return {
				...prev,
				selectedComponents: next
			};
		});

		setActiveRecommendationFilters((prev) => ({
			...prev,
			photo: !prev.photo
		}));
	};

	const handleSelectStyleRecommendedComponents = () => {
		if (!hasStyleRecommendations) return;

		setForm((prev) => {
			const current = prev.selectedComponents ?? [];
			let next: string[];

			if (activeRecommendationFilters.style) {
				// Toggle off: remove style recommendations only if no other active source still recommends them
				const stillActiveIds = [
					...(activeRecommendationFilters.photo ? photoRecommendedComponentIds : []),
					...(activeRecommendationFilters.use ? useRecommendedComponentIds : [])
				];
				next = removeRecommendationsOnlyFromDisabledSource(current, styleRecommendedComponentIds, stillActiveIds);
			} else {
				// Toggle on: add style-based recommendations
				next = Array.from(new Set([...current, ...styleRecommendedComponentIds]));
			}

			return {
				...prev,
				selectedComponents: next
			};
		});

		setActiveRecommendationFilters((prev) => ({
			...prev,
			style: !prev.style
		}));
	};

	const handleSelectUseRecommendedComponents = () => {
		if (!hasUseRecommendations) return;

		setForm((prev) => {
			const current = prev.selectedComponents ?? [];
			let next: string[];

			if (activeRecommendationFilters.use) {
				// Toggle off: remove use recommendations only if no other active source still recommends them
				const stillActiveIds = [
					...(activeRecommendationFilters.photo ? photoRecommendedComponentIds : []),
					...(activeRecommendationFilters.style ? styleRecommendedComponentIds : [])
				];
				next = removeRecommendationsOnlyFromDisabledSource(current, useRecommendedComponentIds, stillActiveIds);
			} else {
				// Toggle on: add use-based recommendations
				next = Array.from(new Set([...current, ...useRecommendedComponentIds]));
			}

			return {
				...prev,
				selectedComponents: next
			};
		});

		setActiveRecommendationFilters((prev) => ({
			...prev,
			use: !prev.use
		}));
	};

	// Derive up to a few recommended styles from AI style hints (highest-confidence matches to known presets)
	const recommendedStyleKeys = useMemo(() => {
		if (!aiAnalysis?.styleHints || aiAnalysis.styleHints.length === 0) return [] as string[];
		// Filter to styles we actually support in the UI and sort by confidence
		const sortedHints = aiAnalysis.styleHints
			.filter((hint) => !!stylePresetsByKey[hint.style])
			.sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0));
		if (sortedHints.length === 0) return [] as string[];
		// Prefer reasonably confident hints, but fall back to top hints so we don't always end up with a single style
		let topHints = sortedHints.filter((hint) => (hint.confidence ?? 0) >= 0.4).slice(0, 3);
		if (topHints.length === 0) {
			// If everything is low confidence, just take up to the top 3 anyway
			topHints = sortedHints.slice(0, 3);
		}
		return topHints.map((hint) => hint.style);
	}, [aiAnalysis]);

	// Detect invite code from query string (e.g. ?invite=ABC123 or ?code=ABC123)
	const inviteCodeFromUrl = (() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('invite') || params.get('code');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	})();
	const effectiveInviteCode = (manualInviteCode && manualInviteCode.trim()) || inviteCodeFromUrl;
	
	// Update AI analysis state when form changes (e.g., when editing a plan)
	useEffect(() => {
		if (form.aiAnalysis && !aiAnalysis) {
			setAiAnalysis(form.aiAnalysis);
		}
		if (form.aiAnswers && Object.keys(form.aiAnswers).length > 0 && Object.keys(aiAnswers).length === 0) {
			setAiAnswers(form.aiAnswers);
		}
		if (form.aiQuestionComments && Object.keys(form.aiQuestionComments).length > 0 && Object.keys(aiQuestionComments).length === 0) {
			setAiQuestionComments(form.aiQuestionComments);
		}
	}, [form.aiAnalysis, form.aiAnswers, form.aiQuestionComments]);

	// Load invite details (company name) when invite code is present
	useEffect(() => {
		let cancelled = false;
		if (!effectiveInviteCode) {
			setInviteInfo(null);
			setIsCheckingInvite(false);
			return;
		}
		(async () => {
			try {
				setIsCheckingInvite(true);
				setInviteInputError(null);
				const details = await getPlanInviteByCode(effectiveInviteCode);
				if (cancelled) return;
				setInviteInfo({
					code: details.invite.code,
					companyName: details.company.name
				});
				setIsCheckingInvite(false);
			} catch (err) {
				console.error('Failed to load invite details', err);
				if (!cancelled) {
					setInviteInfo(null);
					setInviteInputError('We could not find a valid invite with that code. Please check the code or ask your gardener/company to resend it.');
					setIsCheckingInvite(false);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [effectiveInviteCode]);

	// Check if we're editing an existing plan and load data
	useEffect(() => {
		if (guestMode) {
			return;
		}
		// Helper function to load plan data into form
		function loadPlanData(plan: PublicPlan, planId: string) {
			isEditingRef.current = true; // Mark that we're editing (synchronous ref)
			
			// Set editingPlanId state FIRST to prevent form reset
			setEditingPlanId(planId);
			
			// Merge questionnaire data with defaults to ensure all fields are properly set
			const questionnaire = plan.questionnaire!;
			
			const formData = {
				gardenSize: questionnaire.gardenSize ?? 0,
				gardenShape: questionnaire.gardenShape ?? undefined,
				selectedComponents: questionnaire.selectedComponents ?? [],
				photoBeforeUrl: questionnaire.photoBeforeUrl ?? undefined,
				styleKey: questionnaire.styleKey || (stylePresets[0]?.key ?? 'modern'),
				needsWants: questionnaire.needsWants || '',
				maintenanceLevel: questionnaire.maintenanceLevel ?? undefined,
				childrenPets: questionnaire.childrenPets ?? undefined,
				budgetRange: questionnaire.budgetRange ?? undefined,
				budgetPreference: questionnaire.budgetPreference ?? undefined,
				timeline: questionnaire.timeline || '',
				projectStartDate: questionnaire.projectStartDate ?? undefined,
				location: questionnaire.location || '',
				householdSize: questionnaire.gardenUsers ?? questionnaire.householdSize ?? undefined,
				gardenUsers: questionnaire.gardenUsers ?? questionnaire.householdSize ?? undefined,
				plantDensityPreference: questionnaire.plantDensityPreference ?? undefined,
				projectFocus: questionnaire.projectFocus ?? undefined,
				gardenUses: questionnaire.gardenUses ?? [],
				hostingMaxPeople: questionnaire.hostingMaxPeople ?? undefined,
				kidsCount: questionnaire.kidsCount ?? undefined,
				kidsAges: questionnaire.kidsAges ?? undefined,
				petsDescription: questionnaire.petsDescription ?? undefined,
				aiAnalysis: questionnaire.aiAnalysis ?? undefined,
				aiAnalysisComments: questionnaire.aiAnalysisComments ?? undefined,
				aiAnswers: questionnaire.aiAnswers ?? undefined,
				aiQuestionComments: questionnaire.aiQuestionComments ?? undefined,
				userCorrections: questionnaire.userCorrections ?? undefined
			};
			
			setForm(formData);
			setAiAnalysis(formData.aiAnalysis ?? null);
			setAiAnswers(formData.aiAnswers || {});
			setAiQuestionComments(formData.aiQuestionComments || {});
			
			setContactEmail(plan.contactEmail || '');
			
			// Set photo preview - prefer plan.photoBeforeUrl, then questionnaire.photoBeforeUrl
			const photoUrl = plan.photoBeforeUrl || questionnaire.photoBeforeUrl;
			if (photoUrl) {
				setPhotoPreviewUrl(normalizeStoredPhotoUrl(photoUrl));
			}
			
			// Clear any generic draft - we're loading from a specific plan
			localStorage.removeItem('gardenQuestionnaireDraft');

			const loadedPhotoPreview = photoUrl ? normalizeStoredPhotoUrl(photoUrl) : null;
			commitQuestionnaireLeaveBaseline({
				form: formData,
				contactEmail: plan.contactEmail || '',
				aiAnswers: (formData.aiAnswers ?? {}) as Record<string, unknown>,
				aiQuestionComments: formData.aiQuestionComments ?? {},
				hasLocalPhotoFile: false,
				photoPreviewUrl: loadedPhotoPreview
			});
		}
		
		// Always clear editingPlanId first - we'll set it only if we find a valid plan to edit
		const editingId = localStorage.getItem('editingPlanId');
		
		// If there's an editingId, verify the plan exists and has questionnaire data
		if (editingId) {
			
			// Check if form is already initialized (from useState initializer)
			// If form has data (not default empty), we might have already initialized it
			const formAlreadyInitialized = form.location || form.gardenSize > 0 || form.needsWants || (form.selectedComponents && form.selectedComponents.length > 0);
			
			// First try to get from localStorage
			let plan = getPublicPlanById(editingId, { includeHidden: true });
			
			if (plan) {
			} else {
			}
			
			// If not found, try fetching from API (for database plans)
			if (!plan && (editingId.startsWith('db-') || !isNaN(Number(editingId)))) {
				isEditingRef.current = true; // Mark that we're editing (synchronous ref)
				// Set editingPlanId state IMMEDIATELY to prevent form reset in other effects
				setEditingPlanId(editingId);
				getPublicPlanByIdFromAPI(editingId)
					.then(apiPlan => {
						
						if (apiPlan) {
							if (apiPlan.questionnaire) {
								loadPlanData(apiPlan, editingId);
							} else {
								console.warn('[GardenQuestionnaire] API plan found but no questionnaire data. Attempting to reconstruct...');
								// Try to reconstruct questionnaire from plan data
								const reconstructedQuestionnaire: QuestionnaireData = {
									gardenSize: 0,
									gardenShape: undefined,
									selectedComponents: [],
									photoBeforeUrl: apiPlan.photoBeforeUrl,
									styleKey: apiPlan.styleKey || (stylePresets[0]?.key ?? 'modern'),
									needsWants: apiPlan.notes || '',
									maintenanceLevel: undefined,
									childrenPets: undefined,
									budgetRange: undefined,
									timeline: '',
									projectStartDate: undefined,
									location: apiPlan.city || '',
									householdSize: undefined,
									gardenUsers: undefined,
									gardenUses: [],
									hostingMaxPeople: undefined,
									kidsCount: undefined,
									kidsAges: undefined,
									petsDescription: undefined,
									plantDensityPreference: undefined
								};
								
								const planWithQuestionnaire: PublicPlan = {
									...apiPlan,
									questionnaire: reconstructedQuestionnaire
								};
								
								loadPlanData(planWithQuestionnaire, editingId);
							}
						} else {
							console.warn('[GardenQuestionnaire] Plan not found via API');
							isEditingRef.current = false; // Allow form reset since plan wasn't found
							// Invalid or missing plan - clear editingPlanId
							localStorage.removeItem('editingPlanId');
							setEditingPlanId(null);
						}
					})
					.catch(err => {
						console.error('[GardenQuestionnaire] Failed to fetch plan from API:', err);
						isEditingRef.current = false; // Allow form reset since plan fetch failed
						// Invalid or missing plan - clear editingPlanId
						localStorage.removeItem('editingPlanId');
						setEditingPlanId(null);
					});
				return; // Exit early - async operation in progress
			}
			
			if (plan) {
				isEditingRef.current = true; // Mark that we're editing (synchronous ref)
				// Set editingPlanId state IMMEDIATELY to prevent form reset in other effects
				setEditingPlanId(editingId);
				if (plan.questionnaire) {
					// Only reload if form wasn't already initialized from useState
					if (!formAlreadyInitialized) {
						// Valid plan to edit - load its data
						loadPlanData(plan, editingId);
					} else {
						const photoUrlEarly = plan.photoBeforeUrl || plan.questionnaire?.photoBeforeUrl;
						commitQuestionnaireLeaveBaseline({
							form,
							contactEmail: plan.contactEmail || contactEmail,
							aiAnswers: (form.aiAnswers ?? {}) as Record<string, unknown>,
							aiQuestionComments: form.aiQuestionComments ?? {},
							hasLocalPhotoFile: false,
							photoPreviewUrl: photoUrlEarly ? normalizeStoredPhotoUrl(photoUrlEarly) : null
						});
					}
					return; // Exit early - we're editing
				} else {
					console.warn('[GardenQuestionnaire] Plan found but no questionnaire data. Plan:', plan);
					// Plan exists but no questionnaire - try to reconstruct from plan data
					// or allow editing with empty form
					const reconstructedQuestionnaire: QuestionnaireData = {
						gardenSize: 0,
						gardenShape: undefined,
						selectedComponents: [],
						photoBeforeUrl: plan.photoBeforeUrl,
						styleKey: plan.styleKey || (stylePresets[0]?.key ?? 'modern'),
						needsWants: plan.notes || '',
						maintenanceLevel: undefined,
						childrenPets: undefined,
						budgetRange: undefined,
						timeline: '',
						projectStartDate: undefined,
						location: plan.city || '',
						householdSize: undefined,
						gardenUsers: undefined,
						gardenUses: [],
						hostingMaxPeople: undefined,
						kidsCount: undefined,
						kidsAges: undefined,
						petsDescription: undefined,
						plantDensityPreference: undefined
					};
					
					// Create a plan object with reconstructed questionnaire
					const planWithQuestionnaire: PublicPlan = {
						...plan,
						questionnaire: reconstructedQuestionnaire
					};
					
					loadPlanData(planWithQuestionnaire, editingId);
					return; // Exit early - we're editing
				}
			} else {
				console.warn('[GardenQuestionnaire] Plan not found with ID:', editingId);
				// Invalid or missing plan - clear editingPlanId
				localStorage.removeItem('editingPlanId');
				// Don't set isEditing = true, so form can reset
			}
		} else {
		}
		
		// Only reset form if we're NOT editing (no editingPlanId was found AND we didn't load plan data)
		// This prevents clearing the form when we're in the middle of loading a plan
		// Use ref for synchronous check (state updates are async)
		if (!isEditingRef.current) {
			// Reset the ref to ensure it's false for new plans
			isEditingRef.current = false;
			// If we reach here, we're creating a new plan (not editing)
			// Check if there's a draft to restore
			const draftData = localStorage.getItem('gardenQuestionnaireDraft');
			let draftEmail = '';
			let draftCurrentStep = 1;
			let restoredForm: QuestionnaireData | null = null;
			let restoredPhotoPreviewUrl: string | null = null;
			if (draftData) {
				try {
					const parsed = JSON.parse(draftData);
					draftEmail = parsed.contactEmail || '';
					draftCurrentStep = typeof parsed.currentStep === 'number' ? parsed.currentStep : 1;
					if (parsed.form && typeof parsed.form === 'object') {
						const restoredDraftForm: QuestionnaireData = {
							...parsed.form,
							location: parsed.form.location || getSavedPreferredLocation(),
							photoBeforeUrl: parsed.form.photoBeforeUrl || parsed.photoBeforeUrl || undefined
						};
						restoredForm = restoredDraftForm;
						restoredPhotoPreviewUrl = restoredDraftForm.photoBeforeUrl || null;
					}
				} catch {
					// Invalid draft, ignore
				}
			}
			
			// Clear editing-related storage
			localStorage.removeItem('editingPlanId');
			localStorage.removeItem('currentDraftPlanId');
			
			const emptyFormForNewPlan: QuestionnaireData = {
				gardenSize: 0,
				gardenShape: undefined,
				selectedComponents: [],
				photoBeforeUrl: undefined,
				styleKey: stylePresets[0]?.key ?? 'modern',
				needsWants: '',
				maintenanceLevel: undefined,
				childrenPets: undefined,
				budgetRange: undefined,
				timeline: '',
				projectStartDate: undefined,
				location: getSavedPreferredLocation(),
				householdSize: undefined,
				gardenUsers: undefined,
				gardenUses: [],
				hostingMaxPeople: undefined,
				kidsCount: undefined,
				kidsAges: undefined,
				petsDescription: undefined,
				plantDensityPreference: undefined
			};
			const formToUse = restoredForm ?? emptyFormForNewPlan;
			// Restore saved draft when available; otherwise reset to a clean form.
			setForm(formToUse);
			// Use draft email if available, otherwise use logged-in user's email, otherwise empty
			const nextEmail = draftEmail || user?.email || '';
			setContactEmail(nextEmail);
			setCurrentStep(restoredForm ? migrateQuestionnaireDraftStep(draftCurrentStep, formToUse) : 1);
			setPhotoPreviewUrl(restoredPhotoPreviewUrl);
			setPhotoFile(null);
			setEditingPlanId(null);
			commitQuestionnaireLeaveBaseline({
				form: formToUse,
				contactEmail: nextEmail,
				aiAnswers: formToUse.aiAnswers || {},
				aiQuestionComments: formToUse.aiQuestionComments || {},
				hasLocalPhotoFile: false,
				photoPreviewUrl: restoredPhotoPreviewUrl
			});
		} else {
		}
	}, [guestMode]);

	// Update email if user logs in and email field is empty
	useEffect(() => {
		// Only update if we're not editing a plan and email is empty
		if (!editingPlanId && !contactEmail && user?.email) {
			setContactEmail(user.email);
		}
	}, [user?.email, editingPlanId, contactEmail]);

	// Auto-save draft to localStorage (only for new plans, not when editing)
	// When editing, the plan is updated via "Save Draft" or "Generate Plan" buttons
	useEffect(() => {
		if (guestMode) {
			return;
		}
		// Only auto-save if we're NOT editing (creating a new plan) and have some data
		// Use ref for synchronous check to prevent overwriting loaded plan data
		if (!isEditingRef.current && !editingPlanId && (form.location || (form.selectedComponents && form.selectedComponents.length > 0) || form.needsWants || contactEmail)) {
			// Save draft to localStorage - include photoBeforeUrl if available (but not File objects)
			const draftData: any = { 
				form, 
				contactEmail, 
				currentStep 
			};
			// Include photo URL if we have one (from existing plan or preview)
			// Don't save File objects - they'll be converted when user clicks "Save Draft"
			if (photoPreviewUrl && !photoFile) {
				// Only save if it's a data URL or URL string, not a blob URL
				if (photoPreviewUrl.startsWith('data:') || photoPreviewUrl.startsWith('http')) {
					draftData.photoBeforeUrl = photoPreviewUrl;
				}
			}
			localStorage.setItem('gardenQuestionnaireDraft', JSON.stringify(draftData));
		}
	}, [form, contactEmail, currentStep, photoPreviewUrl, photoFile, editingPlanId, guestMode]);

	// Photo preview and basic AI analysis (without questions)
	useEffect(() => {
		if (photoFile) {
			const url = URL.createObjectURL(photoFile);
			setPhotoPreviewUrl(url);
			
			// Trigger basic AI analysis when photo is uploaded (just for feature detection, not questions)
			handlePhotoAnalysis(photoFile);
			
			return () => URL.revokeObjectURL(url);
		}
		// Don't clear photoPreviewUrl if we're editing and have an existing photo
	}, [photoFile]);
	
	// Function to analyze photo with AI (basic analysis only - no questions yet)
	async function handlePhotoAnalysis(file: File) {
		try {
			setIsAnalyzing(true);
			
			// Convert file to data URL for analysis
			const dataUrl = await fileToDataURL(file);
			
			// Call AI analysis endpoint - but don't generate questions yet (we'll do that after step 2)
			// Pass style and garden uses from Step 1 so the AI can tailor the analysis
			const analysis = await analyzeGardenImage(dataUrl, true, form.styleKey, form.gardenUses, language);
			
			// Store analysis including suggested questions so Step 3 can reuse them
			// without making a second AI API call.
			const basicAnalysis: AIAnalysis = {
				isGarden: analysis.isGarden,
				gardenRecognitionNote: analysis.gardenRecognitionNote,
				spaceType: analysis.spaceType,
				detectedSize: analysis.detectedSize,
				detectedFeatures: analysis.detectedFeatures,
				constraints: analysis.constraints,
				styleHints: analysis.styleHints,
				recommendedComponents: analysis.recommendedComponents,
				componentFeasibility: analysis.componentFeasibility,
				layoutCapacity: analysis.layoutCapacity,
				suggestedQuestions: analysis.suggestedQuestions ?? []
			};
			
			setAiAnalysis(basicAnalysis);
			
			// Pre-fill form based on AI analysis
			applyAIAnalysisToForm(basicAnalysis);
			
			// Store analysis in form
			setForm(prev => ({
				...prev,
				aiAnalysis: basicAnalysis
			}));
			
		} catch (error) {
			console.error('Failed to analyze garden image:', error);
			void trackActivityEvent({
				event_name: 'questionnaire_ai_analysis_error',
				properties: { step: currentStep, guest_mode: guestMode }
			});
			showAlert('Could not read details from this photo. You can still continue with manual entry.', { 
				title: 'Photo check unavailable'
			});
		} finally {
			setIsAnalyzing(false);
		}
	}
	
	const questionBuildSignature = useMemo(
		() =>
			JSON.stringify({
				selectedComponents: [...(form.selectedComponents || [])].sort(),
				needsWants: form.needsWants || '',
				styleKey: form.styleKey || '',
				photo: resolvedPhotoPreviewUrl || '',
				aiSeedCount: (aiAnalysis?.suggestedQuestions || form.aiAnalysis?.suggestedQuestions || []).length,
			}),
		[
			form.selectedComponents,
			form.needsWants,
			form.styleKey,
			resolvedPhotoPreviewUrl,
			aiAnalysis?.suggestedQuestions,
			form.aiAnalysis?.suggestedQuestions,
		]
	);

	// Build/rebuild follow-up questions whenever relevant inputs changed (details step).
	useEffect(() => {
		if (currentStep !== 5 || isGeneratingQuestions) return;
		if (lastQuestionBuildSignatureRef.current === questionBuildSignature) return;
		generateAllQuestions(questionBuildSignature);
	}, [currentStep, questionBuildSignature, isGeneratingQuestions]);
	
	// Function to filter out components user wants to keep/remove
	function filterComponentsToAdd(selectedComponents: string[], needsWants: string, aiAnswers: Record<string, any>): string[] {
		// IMPORTANT:
		// Only use explicit free-text intent from "needsWants" for component filtering.
		// Using all aiAnswers here caused unrelated option clicks (e.g. "remove some")
		// to unexpectedly hide other component cards in the follow-up step.
		const allText = (needsWants || '').toLowerCase();
		
		// Check for explicit "keep", "leave", "don't change", "remove" mentions
		return selectedComponents.filter(componentId => {
			const componentName = componentId.toLowerCase();
			const componentLabel = GARDEN_COMPONENTS.find(c => c.id === componentId)?.label.toLowerCase() || componentName;
			
			// If user explicitly says to keep/leave/remove this component, don't show questions
			if (allText.includes(componentName) || allText.includes(componentLabel)) {
				const keepPatterns = ['keep', 'leave', "don't change", 'dont change', 'as is', 'as-is', 'same', 'unchanged'];
				const removePatterns = ['remove', 'delete', 'take out', 'get rid of', 'no ' + componentName, 'no ' + componentLabel];
				
				// Check context around the mention
				const mentionIndex = Math.max(
					allText.indexOf(componentName),
					allText.indexOf(componentLabel)
				);
				
				if (mentionIndex >= 0) {
					// Get surrounding text (50 chars before and after)
					const context = allText.substring(Math.max(0, mentionIndex - 50), mentionIndex + 50);
					
					// If user wants to keep/remove it, filter it out
					if (keepPatterns.some(pattern => context.includes(pattern)) ||
						removePatterns.some(pattern => context.includes(pattern))) {
						return false; // Don't show questions for this component
					}
				}
			}
			
			return true; // Show questions for this component
		});
	}
	
	// Function to prepare all questions (AI + component-specific) after step 2
	// Reuses AI questions from initial analysis; avoids a second AI request in normal flow.
	async function generateAllQuestions(signature?: string) {
		try {
			setIsGeneratingQuestions(true);

			// Filter components - only show questions for components user wants to ADD
			const componentsToAdd = filterComponentsToAdd(form.selectedComponents || [], form.needsWants, aiAnswers);
			const componentQs = getComponentQuestions(componentsToAdd);

			// Prefer questions from existing analysis to avoid duplicate AI calls.
			// Only use AI-sourced questions here (group === 'ai' or unset). Older builds may have
			// incorrectly saved the merged list (AI + components) into suggestedQuestions — that
			// caused component questions to be re-filtered as "AI" and dropped (e.g. isStyleQuestion).
			let rawSuggested = aiAnalysis?.suggestedQuestions || form.aiAnalysis?.suggestedQuestions || [];
			let sourceQuestions = rawSuggested.filter((q) => !q.group || q.group === 'ai');

			// Legacy fallback: if analysis exists but has no questions, fetch once.
			if (sourceQuestions.length === 0 && resolvedPhotoPreviewUrl) {
				let dataUrl: string;
				if (photoFile) {
					dataUrl = await new Promise<string>((resolve, reject) => {
						const reader = new FileReader();
						reader.onload = () => resolve(reader.result as string);
						reader.onerror = reject;
						reader.readAsDataURL(photoFile);
					});
				} else if (resolvedPhotoPreviewUrl.startsWith('data:')) {
					dataUrl = resolvedPhotoPreviewUrl;
				} else {
					// If it's a URL, we need to fetch it and convert
					const response = await fetch(resolvedPhotoPreviewUrl);
					const blob = await response.blob();
					dataUrl = await new Promise<string>((resolve, reject) => {
						const reader = new FileReader();
						reader.onload = () => resolve(reader.result as string);
						reader.onerror = reject;
						reader.readAsDataURL(blob);
					});
				}

				const analysis = await analyzeGardenImage(dataUrl, true, form.styleKey, form.gardenUses, language);
				sourceQuestions = analysis.suggestedQuestions || [];
				setAiAnalysis(prev => ({ ...(prev || {}), suggestedQuestions: sourceQuestions }));
				setForm(prev => ({
					...prev,
					aiAnalysis: {
						...(prev.aiAnalysis || {}),
						suggestedQuestions: sourceQuestions
					}
				}));
			}
			
			// Style names already chosen in step 2 - don't ask again in AI questions
			const STYLE_NAMES = stylePresets.map(s => s.key);
			const isStyleQuestion = (q: AIGeneratedQuestion) => {
				// Component catalog questions may use "style" in a non-garden-style sense (e.g. tile style) — never strip those.
				if (q.group && q.group !== 'ai') return false;
				const questionLower = q.question.toLowerCase();
				if (/style|aesthetic|garden style|plants.*prefer|type of plants/.test(questionLower)) return true;
				const opts = Array.isArray(q.options) ? q.options : [];
				const optionText = opts.map((o: string) => String(o).toLowerCase()).join(' ');
				if (STYLE_NAMES.some(s => optionText.includes(s))) return true;
				return false;
			};

			function getMentionedComponentIds(questionText: string): string[] {
				const normalized = questionText.toLowerCase();
				return GARDEN_COMPONENTS
					.filter((component) => {
						const aliases = COMPONENT_KEYWORD_ALIASES[component.id] ?? [];
						const labelChunks = component.label
							.toLowerCase()
							.split(/[/(),-]/g)
							.map((s) => s.trim())
							.filter((s) => s.length >= 4);
						const tokens = [component.id.replace(/-/g, ' '), ...aliases, ...labelChunks];
						return tokens.some((token) => normalized.includes(token));
					})
					.map((component) => component.id);
			}

			function isComponentIntentQuestion(questionText: string): boolean {
				return /(do|would|are)\s+you\s+(want|like|interested|planning|prefer).*(add|include|have|want)|\bdo you want\b|\bwould you like\b|\bare you interested\b|\bmajor components?\b.*\b(include|add|want)\b|\byou'?d love to include\b/i.test(questionText);
			}

			function isComponentContextQuestion(questionText: string): boolean {
				// Keep contextual follow-ups about existing elements or treatment decisions.
				return /\b(existing|current|already there|keep|maintain|replace|remove|repair|condition)\b/i.test(questionText);
			}

			function optionMapsToComponent(optionText: string): boolean {
				const normalized = optionText.toLowerCase();
				return Object.entries(COMPONENT_KEYWORD_ALIASES).some(([componentId, aliases]) => {
					const componentToken = componentId.replace(/-/g, ' ');
					return [componentToken, ...aliases].some((alias) => normalized.includes(alias));
				});
			}

			const detectedFeatureTokens = (aiAnalysis?.detectedFeatures || [])
				.map((feature) => String(feature?.name || '').toLowerCase().trim())
				.filter(Boolean);

			function isDetectedFeatureQuestion(questionText: string): boolean {
				const normalized = questionText.toLowerCase();
				return detectedFeatureTokens.some((token) => token.length >= 3 && normalized.includes(token));
			}

			function isSiteConditionQuestion(questionText: string): boolean {
				return /\b(sun|shade|shady|exposure|orientation|north|south|east|west|wind|slope|sloped|drainage|wet area|dry area|soil|privacy|overlooked)\b/i.test(questionText);
			}

			function isGardenUseDuplicateQuestion(questionText: string): boolean {
				return /\b(preferred use|use this garden|use this space|how (will|do) you use|what.*use.*garden|specific features)\b/i.test(questionText);
			}

			function isGeneralPreferenceQuestion(questionText: string): boolean {
				return /\b(anything else|other preferences|additional notes|what style|design style|look and feel)\b/i.test(questionText);
			}

			function isMaterialOrSpecPreferenceQuestion(questionText: string): boolean {
				return /\b(preferred material|what material|which material|paving material|path material|colour|color|finish|specific features|irrigation|sprinkler)\b/i.test(questionText);
			}

			// Filter AI questions - remove style duplicates + redundant component-selection questions.
			const filteredAiQs = (sourceQuestions || []).filter(q => {
				if (isStyleQuestion(q)) return false; // Style already chosen in step 2
				const questionText = q.question.toLowerCase();
				const allText = (form.needsWants + ' ' + Object.values(aiAnswers).join(' ')).toLowerCase();
				
				// Check if question is about a component user wants to keep/remove
				for (const component of GARDEN_COMPONENTS) {
					if (questionText.includes(component.id) || questionText.includes(component.label.toLowerCase())) {
						const keepPatterns = ['keep', 'leave', "don't change", 'dont change', 'as is', 'as-is'];
						const removePatterns = ['remove', 'delete', 'take out'];
						
						if (keepPatterns.some(p => allText.includes(p + ' ' + component.id) || allText.includes(p + ' ' + component.label.toLowerCase())) ||
							removePatterns.some(p => allText.includes(p + ' ' + component.id) || allText.includes(p + ' ' + component.label.toLowerCase()))) {
							return false; // Filter out this question
						}
					}
				}

				const mentionedComponentIds = getMentionedComponentIds(questionText);
				if (mentionedComponentIds.length > 0 && isComponentIntentQuestion(questionText) && !isComponentContextQuestion(questionText)) {
					// Step 2 already captures what components users want, so avoid re-asking that intent.
					return false;
				}

				if (isComponentIntentQuestion(questionText) && !isComponentContextQuestion(questionText)) {
					// Keep only contextual component questions (e.g. existing fence handling), not intent/preferences.
					return false;
				}

				if (isGardenUseDuplicateQuestion(questionText)) {
					// Step 2 already captures how the seeker wants to use the garden.
					return false;
				}

				if (isMaterialOrSpecPreferenceQuestion(questionText)) {
					// These are usually redundant with component-specific detail questions.
					return false;
				}

				const optionList = Array.isArray(q.options) ? q.options.map((o) => String(o)) : [];
				const componentMappedOptionCount = optionList.filter(optionMapsToComponent).length;
				if (
					componentMappedOptionCount >= 2 &&
					/\b(include|add|want|interested|major components?)\b/i.test(questionText)
				) {
					// Generic "which components do you want" variants are redundant with step 2.
					return false;
				}

				const selectedComponentSet = new Set(form.selectedComponents || []);
				if (
					selectedComponentSet.has('pathway') &&
					/\b(paving|pathway|walkway|path|patio material|path material)\b/i.test(questionText)
				) {
					// Pathway material/details are already handled by pathway component questions.
					return false;
				}

				// Strict mode: keep only photo/site-context AI questions.
				const isPhotoGrounded =
					isComponentContextQuestion(questionText) ||
					isDetectedFeatureQuestion(questionText) ||
					isSiteConditionQuestion(questionText);
				if (!isPhotoGrounded) {
					return false;
				}

				if (isGeneralPreferenceQuestion(questionText)) return false;
				
				return true; // Keep this question
			});
			
			// Combine and deduplicate questions
			const cappedAiQs = filteredAiQs.slice(0, MAX_AI_FOLLOW_UP_QUESTIONS);
			const combined = combineAndDeduplicateQuestions(cappedAiQs, componentQs);
			
			setAllQuestions(combined);
			
			// Keep only AI-suggested questions here (never the merged list) so we don't re-filter
			// component catalog questions as if they were AI output on the next run.
			const updatedAnalysis: AIAnalysis = {
				...aiAnalysis,
				suggestedQuestions: cappedAiQs,
				recommendedComponents: aiAnalysis?.recommendedComponents
			};
			setAiAnalysis(updatedAnalysis);
			setForm(prev => ({
				...prev,
				aiAnalysis: updatedAnalysis
			}));
			
		} catch (error) {
			console.error('Failed to generate questions:', error);
			void trackActivityEvent({
				event_name: 'questionnaire_ai_analysis_error',
				properties: { step: currentStep, context: 'generate_questions', guest_mode: guestMode }
			});
			// Fallback to just component questions
			const componentsToAdd = filterComponentsToAdd(form.selectedComponents || [], form.needsWants, aiAnswers);
			const componentQs = getComponentQuestions(componentsToAdd);
			setAllQuestions(componentQs);
		} finally {
			lastQuestionBuildSignatureRef.current = signature ?? questionBuildSignature;
			setIsGeneratingQuestions(false);
		}
	}

	// Translate follow-up questions (component catalog + AI) to the current UI language.
	// Component catalog questions are always authored in English; AI questions may already match
	// the UI language from photo analysis — translate in separate batches so one failure does not
	// leave component questions in English while AI text stays localized.
	useEffect(() => {
		let cancelled = false;
		if (!allQuestions.length) {
			setTranslatedQuestions([]);
			setIsTranslatingQuestions(false);
			return;
		}

		if (language === 'en') {
			setTranslatedQuestions(allQuestions);
			setIsTranslatingQuestions(false);
			return;
		}

		const cacheKey = `${language}::${allQuestions.map((q) => q.question).join('\u001e')}`;
		const cached = questionTranslationCacheRef.current[cacheKey];
		if (cached && cached.length === allQuestions.length) {
			setTranslatedQuestions(cached);
			setIsTranslatingQuestions(false);
			return;
		}

		const componentIndices: number[] = [];
		const componentBatch: AIGeneratedQuestion[] = [];
		const aiIndices: number[] = [];
		const aiBatch: AIGeneratedQuestion[] = [];
		allQuestions.forEach((q, idx) => {
			if (q.group && q.group !== 'ai') {
				componentIndices.push(idx);
				componentBatch.push(q);
			} else {
				aiIndices.push(idx);
				aiBatch.push(q);
			}
		});

		const mergeTranslatedBatch = (
			base: AIGeneratedQuestion[],
			indices: number[],
			translated: AIGeneratedQuestion[] | null | undefined
		): AIGeneratedQuestion[] => {
			if (!indices.length) return base;
			if (!Array.isArray(translated) || translated.length !== indices.length) return base;
			const merged = [...base];
			indices.forEach((origIdx, i) => {
				merged[origIdx] = translated[i];
			});
			return merged;
		};

		const translateBatch = async (questions: AIGeneratedQuestion[]) => {
			if (!questions.length) return [] as AIGeneratedQuestion[];
			const chunkSize = 20;
			const translated: AIGeneratedQuestion[] = [];
			for (let i = 0; i < questions.length; i += chunkSize) {
				const chunk = questions.slice(i, i + chunkSize);
				const chunkTranslated = await apiPost<AIGeneratedQuestion[]>('/translate-questionnaire-content', {
					questions: chunk,
					target_language: language
				});
				if (!Array.isArray(chunkTranslated) || chunkTranslated.length !== chunk.length) {
					throw new Error('Questionnaire translation chunk length mismatch');
				}
				translated.push(...chunkTranslated);
			}
			return translated;
		};

		setIsTranslatingQuestions(true);
		Promise.all([translateBatch(componentBatch), translateBatch(aiBatch)])
			.then(([translatedComponents, translatedAi]) => {
				if (cancelled) return;
				let merged = [...allQuestions];
				merged = mergeTranslatedBatch(merged, componentIndices, translatedComponents);
				merged = mergeTranslatedBatch(merged, aiIndices, translatedAi);
				questionTranslationCacheRef.current[cacheKey] = merged;
				setTranslatedQuestions(merged);
			})
			.catch(() => {
				if (!cancelled) setTranslatedQuestions(allQuestions);
			})
			.finally(() => {
				if (!cancelled) setIsTranslatingQuestions(false);
			});

		return () => {
			cancelled = true;
		};
	}, [allQuestions, language]);

	const questionsForDisplay =
		translatedQuestions.length === allQuestions.length ? translatedQuestions : allQuestions;
	const rawStyleRecommendationText = useMemo(
		() => (form.styleKey ? formatStyleRecommendations(form.styleKey) : ''),
		[form.styleKey]
	);

	useEffect(() => {
		let cancelled = false;
		const source = rawStyleRecommendationText;
		if (!source) {
			setTranslatedStyleRecommendationText('');
			return;
		}
		if (language === 'en') {
			setTranslatedStyleRecommendationText(source);
			return;
		}
		const cacheKey = `${language}::${source}`;
		const cached = styleRecommendationTranslationCacheRef.current[cacheKey];
		if (cached) {
			setTranslatedStyleRecommendationText(cached);
			return;
		}
		apiPost<string>('/translate-ui-content', {
			content: source,
			target_language: language
		})
			.then((translated) => {
				if (cancelled) return;
				const normalized = typeof translated === 'string' && translated.trim() ? translated : source;
				styleRecommendationTranslationCacheRef.current[cacheKey] = normalized;
				setTranslatedStyleRecommendationText(normalized);
			})
			.catch(() => {
				if (!cancelled) setTranslatedStyleRecommendationText(source);
			});

		return () => {
			cancelled = true;
		};
	}, [language, rawStyleRecommendationText]);

	useEffect(() => {
		let cancelled = false;
		if (!previewingComponentId || !previewComponentDetails) {
			setTranslatedPreviewComponentDetails(null);
			return;
		}
		if (language === 'en') {
			setTranslatedPreviewComponentDetails(previewComponentDetails);
			return;
		}
		const cacheKey = `${language}::${previewingComponentId}`;
		const cached = previewTranslationCacheRef.current[cacheKey];
		if (cached) {
			setTranslatedPreviewComponentDetails(cached);
			return;
		}
		apiPost<LocalizedComponentDescription>('/translate-ui-content', {
			content: previewComponentDetails,
			target_language: language
		})
			.then((translated) => {
				if (cancelled) return;
				const normalized = translated && typeof translated === 'object'
					? translated
					: previewComponentDetails;
				previewTranslationCacheRef.current[cacheKey] = normalized;
				setTranslatedPreviewComponentDetails(normalized);
			})
			.catch(() => {
				if (!cancelled) setTranslatedPreviewComponentDetails(previewComponentDetails);
			});
		return () => {
			cancelled = true;
		};
	}, [language, previewingComponentId, previewComponentDetails]);

	useEffect(() => {
		let cancelled = false;
		if (!aiAnalysis) {
			setTranslatedAnalysisText(null);
			return;
		}
		const source = {
			gardenRecognitionNote: aiAnalysis.gardenRecognitionNote || '',
			layoutComplexityWarning: aiAnalysis.layoutCapacity?.layoutComplexityWarning || '',
			detectedFeatures: Array.isArray(aiAnalysis.detectedFeatures) ? aiAnalysis.detectedFeatures : [],
			recommendedComponents: Array.isArray(aiAnalysis.recommendedComponents) ? aiAnalysis.recommendedComponents : [],
			componentFeasibility: Array.isArray(aiAnalysis.componentFeasibility) ? aiAnalysis.componentFeasibility : []
		};
		const cacheKey = `${language}::${JSON.stringify(source)}`;
		const cached = analysisTranslationCacheRef.current[cacheKey];
		if (cached) {
			setTranslatedAnalysisText(cached);
			return;
		}
		apiPost<typeof source>('/translate-ui-content', {
			content: source,
			target_language: language
		})
			.then((translated) => {
				if (cancelled) return;
				const normalized = translated && typeof translated === 'object' ? translated : source;
				analysisTranslationCacheRef.current[cacheKey] = normalized;
				setTranslatedAnalysisText(normalized);
			})
			.catch(() => {
				if (!cancelled) setTranslatedAnalysisText(source);
			});
		return () => {
			cancelled = true;
		};
	}, [aiAnalysis, language]);

	const displayedGardenRecognitionNote =
		translatedAnalysisText?.gardenRecognitionNote || aiAnalysis?.gardenRecognitionNote;
	const displayedLayoutComplexityWarning =
		translatedAnalysisText?.layoutComplexityWarning || aiAnalysis?.layoutCapacity?.layoutComplexityWarning;
	const displayedDetectedFeatures = useMemo(() => {
		const translated = translatedAnalysisText?.detectedFeatures;
		if (Array.isArray(translated) && translated.length > 0) return translated;
		return aiAnalysis?.detectedFeatures ?? [];
	}, [translatedAnalysisText, aiAnalysis?.detectedFeatures]);
	const translatedRecommendedComponentsById = useMemo(() => {
		const map = new Map<string, RecommendedComponent>();
		const list = translatedAnalysisText?.recommendedComponents;
		if (!Array.isArray(list)) return map;
		list.forEach((item) => {
			if (item?.componentId) map.set(item.componentId, item);
		});
		return map;
	}, [translatedAnalysisText]);
	const translatedFeasibilityById = useMemo(() => {
		const map = new Map<string, ComponentFeasibility>();
		const list = translatedAnalysisText?.componentFeasibility;
		if (!Array.isArray(list)) return map;
		list.forEach((item) => {
			if (item?.componentId) map.set(item.componentId, item);
		});
		return map;
	}, [translatedAnalysisText]);
	
	// Function to combine and deduplicate questions
	function combineAndDeduplicateQuestions(
		aiQuestions: AIGeneratedQuestion[],
		componentQuestions: AIGeneratedQuestion[]
	): AIGeneratedQuestion[] {
		const combined: AIGeneratedQuestion[] = [];
		const seenQuestions = new Set<string>();
		
		// Helper to normalize question text for comparison
		function normalizeQuestion(q: string): string {
			return q.toLowerCase()
				.replace(/[^\w\s]/g, '')
				.replace(/\s+/g, ' ')
				.trim();
		}
		
		// Add AI questions first (ensure each has a group for rendering; AI uses 'ai' if not set)
		aiQuestions.forEach(q => {
			const normalized = normalizeQuestion(q.question);
			if (!seenQuestions.has(normalized)) {
				seenQuestions.add(normalized);
				combined.push({ ...q, group: q.group ?? 'ai' });
			}
		});

		// Add component questions (skip if duplicate; they already have group + showWhen from getComponentQuestions)
		componentQuestions.forEach(q => {
			const normalized = normalizeQuestion(q.question);
			if (!seenQuestions.has(normalized)) {
				seenQuestions.add(normalized);
				combined.push(q);
			}
		});
		
		return combined;
	}

	/** Build grouped view for Smart Questions: group id, label, gate index, and items (question + global index). */
	type QuestionGroupInfo = {
		groupId: string;
		groupLabel: string;
		gateIndex: number | null;
		items: { question: AIGeneratedQuestion; index: number }[];
	};

type LocalizedComponentDescription = {
	whatItIs: string;
	commonTypes: string[];
	goodFor?: string[];
};
	function getQuestionGroups(questions: AIGeneratedQuestion[]): QuestionGroupInfo[] {
		const groupOrder: string[] = [];
		const map = new Map<string, { gateIndex: number | null; items: { question: AIGeneratedQuestion; index: number }[] }>();
		questions.forEach((q, index) => {
			const g = q.group ?? 'ai';
			if (!map.has(g)) {
				groupOrder.push(g);
				map.set(g, { gateIndex: q.type === 'yes_no' ? index : null, items: [] });
			}
			const entry = map.get(g)!;
			if (q.type === 'yes_no' && entry.gateIndex === null) entry.gateIndex = index;
			entry.items.push({ question: q, index });
		});
		return groupOrder.map(gid => {
			const entry = map.get(gid)!;
			const groupLabel = gid === 'ai'
				? copy('More details', 'Mehr Details', 'Više detalja')
				: localizeComponentLabel(gid, GARDEN_COMPONENTS.find(c => c.id === gid)?.label ?? gid);
			return { groupId: gid, groupLabel, gateIndex: entry.gateIndex, items: entry.items };
		});
	}

	useEffect(() => {
		const groups = getQuestionGroups(questionsForDisplay);
		if (groups.length === 0) {
			setExpandedQuestionGroups({});
			return;
		}
		setExpandedQuestionGroups((prev) => {
			const next: Record<string, boolean> = {};
			groups.forEach((grp) => {
				next[grp.groupId] = prev[grp.groupId] ?? false;
			});
			return next;
		});
	}, [questionsForDisplay]);

	/** True if this item should be shown (no showWhen, or gate answer matches). */
	function isQuestionVisible(
		item: { question: AIGeneratedQuestion; index: number },
		gateIndex: number | null
	): boolean {
		if (!item.question.showWhen) return true;
		if (gateIndex === null) return true; // no gate in group, show anyway
		return aiAnswers[`ai_q_${gateIndex}`] === 'yes';
	}

	// Function to pre-fill form based on AI analysis
	function applyAIAnalysisToForm(analysis: AIAnalysis) {
		const updates: Partial<QuestionnaireData> = {};
		const corrections: Array<{ field: string; aiValue: any; userValue: any }> = [];
		
		// Pre-fill garden size when AI detects it (medium confidence or higher)
		if (analysis.detectedSize && analysis.detectedSize.confidence > 0.3) {
			if (form.gardenSize === 0 || form.gardenSize === undefined) {
				updates.gardenSize = Math.round(analysis.detectedSize.value);
			} else {
				// User already entered a size - track as potential correction
				corrections.push({
					field: 'gardenSize',
					aiValue: analysis.detectedSize.value,
					userValue: form.gardenSize
				});
			}
		}
		
		// DON'T auto-select detected components - just show them as detected
		// User should explicitly choose what they want to add/modify
		// This prevents asking questions about things they want to keep as-is
		
		// Suggest style based on style hints
		if (analysis.styleHints && analysis.styleHints.length > 0) {
			const topStyleHint = analysis.styleHints.sort((a, b) => b.confidence - a.confidence)[0];
			if (topStyleHint.confidence > 0.6 && !form.styleKey) {
				// Only suggest if user hasn't selected a style yet
				// Don't auto-select, just show as suggestion
			}
		}
		
		// Apply updates
		if (Object.keys(updates).length > 0) {
			setForm(prev => ({
				...prev,
				...updates
			}));
		}
		
		// Store corrections
		if (corrections.length > 0) {
			setForm(prev => ({
				...prev,
				userCorrections: [...(prev.userCorrections || []), ...corrections]
			}));
		}
	}

	// Initialize Bootstrap tooltips
	useEffect(() => {
		const tooltipTriggerList = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"]'));
		const tooltipList = tooltipTriggerList.map((tooltipTriggerEl) => {
			// @ts-ignore - Bootstrap is loaded globally
			return new bootstrap.Tooltip(tooltipTriggerEl);
		});
		return () => {
			tooltipList.forEach(tooltip => {
				tooltip.dispose();
			});
		};
	}, [currentStep]); // Re-initialize when step changes

	// Check permission state when reaching step 2 (where location is asked).
	// We only update permissionState for UI hints; location is filled only when the user clicks "Use my location".
	useEffect(() => {
		if (currentStep === 3 && 'permissions' in navigator && navigator.permissions) {
			(navigator.permissions as any).query({ name: 'geolocation' }).then((result: PermissionStatus) => {
				setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
				result.addEventListener('change', () => {
					const newState = result.state as 'prompt' | 'granted' | 'denied';
					setPermissionState(newState);
				});
			}).catch(() => {
				setPermissionState('unknown');
			});
		}
	}, [currentStep]);

	// Preload component hero images when entering the components step.
	useEffect(() => {
		if (currentStep !== 4) return;
		GARDEN_COMPONENTS.forEach((component) => {
			const url = getComponentHeroImageUrl(component.id);
			if (!url) return;
			const img = new Image();
			img.src = url;
		});
	}, [currentStep]);

	function validateStep(step: number): boolean {
		const newErrors: Record<string, string> = {};
		
		if (step === 1) {
			if (!photoFile && !photoPreviewUrl) {
				newErrors.photoFile = copy(
					'Please upload a photo of your garden to continue.',
					'Bitte laden Sie ein Foto Ihres Gartens hoch, um fortzufahren.',
					'Molimo učitajte fotografiju vrta kako biste nastavili.'
				);
			} else if (photoFile) {
				const photoValidationError = getPhotoValidationError(photoFile);
				if (photoValidationError) newErrors.photoFile = photoValidationError;
			}
			// Soft warning only: do not block progression on "not a garden".
			// Exterior edge-cases (driveway/front yard/entry zone) are still valid redesign spaces.
			if ((photoFile || photoPreviewUrl) && aiAnalysis && aiAnalysis.isGarden === false) {
				void trackActivityEvent({
					event_name: 'questionnaire_validation_error',
					properties: { step, fields: ['ai_not_garden_soft_warning'], guest_mode: guestMode }
				});
			}
		} else if (step === 2) {
			if (!form.styleKey) {
				newErrors.styleKey = copy(
					'Please choose a style to continue.',
					'Bitte wählen Sie einen Stil, um fortzufahren.',
					'Molimo odaberite stil kako biste nastavili.'
				);
			}
		} else if (step === 3) {
			if (!form.gardenSize || form.gardenSize <= 0) {
				newErrors.gardenSize = copy(
					'Garden size is required.',
					'Gartengröße ist erforderlich.',
					'Veličina vrta je obavezna.'
				);
			}
			if (!form.location.trim()) {
				newErrors.location = copy(
					'Location is required.',
					'Standort ist erforderlich.',
					'Lokacija je obavezna.'
				);
			}
			if (SHOW_CONTACT_EMAIL_QUESTION) {
				if (!contactEmail.trim()) {
					newErrors.contactEmail = copy(
						'Contact email is required.',
						'Kontakt-E-Mail ist erforderlich.',
						'Kontakt e-mail je obavezan.'
					);
				} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
					newErrors.contactEmail = copy(
						'Enter a valid email.',
						'Geben Sie eine gültige E-Mail ein.',
						'Unesite valjanu e-mail adresu.'
					);
				}
			}
		}
		
		setErrors(newErrors);
		
		if (Object.keys(newErrors).length > 0) {
			void trackActivityEvent({
				event_name: 'questionnaire_validation_error',
				properties: { step, fields: Object.keys(newErrors), guest_mode: guestMode }
			});
			queueMicrotask(() => {
				if (firstInvalidRef.current) {
					firstInvalidRef.current.focus();
				}
			});
			return false;
		}
		
		return true;
	}

	function validate(): boolean {
		// Validate all steps
		for (let step = 1; step <= steps.length; step++) {
			if (!validateStep(step)) {
				setCurrentStep(step);
				return false;
			}
		}
		return true;
	}

	function handleNext() {
		// Prevent skipping ahead while AI work is still in flight
		if (isAnalyzing || isGeneratingQuestions || isGeneratingPlan) {
			return;
		}
		if (validateStep(currentStep)) {
			if (currentStep < steps.length) {
				const nextStep = currentStep + 1;
				void trackActivityEvent({
					event_name: 'questionnaire_step_next',
					properties: { step_from: currentStep, step_to: nextStep, guest_mode: guestMode }
				});
				setCurrentStep(nextStep);
				
				// Follow-up questions on step 5 are built in useEffect (avoids double API / double merge)
				
				// Scroll to top in the internal questionnaire scroller and window.
				requestAnimationFrame(scrollQuestionnaireToTop);
			}
		}
	}

	function handleBack() {
		if (currentStep > 1) {
			void trackActivityEvent({
				event_name: 'questionnaire_step_back',
				properties: { step_from: currentStep, step_to: currentStep - 1, guest_mode: guestMode }
			});
			setCurrentStep(currentStep - 1);
			setErrors({});
			// Scroll to top in the internal questionnaire scroller and window.
			requestAnimationFrame(scrollQuestionnaireToTop);
		}
	}

	/** Build absolute URL for an upload path */
	function toAbsoluteUploadUrl(relativePath: string): string {
		const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`;
		return getImageUrl(path) ?? `${getApiBaseUrl()}${path}`;
	}

	async function convertHeicIfNeeded(file: File): Promise<File> {
		const mime = inferPhotoMime(file);
		if (!HEIC_MIME_TYPES.includes(mime)) return file;
		const heic2anyModule = await import('heic2any');
		const heic2any = heic2anyModule.default as (options: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]>;
		const converted = await heic2any({
			blob: file,
			toType: 'image/jpeg',
			quality: 0.9
		});
		const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
		if (!convertedBlob) throw new Error('HEIC conversion returned no image');
		const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
		return new File([convertedBlob], `${baseName}.jpg`, { type: 'image/jpeg' });
	}

	async function uploadPlanImageFile(rawFile: File): Promise<string> {
		const convertedFile = await convertHeicIfNeeded(rawFile);
		const file = await optimizePhotoFileForUpload(convertedFile);
		const formData = new FormData();
		formData.append('image', file);
		formData.append('type', 'plan');
		formData.append('subtype', 'before');
		const response = await fetch(buildApiUrl('/upload'), { method: 'POST', body: formData });
		const data = await response.json().catch(() => ({}));
		if (!response.ok || !data?.success || !data?.data?.url) {
			throw new Error(data?.error || 'Upload failed');
		}
		const url = data.data.url as string;
		return url.startsWith('http') ? url : toAbsoluteUploadUrl(url);
	}

	/** Upload image (File or data URL) and return absolute URL for issues API */
	async function uploadImageForReport(): Promise<string> {
		if (photoFile) {
			try {
				// Convert to a backend-compatible type (JPEG/PNG/WebP) to avoid iPhone HEIC issues.
				const photoDataUrl = await fileToDataURL(photoFile);
				const res = await fetch(photoDataUrl);
				const blob = await res.blob();
				const convertedFile = new File(
					[blob],
					blob.type === 'image/png' ? 'garden.png' : 'garden.jpg',
					{ type: blob.type || 'image/jpeg' }
				);
				return await uploadPlanImageFile(convertedFile);
			} catch {
				// Fallback: if device-side processing fails, upload original/HEIC-converted file directly.
				return await uploadPlanImageFile(photoFile);
			}
		}
		if (resolvedPhotoPreviewUrl?.startsWith('data:')) {
			const res = await fetch(resolvedPhotoPreviewUrl);
			const blob = await res.blob();
			const file = new File([blob], 'garden.png', { type: blob.type || 'image/png' });
			const formData = new FormData();
			formData.append('image', file);
			formData.append('type', 'plan');
			formData.append('subtype', 'before');
			const response = await fetch(buildApiUrl('/upload'), { method: 'POST', body: formData });
			const data = await response.json();
			if (!response.ok || !data.success || !data.data?.url) {
				throw new Error(data.error || 'Upload failed');
			}
			const url = data.data.url;
			return url.startsWith('http') ? url : toAbsoluteUploadUrl(url);
		}
		if (resolvedPhotoPreviewUrl?.startsWith('http')) {
			return resolvedPhotoPreviewUrl;
		}
		throw new Error('No image to report');
	}

	async function handleSubmitReport() {
		const userId = user?.id;
		const userIdNum = typeof userId === 'string' ? parseInt(userId, 10) : userId;
		if (!userIdNum || isNaN(userIdNum)) {
			showAlert('You must be logged in to submit a report.', { title: 'Sign in required' });
			return;
		}
		setIsSubmittingReport(true);
		try {
			const imageUrl = await uploadImageForReport();
			await createIssue({
				reporter_user_id: userIdNum,
				image_url: imageUrl,
				message: reportMessage.trim() || null
			});
			setShowReportModal(false);
			setReportMessage('');
			showAlert('Thank you. Your report has been sent. Our team will review it and get back to you if needed.', { title: 'Report submitted' });
		} catch (err) {
			console.error('Report submit error:', err);
			showAlert(err instanceof Error ? err.message : 'Failed to submit report. Please try again.', { title: 'Error' });
		} finally {
			setIsSubmittingReport(false);
		}
	}

	// Normalize large photos before storing them in browser state or sending them to the backend.
	async function fileToDataURL(file: File): Promise<string> {
		const validationError = getPhotoValidationError(file);
		if (validationError) throw new Error(validationError);

		let workingFile = file;
		const inputMime = inferPhotoMime(file);
		if (HEIC_MIME_TYPES.includes(inputMime)) {
			try {
				const heic2anyModule = await import('heic2any');
				const heic2any = heic2anyModule.default as (options: { blob: Blob; toType: string; quality?: number }) => Promise<Blob | Blob[]>;
				const converted = await heic2any({
					blob: file,
					toType: 'image/jpeg',
					quality: 0.9
				});
				const convertedBlob = Array.isArray(converted) ? converted[0] : converted;
				if (!convertedBlob) {
					throw new Error('HEIC conversion returned no image');
				}
				const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
				workingFile = new File([convertedBlob], `${baseName}.jpg`, { type: 'image/jpeg' });
			} catch (err) {
				throw new Error(`HEIC conversion failed: ${err instanceof Error ? err.message : 'unknown error'}`);
			}
		}
		workingFile = await optimizePhotoFileForUpload(workingFile);

		return await new Promise<string>((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = () => {
				const result = reader.result;
				if (typeof result !== 'string' || !result.startsWith('data:image')) {
					reject(new Error('Photo processing failed: invalid image data URL'));
					return;
				}
				if (estimateDataUrlBytes(result) > PHOTO_HARD_MAX_BYTES) {
					reject(new Error('Photo is too large'));
					return;
				}
				resolve(result);
			};
			reader.onerror = () => reject(new Error('Photo processing failed: unable to read file'));
			reader.readAsDataURL(workingFile);
		});
	}

	// Function to get and set user's location
	const handleUseMyLocation = (retryCount = 0) => {
		if (!navigator.geolocation) {
			// Geolocation not supported - user can enter location manually
			return;
		}

		const maxRetries = 1;
		// First attempt: longer timeout so permission prompt + first fix can complete. Retry: same options.
		const options: PositionOptions = {
			enableHighAccuracy: false, // Use lower accuracy for faster response (good enough for city-level)
			timeout: retryCount === 0 ? 15000 : 10000, // 15s first time (permission + fix), 10s on retry
			maximumAge: 300000, // Allow cached location up to 5 minutes old (faster if available)
		};

		setIsGettingLocation(true);
		// Request location - this will trigger the browser's permission prompt if needed
		navigator.geolocation.getCurrentPosition(
			async (position) => {
				try {
					const { latitude, longitude } = position.coords;
					// Use backend proxy to avoid CORS when frontend is on a different origin (e.g. agilos-it.com)
					const result = await apiGet<{ city: string }>(
						`/geocode/reverse?lat=${latitude}&lon=${longitude}`
					);
					const city = result?.city ?? '';
					if (city) {
						setForm(prev => ({ ...prev, location: city }));
						localStorage.setItem(SEEKER_SAVED_LOCATION_KEY, city);
					}
				} catch (error) {
					// Failed to reverse geocode - user can enter location manually; fail silently
				} finally {
					setIsGettingLocation(false);
				}
			},
			(error) => {
				// Permission denied - don't retry
				if (error.code === error.PERMISSION_DENIED) {
					setIsGettingLocation(false);
					return;
				}
				// Timeout or position unavailable: retry once so first click often succeeds without user clicking again
				if ((error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) && retryCount < maxRetries) {
					handleUseMyLocation(retryCount + 1);
					return;
				}
				setIsGettingLocation(false);
			},
			options
		);
	};

	async function handleSaveDraft() {
		try {
			// Convert photo file to base64 data URL if present
			let photoDataUrl: string | undefined = undefined;
			if (photoFile) {
				try {
					photoDataUrl = await fileToDataURL(photoFile);
				} catch (err) {
					console.error('Failed to convert photo to data URL:', err);
					// Don't return - continue saving without photo
				}
			}
			
			// Get existing plan data if editing (to preserve what's already there)
			const existingPlan = editingPlanId ? getPublicPlanById(editingPlanId, { includeHidden: true }) : null;
			const existingQuestionnaire = existingPlan?.questionnaire;
			
			// Prepare questionnaire data - merge with existing data and use defaults for missing required fields
			// This allows saving drafts with partial data
			const questionnaireData: QuestionnaireData = {
				// Use existing values as fallback, then current form values, then defaults
				// Use ?? for numeric values to preserve 0, || for strings
				gardenSize: form.gardenSize ?? existingQuestionnaire?.gardenSize ?? 0,
				gardenShape: form.gardenShape ?? existingQuestionnaire?.gardenShape ?? undefined,
				selectedComponents: form.selectedComponents && form.selectedComponents.length > 0 
					? form.selectedComponents 
					: existingQuestionnaire?.selectedComponents || [],
				photoBeforeUrl: photoDataUrl || photoPreviewUrl || existingQuestionnaire?.photoBeforeUrl || existingPlan?.photoBeforeUrl || undefined,
				styleKey: form.styleKey || existingQuestionnaire?.styleKey || (stylePresets[0]?.key ?? 'modern'),
				needsWants: form.needsWants || existingQuestionnaire?.needsWants || '',
				maintenanceLevel: form.maintenanceLevel ?? existingQuestionnaire?.maintenanceLevel ?? undefined,
				childrenPets: (() => {
					if (!(form.gardenUses ?? []).includes('kids_pets')) return existingQuestionnaire?.childrenPets ?? undefined;
					const hasKids = (form.kidsCount != null && form.kidsCount > 0) || (form.kidsAges != null && String(form.kidsAges).trim() !== '');
					const hasPets = form.petsDescription != null && String(form.petsDescription).trim() !== '';
					return hasKids && hasPets ? 'both' : hasKids ? 'children' : hasPets ? 'pets' : 'both';
				})(),
				kidsCount: form.kidsCount ?? existingQuestionnaire?.kidsCount ?? undefined,
				kidsAges: form.kidsAges ?? existingQuestionnaire?.kidsAges ?? undefined,
				petsDescription: form.petsDescription ?? existingQuestionnaire?.petsDescription ?? undefined,
				budgetRange: form.budgetRange ?? existingQuestionnaire?.budgetRange ?? undefined,
				timeline: form.timeline || existingQuestionnaire?.timeline || '',
				projectStartDate: form.projectStartDate || existingQuestionnaire?.projectStartDate || undefined,
				location: form.location || existingQuestionnaire?.location || '',
				householdSize: form.gardenUsers ?? form.householdSize ?? existingQuestionnaire?.gardenUsers ?? existingQuestionnaire?.householdSize ?? undefined,
				gardenUsers: form.gardenUsers ?? form.householdSize ?? existingQuestionnaire?.gardenUsers ?? existingQuestionnaire?.householdSize ?? undefined,
				projectFocus: form.projectFocus ?? existingQuestionnaire?.projectFocus ?? undefined,
				gardenUses: form.gardenUses?.length ? form.gardenUses : (existingQuestionnaire?.gardenUses ?? []),
				hostingMaxPeople: form.hostingMaxPeople ?? existingQuestionnaire?.hostingMaxPeople ?? undefined,
				plantDensityPreference: form.plantDensityPreference ?? existingQuestionnaire?.plantDensityPreference ?? undefined,
				// Include AI analysis and answers
				aiAnalysis: aiAnalysis || existingQuestionnaire?.aiAnalysis || undefined,
				aiAnswers: Object.keys(aiAnswers).length > 0 ? aiAnswers : existingQuestionnaire?.aiAnswers || undefined,
				aiQuestionComments: Object.keys(aiQuestionComments).length > 0 ? aiQuestionComments : existingQuestionnaire?.aiQuestionComments || undefined,
				userCorrections: form.userCorrections || existingQuestionnaire?.userCorrections || undefined
			};
			
			// Don't save base64 image data to localStorage - it's too large
			// Only save URLs (not data URLs starting with data:)
			const photoUrl = questionnaireData.photoBeforeUrl;
			const isBase64DataUrl = photoUrl?.startsWith('data:');
			const photoUrlForStorage = isBase64DataUrl ? undefined : photoUrl;
			
			// Save minimal data to localStorage (without base64 images)
			const draftData: any = { 
				form: {
					...questionnaireData,
					photoBeforeUrl: photoUrlForStorage // Only save URL, not base64
				}, 
				contactEmail: contactEmail || existingPlan?.contactEmail || '', 
				currentStep 
			};
			
			try {
				localStorage.setItem('gardenQuestionnaireDraft', JSON.stringify(draftData));
			} catch (storageErr) {
				console.error('Failed to save to localStorage:', storageErr);
				// Don't return - continue to try database save
			}
			
			// If editing, also update the plan (without regenerating AI)
			if (editingPlanId) {
				try {
					if (!existingPlan) {
						showAlert('Plan not found. Please try again.', { title: 'Error' });
						localStorage.removeItem('editingPlanId');
						return;
					}
					
					// Build title and city with fallbacks
					const location = questionnaireData.location || existingPlan.city || 'Location';
					const styleTitle = stylePresetsByKey[questionnaireData.styleKey]?.title || 'Garden';
					const planTitle = `${styleTitle} Garden - ${location}`;
					const aiPersist = buildAiQuestionnairePersistenceForPlanApi({ questionnaireData, allQuestions });

					// Update the plan with current questionnaire data (keep existing AI content)
					updatePublicPlan(editingPlanId, {
						questionnaire: questionnaireData,
						contactEmail: contactEmail || existingPlan.contactEmail,
						photoBeforeUrl: photoDataUrl || photoPreviewUrl || existingPlan.photoBeforeUrl,
						city: location,
						title: planTitle
					});

					if (existingPlan.databaseId) {
						let before_image_url: string | undefined =
							typeof photoUrlForStorage === 'string' && photoUrlForStorage.length > 0
								? photoUrlForStorage
								: typeof existingPlan.photoBeforeUrl === 'string' &&
									  (existingPlan.photoBeforeUrl.startsWith('http') || existingPlan.photoBeforeUrl.startsWith('/'))
									? existingPlan.photoBeforeUrl
									: undefined;
						if (photoDataUrl?.startsWith('data:')) {
							const uploaded = await uploadPlanBeforeImageFromDataUrl(photoDataUrl);
							if (uploaded) before_image_url = uploaded;
						}
						if (
							!before_image_url &&
							typeof photoPreviewUrl === 'string' &&
							(photoPreviewUrl.startsWith('http') || photoPreviewUrl.startsWith('/'))
						) {
							before_image_url = photoPreviewUrl;
						}
						try {
							await updatePlanContent(existingPlan.databaseId, {
								title: planTitle,
								description: questionnaireData.needsWants || undefined,
								style: questionnaireData.styleKey,
								location: questionnaireData.location || undefined,
								garden_size: questionnaireData.gardenSize || undefined,
								garden_shape: questionnaireData.gardenShape,
								garden_components:
									questionnaireData.selectedComponents && questionnaireData.selectedComponents.length > 0
										? questionnaireData.selectedComponents
										: undefined,
								needs_wants: questionnaireData.needsWants || undefined,
								maintenance_level: questionnaireData.maintenanceLevel || undefined,
								children_pets: questionnaireData.childrenPets || undefined,
								budget_min: questionnaireData.budgetRange?.min || undefined,
								budget_max: questionnaireData.budgetRange?.max || undefined,
								timeline: questionnaireData.timeline || undefined,
								project_start_date: questionnaireData.projectStartDate || undefined,
								contact_email: contactEmail || existingPlan.contactEmail || undefined,
								household_size: questionnaireData.householdSize ?? undefined,
								garden_users: questionnaireData.gardenUsers ?? undefined,
								project_focus: questionnaireData.projectFocus ?? undefined,
								garden_uses: questionnaireData.gardenUses?.length ? questionnaireData.gardenUses : undefined,
								hosting_max_people: questionnaireData.hostingMaxPeople ?? undefined,
								kids_count: questionnaireData.kidsCount ?? undefined,
								kids_ages: questionnaireData.kidsAges ?? undefined,
								pets_description: questionnaireData.petsDescription ?? undefined,
								plant_density_preference: questionnaireData.plantDensityPreference || undefined,
								before_image_url,
								append_previous_revision: false,
								...aiPersist
							});
							await getPublicPlanByIdFromAPI(`db-${existingPlan.databaseId}`);
						} catch (apiErr) {
							console.error('Failed to sync draft questionnaire to server:', apiErr);
							showAlert(
								'Saved on this device, but syncing to your account failed. Check your connection and try Save again.',
								{ title: 'Partial save' }
							);
							return;
						}
					}

					showAlert('Changes saved successfully!', { title: 'Changes saved' });
					commitQuestionnaireLeaveBaseline({
						form,
						contactEmail,
						aiAnswers,
						aiQuestionComments,
						hasLocalPhotoFile: !!photoFile,
						photoPreviewUrl
					});
				} catch (err) {
					console.error('Failed to update plan:', err);
					showAlert('Draft saved locally, but failed to update plan. Please try again.', { title: 'Partial save' });
				}
			} else {
				// Creating a new draft - try to save to database first if user is logged in
				const userId = user?.id;
				let userIdNum: number | undefined;
				if (typeof userId === 'string') {
					const parsed = parseInt(userId, 10);
					userIdNum = !isNaN(parsed) ? parsed : undefined;
				} else if (typeof userId === 'number') {
					userIdNum = userId;
				}
				const isNumericUserId = userIdNum !== undefined && !isNaN(userIdNum);
				
				// Try to save to database if user is logged in
				if (isNumericUserId && userIdNum !== undefined) {
					try {
						let uploadedPhotoUrl: string | undefined = undefined;
						if (photoDataUrl && photoDataUrl.startsWith('data:')) {
							uploadedPhotoUrl = await uploadPlanBeforeImageFromDataUrl(photoDataUrl);
							if (uploadedPhotoUrl) {
							}
						}
						if (!uploadedPhotoUrl) {
							uploadedPhotoUrl = photoDataUrl || photoPreviewUrl || undefined;
						}
						
						// Build title and city
						const location = questionnaireData.location || 'Location';
						const styleTitle = stylePresetsByKey[questionnaireData.styleKey]?.title || 'Garden';
						const planTitle = `${styleTitle} Garden - ${location}`;
						
						// Save to database
						const aiPersist = buildAiQuestionnairePersistenceForPlanApi({ questionnaireData, allQuestions });
						const draftContentPayload = {
							title: planTitle,
							description: questionnaireData.needsWants || undefined,
							location: location,
							style: questionnaireData.styleKey,
							garden_size: questionnaireData.gardenSize || undefined,
							garden_shape: questionnaireData.gardenShape || undefined,
							garden_components:
								questionnaireData.selectedComponents && questionnaireData.selectedComponents.length > 0
									? questionnaireData.selectedComponents
									: undefined,
							needs_wants: questionnaireData.needsWants || undefined,
							maintenance_level: questionnaireData.maintenanceLevel || undefined,
							children_pets: questionnaireData.childrenPets || undefined,
							budget_min: questionnaireData.budgetRange?.min || undefined,
							budget_max: questionnaireData.budgetRange?.max || undefined,
							timeline: questionnaireData.timeline || undefined,
							project_start_date: questionnaireData.projectStartDate || undefined,
							contact_email: contactEmail || undefined,
							before_image_url: uploadedPhotoUrl || undefined,
							household_size: questionnaireData.householdSize ?? undefined,
							garden_users: questionnaireData.gardenUsers ?? undefined,
							project_focus: questionnaireData.projectFocus ?? undefined,
							garden_uses: (questionnaireData.gardenUses?.length ? questionnaireData.gardenUses : undefined) as string[] | undefined,
							hosting_max_people: questionnaireData.hostingMaxPeople ?? undefined,
							kids_count: questionnaireData.kidsCount ?? undefined,
							kids_ages: questionnaireData.kidsAges ?? undefined,
							pets_description: questionnaireData.petsDescription ?? undefined,
							plant_density_preference: questionnaireData.plantDensityPreference || undefined,
							...aiPersist
						};

						const existingDraftKey = localStorage.getItem('currentDraftPlanId');
						const existingDraftDbId =
							existingDraftKey?.startsWith('db-') ? parseInt(existingDraftKey.slice(3), 10) : NaN;

						if (Number.isFinite(existingDraftDbId) && existingDraftDbId > 0) {
							await updatePlanContent(existingDraftDbId, draftContentPayload);
							localStorage.setItem('currentDraftPlanId', `db-${existingDraftDbId}`);
						} else {
							const planData = {
								user_id: userIdNum,
								...draftContentPayload,
								garden_components:
									questionnaireData.selectedComponents && questionnaireData.selectedComponents.length > 0
										? JSON.stringify(questionnaireData.selectedComponents)
										: undefined,
								status: 'draft' as 'draft' | 'published' | 'closed' | 'archived',
								plan_type: 'professional' as 'professional' | 'diy',
								source: user?.role === 'company' ? ('invite' as const) : undefined,
								visibility: user?.role === 'company' ? ('private' as const) : undefined,
								invite_code: inviteInfo?.code || undefined
							};

							const backendPlan = await createPlan(planData);
							localStorage.setItem('currentDraftPlanId', `db-${backendPlan.id}`);
						}
						
						showAlert('Draft saved successfully!', { title: 'Draft saved' });
						commitQuestionnaireLeaveBaseline({
							form,
							contactEmail,
							aiAnswers,
							aiQuestionComments,
							hasLocalPhotoFile: !!photoFile,
							photoPreviewUrl
						});
						return;
					} catch (dbErr) {
						console.error('Failed to save draft to database:', dbErr);
						// Fall through to localStorage save as backup
					}
				}
				
				// Fallback: Save to localStorage (demo mode or database save failed)
				try {
					// Build title and city with fallbacks
					const location = questionnaireData.location || 'Location';
					const styleTitle = stylePresetsByKey[questionnaireData.styleKey]?.title || 'Garden';
					const planTitle = `${styleTitle} Garden - ${location}`;
					
					// Check if we already have a draft plan ID stored (from a previous save)
					let draftPlanId = localStorage.getItem('currentDraftPlanId');
					
					if (draftPlanId) {
						// Update existing draft plan
						const existingDraft = getPublicPlanById(draftPlanId, { includeHidden: true });
						if (existingDraft && existingDraft.status === 'draft') {
							// Don't save base64 images to localStorage
							const photoUrlForStorage = (photoDataUrl || photoPreviewUrl)?.startsWith('data:') 
								? undefined 
								: (photoDataUrl || photoPreviewUrl || existingDraft.photoBeforeUrl);
							
							updatePublicPlan(draftPlanId, {
								questionnaire: {
									...questionnaireData,
									photoBeforeUrl: photoUrlForStorage
								},
								contactEmail: contactEmail || existingDraft.contactEmail,
								photoBeforeUrl: photoUrlForStorage,
								city: location,
								title: planTitle
							});
							showAlert('Draft updated successfully!', { title: 'Draft saved' });
							commitQuestionnaireLeaveBaseline({
								form,
								contactEmail,
								aiAnswers,
								aiQuestionComments,
								hasLocalPhotoFile: !!photoFile,
								photoPreviewUrl
							});
							return;
						} else {
							// Invalid draft ID, create a new one
							localStorage.removeItem('currentDraftPlanId');
						}
					}
					
					// Create a new draft plan (without base64 images)
					draftPlanId = `pl${Math.random().toString(36).slice(2, 8)}`;
					const photoUrlForStorage = (photoDataUrl || photoPreviewUrl)?.startsWith('data:') 
						? undefined 
						: (photoDataUrl || photoPreviewUrl || undefined);
					
					const plan: PublicPlan = {
						id: draftPlanId,
						seekerUserId: 'u-seeker-demo',
						title: planTitle,
						city: location,
						contactEmail: contactEmail || '',
						styleKey: questionnaireData.styleKey,
						photoBeforeUrl: photoUrlForStorage, // Only URLs, not base64
						status: 'draft',
						createdAt: new Date().toISOString(),
						questionnaire: {
							...questionnaireData,
							photoBeforeUrl: photoUrlForStorage
						}
					};
					
					// Prune old demo drafts to make room
					pruneDemoPlans(4);
					addPublicPlan(plan);
					
					// Store the draft plan ID so we can update it on subsequent saves
					localStorage.setItem('currentDraftPlanId', draftPlanId);
					
					showAlert('Draft saved successfully!', { title: 'Draft saved' });
					commitQuestionnaireLeaveBaseline({
						form,
						contactEmail,
						aiAnswers,
						aiQuestionComments,
						hasLocalPhotoFile: !!photoFile,
						photoPreviewUrl
					});
				} catch (err) {
					console.error('Failed to create draft plan:', err);
					const quotaExceeded =
						err instanceof DOMException &&
						(err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED');
					if (quotaExceeded) {
						showAlert('Storage is full. Please try logging in to save drafts to the database, or clear your browser storage.', { title: 'Storage full' });
					} else {
						showAlert('Failed to save draft. Please try again.', { title: 'Save failed' });
					}
				}
			}
		} catch (err) {
			console.error('Error in handleSaveDraft:', err);
			showAlert('An error occurred while saving. Please try again.', { title: 'Error' });
		}
	}

	async function handleGeneratePlan(options?: { force?: boolean; autoOptimize?: boolean }) {
		void trackActivityEvent({
			event_name: 'plan_generation_start',
			properties: {
				guest_mode: guestMode,
				is_edit_flow: !!editingPlanId
			}
		});
		setIsPreparingGeneration(true);
		if (isGeneratingQuestions) {
			showAlert('Please wait until follow-up questions finish loading before generating your plan.', { title: 'Still loading' });
			setIsPreparingGeneration(false);
			return;
		}
		if (!options?.force && needsGenerationFitConfirmation) {
			setShowGenerateFitModal(true);
			setIsPreparingGeneration(false);
			return;
		}
		if (!validate()) {
			setIsPreparingGeneration(false);
			return;
		}

		const selectedComponentsForGeneration = options?.autoOptimize
			? optimizeSelectedComponentsForFit(form.selectedComponents ?? [])
			: (form.selectedComponents ?? []);
		if (options?.autoOptimize) {
			const originalCount = (form.selectedComponents ?? []).length;
			const optimizedCount = selectedComponentsForGeneration.length;
			if (optimizedCount !== originalCount) {
				showAlert(`We optimized your selection for space realism (${originalCount} -> ${optimizedCount} components).`, { title: 'Selection optimized' });
			}
		}
		const workingForm: QuestionnaireData = {
			...form,
			selectedComponents: selectedComponentsForGeneration
		};
		
		// Convert photo file to base64 data URL if present
		let photoDataUrl: string | undefined = undefined;
		if (photoFile) {
			try {
				photoDataUrl = await fileToDataURL(photoFile);
			} catch (err) {
				console.error('Failed to convert photo to data URL:', err);
				try {
					// Fallback path for memory/decoder issues on some phones.
					photoDataUrl = await uploadPlanImageFile(photoFile);
				} catch (uploadErr) {
					console.error('Fallback upload after processing failure also failed:', uploadErr);
					void trackActivityEvent({
						event_name: 'questionnaire_upload_error',
						properties: { step: currentStep, guest_mode: guestMode, context: 'pre_generation_photo_processing' }
					});
					showAlert(getPhotoProcessingErrorMessage(err), { title: 'Upload failed' });
					setIsPreparingGeneration(false);
					return;
				}
			}
		}

		// Prepare questionnaire data with photo data URL and AI analysis
		const questionnaireData: QuestionnaireData & { aiAllQuestions?: AIGeneratedQuestion[] } = {
			...workingForm,
			photoBeforeUrl: photoDataUrl || resolvedPhotoPreviewUrl || undefined,
			aiAnalysis: aiAnalysis || form.aiAnalysis || undefined,
			aiAnalysisComments: workingForm.aiAnalysisComments?.trim() || undefined,
			aiAnswers: Object.keys(aiAnswers).length > 0 ? aiAnswers : form.aiAnswers || undefined,
			aiQuestionComments: Object.keys(aiQuestionComments).length > 0 ? aiQuestionComments : form.aiQuestionComments || undefined,
			aiAllQuestions: allQuestions,
			userCorrections: workingForm.userCorrections || undefined
		};

		// If editing, update the existing plan and regenerate AI content (blocks leaving only during this work)
		if (editingPlanId) {
			setIsGeneratingPlan(true);
			setIsPreparingGeneration(false);
			try {
				const plan = getPublicPlanById(editingPlanId, { includeHidden: true });
				if (!plan) {
					showAlert('Plan not found. Please try again.', { title: 'Error' });
					localStorage.removeItem('editingPlanId');
					navigate('/app/seeker/plans?tab=drafts');
					return;
				}

				const aiContent = await generateAIPlan(questionnaireData);
				const photoBeforeUrl = photoDataUrl || resolvedPhotoPreviewUrl || normalizeStoredPhotoUrl(plan.photoBeforeUrl) || undefined;
				const isPlaceholderImage = aiContent.photoAfterUrl?.includes('unsplash.com');
				const photoAfterUrl = (isPlaceholderImage ? undefined : aiContent.photoAfterUrl) || photoBeforeUrl;
				const nextTitle = `${stylePresetsByKey[questionnaireData.styleKey]?.title} Garden - ${questionnaireData.location}`;
				const nextAiGenerated = {
					...aiContent,
					photoAfterUrl: photoAfterUrl || ''
				};

				// Persist to DB when this is a database-backed plan; otherwise fall back to local storage.
				if (plan.databaseId) {
					await updatePlanContent(plan.databaseId, {
						title: nextTitle,
						description: questionnaireData.needsWants || undefined,
						style: questionnaireData.styleKey,
						location: questionnaireData.location || undefined,
						garden_size: questionnaireData.gardenSize || undefined,
						garden_shape: questionnaireData.gardenShape || undefined,
						garden_components: questionnaireData.selectedComponents && questionnaireData.selectedComponents.length > 0
							? questionnaireData.selectedComponents
							: undefined,
						needs_wants: questionnaireData.needsWants || undefined,
						maintenance_level: questionnaireData.maintenanceLevel || undefined,
						children_pets: questionnaireData.childrenPets || undefined,
						budget_min: questionnaireData.budgetRange?.min || undefined,
						budget_max: questionnaireData.budgetRange?.max || undefined,
						timeline: questionnaireData.timeline || undefined,
						project_start_date: questionnaireData.projectStartDate || undefined,
						contact_email: contactEmail || undefined,
						household_size: questionnaireData.householdSize ?? undefined,
						garden_users: questionnaireData.gardenUsers ?? undefined,
						project_focus: questionnaireData.projectFocus ?? undefined,
						garden_uses: questionnaireData.gardenUses?.length ? questionnaireData.gardenUses : undefined,
						hosting_max_people: questionnaireData.hostingMaxPeople ?? undefined,
						kids_count: questionnaireData.kidsCount ?? undefined,
						kids_ages: questionnaireData.kidsAges ?? undefined,
						pets_description: questionnaireData.petsDescription ?? undefined,
						plant_density_preference: questionnaireData.plantDensityPreference || undefined,
						before_image_url: photoBeforeUrl || undefined,
						after_image_url: nextAiGenerated.photoAfterUrl || undefined,
						ai_plan_details: { aiGenerated: nextAiGenerated },
						ai_questions: (((questionnaireData as any).aiAllQuestions ?? questionnaireData.aiAnalysis?.suggestedQuestions) as any) || undefined,
						ai_answers: {
							...(questionnaireData.aiAnswers || {}),
							...(questionnaireData.aiQuestionComments && Object.keys(questionnaireData.aiQuestionComments).length > 0
								? Object.fromEntries(
										Object.entries(questionnaireData.aiQuestionComments).map(([k, v]) => [`ai_q_${k}_comment`, v])
									)
								: {})
						},
						ai_analysis: questionnaireData.aiAnalysis || undefined,
						append_previous_revision: true
					});

					// Refresh local cache with latest DB state.
					await getPublicPlanByIdFromAPI(`db-${plan.databaseId}`);
				} else {
					updatePublicPlan(editingPlanId, {
						questionnaire: questionnaireData,
						contactEmail,
						photoBeforeUrl: photoBeforeUrl,
						aiGenerated: nextAiGenerated,
						city: questionnaireData.location,
						title: nextTitle
					});
				}

				localStorage.removeItem('editingPlanId');
				localStorage.removeItem('gardenQuestionnaireDraft');
				localStorage.removeItem('currentDraftPlanId');

				// Prevent blocker prompt during successful post-generation redirect.
				suppressLeaveGuardRef.current = true;
				const targetId = plan.databaseId ? `db-${plan.databaseId}` : editingPlanId;
				setPendingSuccessRedirect(`/app/seeker/plan/${targetId}`);
				return;
			} catch (err) {
				console.error('Failed to update plan', err);
				showAlert('Failed to update plan. Please try again.', { title: 'Update failed' });
				return;
			} finally {
				setIsGeneratingPlan(false);
			}
		}

		// New plans: navigate to generation page (must not set isGeneratingPlan before navigate — useBlocker would block the same navigation)
		// Keep currentDraftPlanId so AI generation updates the same row instead of creating another direct plan.
		const draftPlanDatabaseId = (() => {
			const key = localStorage.getItem('currentDraftPlanId');
			if (!key?.startsWith('db-')) return undefined;
			const id = parseInt(key.slice(3), 10);
			return Number.isFinite(id) && id > 0 ? id : undefined;
		})();
		
		// Upload photo first if it's a data URL, so we have a URL instead of base64
		// This way we can save it to localStorage without quota issues
		let photoUrlForStorage = questionnaireData.photoBeforeUrl;
		if (photoDataUrl && photoDataUrl.startsWith('data:')) {
			try {
				// Convert data URL to File and upload
				const response = await fetch(photoDataUrl);
				const blob = await response.blob();
				const file = new File([blob], 'photo.jpg', { type: blob.type });
				
				const formData = new FormData();
				formData.append('image', file);
				formData.append('type', 'plan');
				formData.append('subtype', 'before');
				
				const uploadResponse = await fetch(buildApiUrl('/upload'), {
					method: 'POST',
					body: formData
				});
				
								if (uploadResponse.ok) {
									const uploadData = await uploadResponse.json();
									if (uploadData.success && uploadData.data?.url) {
										// Store the relative URL (not absolute) - it will be converted when displaying
										const relativeUrl = uploadData.data.url;
										photoUrlForStorage = relativeUrl; // Store relative URL, not absolute
					}
				}
			} catch (uploadErr) {
				console.error('Failed to upload photo before generation:', uploadErr);
				// Continue with data URL - will be handled in AIGeneratedPlanView
			}
		}
		
		// Save questionnaire with uploaded URL (or original if not a data URL)
		// Also persist the full combined smart-question list (AI + component questions).
		// This is required so plan/project views can show the question text for every answered `ai_q_{idx}` key.
		const questionnaireForStorage: any = {
			...questionnaireData,
			photoBeforeUrl: photoUrlForStorage, // Use uploaded URL if available, otherwise original
			aiAllQuestions: allQuestions
		};

		if (guestMode) {
			void trackActivityEvent({
				event_name: 'questionnaire_submit',
				properties: { guest_mode: true }
			});
			guestTeaserDoneRef.current = false;
			try {
				sessionStorage.setItem(
					GUEST_PENDING_QUESTIONNAIRE_SESSION_KEY,
					JSON.stringify({
						questionnaire: questionnaireForStorage,
						contactEmail,
						inviteCode: inviteInfo?.code?.trim() || undefined
					})
				);
			} catch (e) {
				console.error('Failed to store guest questionnaire', e);
				showAlert('Could not save your answers for sign-in. Try again or use a smaller photo.', {
					title: 'Storage error'
				});
				setIsPreparingGeneration(false);
				return;
			}
			setIsPreparingGeneration(false);
			setGuestAuthGate('teaser');
			return;
		}

		const pendingHandoff = {
			questionnaire: questionnaireForStorage,
			contactEmail
		};
		let localStorageSaved = false;
		let storageErr: unknown = null;
		try {
			const { clearGeneratePlanReview } = await import('@utils/generatePlanReview');
			clearGeneratePlanReview();
			localStorage.removeItem('pendingQuestionnaire');
			localStorage.setItem('pendingQuestionnaire', JSON.stringify(pendingHandoff));
			localStorageSaved = true;
		} catch (err) {
			storageErr = err;
			console.error('Failed to save pendingQuestionnaire to localStorage:', err);
		}
		const { logQuestionnaireHandoff } = await import('@services/planFlowDiagnostic');
		logQuestionnaireHandoff({
			localStorageSaved,
			storageError: storageErr ?? undefined,
			usedNavigationState: true,
			questionnaireSummary: {
				style_key: questionnaireForStorage.styleKey,
				location_len: String(questionnaireForStorage.location ?? '').length,
				photo_before_len: String(questionnaireForStorage.photoBeforeUrl ?? '').length,
				photo_is_data_url: String(questionnaireForStorage.photoBeforeUrl ?? '').startsWith('data:'),
				component_count: Array.isArray(questionnaireForStorage.selectedComponents)
					? questionnaireForStorage.selectedComponents.length
					: 0
			}
		});
		void trackActivityEvent({
			event_name: 'questionnaire_submit',
			properties: {
				guest_mode: false,
				local_storage_saved: localStorageSaved,
				handoff_via_navigation_state: true
			}
		});
		navigate('/app/seeker/generate-plan', {
			state: {
				pendingQuestionnaire: pendingHandoff,
				...(inviteInfo?.code ? { inviteCode: inviteInfo.code } : {}),
				...(draftPlanDatabaseId != null ? { draftPlanDatabaseId } : {})
			}
		});
	}

	if (isGeneratingPlan) {
		return (
			<div className={`questionnaire-page-root ${guestMode ? 'questionnaire-try-mode' : ''} ${isQuestionnaireInteractionLocked ? 'questionnaire-page-root--interaction-locked' : ''}`}>
				<div ref={questionnaireScrollAreaRef} className="questionnaire-scroll-area">
					<div className="d-flex flex-wrap align-items-center gap-3 mb-3">
						<h1 className="h3 mb-0">{editingPlanId ? copy('Updating Garden Plan', 'Gartenplan wird aktualisiert', 'Ažuriranje vrtnog plana') : copy('Generating Garden Plan', 'Gartenplan wird erstellt', 'Generiranje vrtnog plana')}</h1>
					</div>
					<div className="card">
						<div className="card-body py-5" style={{ minHeight: '400px' }}>
							<PlanGenerationPuzzleAnimation
								beforeImageUrl={generationAnimationBackgroundUrl}
								selectedComponents={form.selectedComponents}
								subMessage={copy("We're analysing your updates and regenerating your personalised plan. This usually takes about 1-3 minutes.", 'Wir analysieren Ihre Änderungen und erstellen Ihren personalisierten Plan neu. Dies dauert normalerweise 1-3 Minuten.', 'Analiziramo vaše izmjene i ponovno generiramo vaš personalizirani plan. To obično traje 1-3 minute.')}
							/>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={`questionnaire-page-root ${guestMode ? 'questionnaire-try-mode' : ''} ${isQuestionnaireInteractionLocked ? 'questionnaire-page-root--interaction-locked' : ''}`}>
			<div ref={questionnaireScrollAreaRef} className="questionnaire-scroll-area">
				<div className="d-flex flex-wrap align-items-start gap-3 mb-3">
					<div className={guestMode && !editingPlanId && currentStep === 1 ? 'questionnaire-try-intro flex-grow-1' : 'flex-grow-1'}>
						<h1 className="h3 mb-1">
							{editingPlanId
								? copy('Edit Garden Plan', 'Gartenplan bearbeiten', 'Uredi vrtni plan')
								: guestMode
									? currentStep === 1
										? copy('Try the garden questionnaire', 'Gartenfragebogen ausprobieren', 'Isprobajte vrtni upitnik')
										: copy('Garden questionnaire', 'Gartenfragebogen', 'Vrtni upitnik')
									: copy('🌿 Create Your Garden Plan', '🌿 Erstellen Sie Ihren Gartenplan', '🌿 Kreirajte svoj vrtni plan')}
						</h1>
						{!editingPlanId && (!guestMode || currentStep === 1) && (
							<p className="text-muted mb-0">
								{guestMode
									? copy('Walk through the full questionnaire with no account. When you are ready, sign in to save your garden plan and visuals.', 'Gehen Sie den gesamten Fragebogen ohne Konto durch. Wenn Sie bereit sind, melden Sie sich an, um Ihren Plan zu speichern.', 'Prođite kroz cijeli upitnik bez računa. Kada budete spremni, prijavite se za spremanje plana i vizuala.')
									: copy('Get a plan tailored to your space in a few steps.', 'Erhalten Sie in wenigen Schritten einen Plan für Ihren Raum.', 'U nekoliko koraka dobijte plan prilagođen vašem prostoru.')}
							</p>
						)}
					</div>
					{!guestMode && !editingPlanId && !inviteInfo && !inviteCodeFromUrl && (
						<div className="questionnaire-invite-compact ms-auto">
							<div className="d-flex flex-wrap align-items-center justify-content-end gap-2">
								<button
									type="button"
									className="btn btn-sm btn-outline-secondary"
									onClick={() => setShowInviteCodeInput(prev => !prev)}
									aria-expanded={shouldShowExpandedInviteInput}
								>
									<i className="bi bi-building me-1" aria-hidden />
									{copy('Have a company code?', 'Haben Sie einen Unternehmenscode?', 'Imate kod tvrtke?')}
								</button>
							</div>
							{shouldShowExpandedInviteInput && (
								<div className="questionnaire-invite-expanded mt-2">
									<div className="d-flex flex-wrap align-items-center gap-2">
										<input
											type="text"
											className="form-control form-control-sm"
											placeholder={copy('Enter code (e.g. ABC123)', 'Code eingeben (z. B. ABC123)', 'Unesite kod (npr. ABC123)')}
											style={{ maxWidth: '220px' }}
											value={manualInviteCode}
											onChange={e => {
												setManualInviteCode(e.target.value.toUpperCase());
												setInviteInputError(null);
											}}
										/>
										<button
											type="button"
											className="btn btn-sm btn-outline-success"
											disabled={!manualInviteCode.trim() || isCheckingInvite}
											onClick={() => {
												if (!manualInviteCode.trim()) return;
												setInviteInputError(null);
											}}
										>
											{isCheckingInvite ? (
												<>
													<span
														className="spinner-border spinner-border-sm me-1"
														role="status"
														aria-hidden="true"
													/>
													{copy('Checking...', 'Prüfen...', 'Provjera...')}
												</>
											) : (
												<>
													<i className="bi bi-check2-circle me-1" />
													{copy('Apply code', 'Code anwenden', 'Primijeni kod')}
												</>
											)}
										</button>
									</div>
									<div className="text-muted small mt-1">
										{copy('If a gardener or company sent you a code or link, enter it to send your plan directly to them.', 'Wenn Ihnen ein Gärtner oder ein Unternehmen einen Code oder Link gesendet hat, geben Sie ihn hier ein, um Ihren Plan direkt dorthin zu senden.', 'Ako vam je vrtlar ili tvrtka poslala kod ili poveznicu, unesite ga kako biste plan poslali izravno njima.')}
									</div>
									{inviteInputError && (
										<div className="text-danger small mt-1">{inviteInputError}</div>
									)}
								</div>
							)}
						</div>
					)}
				</div>
				{inviteInfo && (
					<div className="alert alert-success mb-3" role="status">
						<strong>{copy("You're creating a plan for", 'Sie erstellen einen Plan für', 'Kreirate plan za')} {inviteInfo.companyName}.</strong>{' '}
						{copy('This plan will be sent directly to this company and will not appear in the public marketplace.', 'Dieser Plan wird direkt an dieses Unternehmen gesendet und erscheint nicht im öffentlichen Marktplatz.', 'Ovaj plan će biti poslan izravno ovoj tvrtki i neće se pojaviti na javnom tržištu.')}
					</div>
				)}
				{inviteCodeFromUrl && !inviteInfo && !inviteInputError && isCheckingInvite && (
					<div className="alert alert-light border mb-3 d-flex align-items-center gap-2" role="status">
						<span
							className="spinner-border spinner-border-sm text-success"
							role="status"
							aria-hidden="true"
						/>
						<span>{copy('Verifying your invite…', 'Einladung wird geprüft…', 'Provjera pozivnice…')}</span>
					</div>
				)}
				{inviteCodeFromUrl && !inviteInfo && inviteInputError && (
					<div className="alert alert-danger mb-3" role="alert">
						{inviteInputError}
					</div>
				)}
				{editingPlanId && (
					<p className="text-muted mb-4">
						{copy('Update your questionnaire to regenerate your garden plan.', 'Aktualisieren Sie den Fragebogen, um Ihren Gartenplan neu zu erstellen.', 'Ažurirajte upitnik za ponovno generiranje vrtnog plana.')}
					</p>
				)}

				<div
					className={`questionnaire-floating-topbar d-none d-lg-flex ${questionnaireFullscreenModalOpen ? 'questionnaire-floating-topbar--under-modal' : ''}`}
					aria-label="Questionnaire progress and estimate"
				>
					<div className="questionnaire-floating-progress">
						<div className="step step-primary">
							{steps.map((step) => (
								<div
									key={step.id}
									className={`step-item ${currentStep === step.id && !questionnaireFullscreenModalOpen ? 'active' : ''} ${currentStep > step.id ? 'completed' : ''}`}
								>
									<div className="step-item-link">
										<span className="step-number">{step.id}</span>
										<div className="step-content d-none d-md-block">
											<div className="step-title">{step.title}</div>
											{currentStep === 1 || currentStep === step.id ? (
												<div className="step-description small text-muted">{step.description}</div>
											) : null}
											<span className="step-done-label">Done</span>
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
					{currentStep >= 4 && (
						<div className="questionnaire-inline-budget questionnaire-inline-budget-floating" aria-live="polite">
							<div className="questionnaire-inline-budget-label">{copy('Estimated cost', 'Geschätzte Kosten', 'Procijenjeni trošak')}</div>
							{topBarCostEstimate ? (
								<>
									<div className="questionnaire-inline-budget-range">
										€{Math.round(topBarCostEstimate.min).toLocaleString()} - €{Math.round(topBarCostEstimate.max).toLocaleString()}
									</div>
									<div className="questionnaire-inline-budget-mid">
										~€{Math.round(topBarCostEstimate.mid).toLocaleString()} {copy('mid-range', 'Mittelwert', 'srednji raspon')}
									</div>
								</>
							) : (
								<div className="questionnaire-inline-budget-placeholder">
									{copy('Add size and components to see estimate', 'Fügen Sie Größe und Komponenten hinzu, um eine Schätzung zu sehen', 'Dodajte veličinu i komponente za procjenu')}
								</div>
							)}
						</div>
					)}
				</div>

				<div className="questionnaire-top-utility d-lg-none mb-3">
					<div className="questionnaire-inline-progress" aria-label={`${copy('Step', 'Schritt', 'Korak')} ${currentStep} ${copy('of', 'von', 'od')} ${steps.length}`}>
						<div className="questionnaire-inline-progress-head">
							<div className="questionnaire-inline-progress-label">
								{copy('Step', 'Schritt', 'Korak')} {currentStep} {copy('of', 'von', 'od')} {steps.length}
							</div>
							<div className="questionnaire-inline-progress-current">{steps[currentStep - 1].title}</div>
						</div>
						<div className="questionnaire-inline-progress-track" aria-hidden>
							<div
								className="questionnaire-inline-progress-fill"
								style={{ width: `${Math.max(0, Math.min(100, Math.round((currentStep / steps.length) * 100)))}%` }}
							/>
						</div>
						<div className="questionnaire-inline-progress-steps">
							{steps.map((step) => (
								<div
									key={step.id}
									className={`questionnaire-inline-step ${currentStep === step.id && !questionnaireFullscreenModalOpen ? 'active' : ''} ${currentStep > step.id ? 'done' : ''}`}
								>
									<span className="questionnaire-inline-step-dot" aria-hidden>{currentStep > step.id ? '✓' : step.id}</span>
									<div className="questionnaire-inline-step-title">{step.shortTitle}</div>
								</div>
							))}
						</div>
					</div>
				</div>

			<div className="card questionnaire-main-card">
				{/* Step-specific header inside card (icon + title, no number) */}
				<div className="questionnaire-step-header">
					<h5 className="mb-0">
						<i className={`bi ${steps[currentStep - 1].icon} me-2`} aria-hidden />
						{steps[currentStep - 1].title}
					</h5>
				</div>
				<div className="card-body questionnaire-card-body">
					<form className="row g-4 questionnaire-form">
						{/* Step 1: Photo only — no questions yet; image-based hints only */}
						{currentStep === 1 && (
							<>
								<div className="col-12 mb-4">
									<p className="text-muted mb-2">
										{copy('Upload a photo of your garden and we will read what is visible: rough size, main features, and follow-up questions that match your space.', 'Laden Sie ein Foto Ihres Gartens hoch. Wir erkennen sichtbare Dinge wie ungefähre Größe, Hauptmerkmale und passende Folgefragen.', 'Učitajte fotografiju vrta, a mi ćemo očitati što je vidljivo: približnu veličinu, glavne značajke i dodatna pitanja prilagođena vašem prostoru.')}
									</p>
									<div className="text-muted small">
										<p className="mb-2">
											<strong className="text-body">{copy('Photo tips:', 'Fototipps:', 'Savjeti za fotografiju:')}</strong> {copy('Use a clear, non-blurry image; good lighting works best. Include the full area you want help with so we can give accurate suggestions.', 'Verwenden Sie ein klares, nicht verschwommenes Bild; gutes Licht hilft. Erfassen Sie den gesamten Bereich für genauere Vorschläge.', 'Koristite jasnu, ne-zamućenu sliku; najbolje je dobro osvjetljenje. Uključite cijelo područje za točnije prijedloge.')}
										</p>
										<button
											type="button"
											className="btn btn-outline-success btn-sm"
											onClick={() => {
												setExamplePhotoIndex(0);
												setShowPhotoExamplesModal(true);
											}}
											aria-haspopup="dialog"
										>
											<i className="bi bi-images me-1" aria-hidden />
											{copy('See example photos', 'Beispielfotos ansehen', 'Pogledaj primjere fotografija')}
										</button>
									</div>
								</div>
								<div className="col-12">
									<div className="questionnaire-mobile-photo-row">
										<label className="form-label questionnaire-question-label mb-0">
											{copy('Photo of Your Garden', 'Foto Ihres Gartens', 'Fotografija vašeg vrta')}
											<HelpTooltip text="Upload a photo and we will read visible features and rough size, then ask follow-up questions that fit what we see." />
										</label>
										<button
											type="button"
											className="btn btn-outline-success btn-sm d-lg-none questionnaire-mobile-camera-btn"
											onClick={() => cameraInputRef.current?.click()}
											disabled={isAnalyzing || isPreparingPhoto}
										>
											<i className="bi bi-camera me-2" aria-hidden />
											{copy('Take photo', 'Foto aufnehmen', 'Snimi fotografiju')}
										</button>
									</div>
									<input
										ref={photoInputRef}
										type="file"
										className="d-none"
										accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
										onChange={(e) => {
											const file = e.target.files?.[0] ?? null;
											void handleSelectedPhoto(file);
											e.target.value = '';
										}}
										disabled={isAnalyzing || isPreparingPhoto}
										aria-invalid={!!errors.photoFile}
									/>
									<input
										ref={cameraInputRef}
										type="file"
										className="d-none"
										accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
										capture="environment"
										onChange={(e) => {
											const file = e.target.files?.[0] ?? null;
											void handleSelectedPhoto(file);
											e.target.value = '';
										}}
										disabled={isAnalyzing || isPreparingPhoto}
										aria-invalid={!!errors.photoFile}
									/>
									{!photoPreviewUrl && !isAnalyzing && !isPreparingPhoto ? (
										<div
											className={`questionnaire-upload-zone ${uploadZoneDrag ? 'drag-over' : ''} ${errors.photoFile ? 'border-danger' : ''}`}
											onClick={() => photoInputRef.current?.click()}
											onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setUploadZoneDrag(true); }}
											onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setUploadZoneDrag(false); }}
											onDrop={(e) => {
												e.preventDefault();
												setUploadZoneDrag(false);
												const file = e.dataTransfer.files?.[0];
												void handleSelectedPhoto(file ?? null);
											}}
											role="button"
											tabIndex={0}
											onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); photoInputRef.current?.click(); } }}
											aria-label={copy('Upload garden photo', 'Gartenfoto hochladen', 'Učitaj fotografiju vrta')}
										>
											<i className="bi bi-cloud-arrow-up upload-icon" aria-hidden />
											<div className="upload-text">{copy('Drop your garden photo here', 'Legen Sie Ihr Gartenfoto hier ab', 'Povucite fotografiju vrta ovdje')}</div>
											<div className="upload-hint">{copy('or click to browse · Large photos are auto-optimized (up to 40MB input)', 'oder klicken zum Hochladen · Große Fotos werden automatisch optimiert (bis 40MB)', 'ili kliknite za odabir · Velike fotografije se automatski optimiziraju (do 40MB)')}</div>
										</div>
									) : null}
									{errors.photoFile && <div className="invalid-feedback d-block mt-1">{errors.photoFile}</div>}
									{!photoPreviewUrl && !isAnalyzing && !isPreparingPhoto && !errors.photoFile && (
										<div className="form-text questionnaire-helper-text mt-1">{copy('Large phone photos are automatically optimized to fit upload limits.', 'Große Handyfotos werden automatisch optimiert, um Upload-Grenzen einzuhalten.', 'Velike fotografije s mobitela automatski se optimiziraju za ograničenja prijenosa.')}</div>
									)}
									{(isAnalyzing || isPreparingPhoto) && (
										<div className="mt-3">
											<MagnifierAnimation 
												message={isPreparingPhoto
													? copy('Optimizing your photo...', 'Ihr Foto wird optimiert...', 'Optimiziramo vašu fotografiju...')
													: copy('Reading your garden photo...', 'Ihr Gartenfoto wird analysiert...', 'Analiziramo fotografiju vašeg vrta...')}
												subMessage={isPreparingPhoto
													? copy('Reducing file size for faster upload and generation', 'Dateigröße wird für schnelleren Upload und schnellere Generierung reduziert', 'Smanjujemo veličinu datoteke za brži prijenos i generiranje')
													: copy('Spotting visible features, rough size, and what to ask next', 'Wir erkennen sichtbare Merkmale, eine ungefähre Größe und passende Folgefragen', 'Prepoznajemo vidljive značajke, približnu veličinu i pitanja koja slijede')}
												imageUrl={resolvedPhotoPreviewUrl || undefined}
											/>
										</div>
									)}
									{photoPreviewUrl && !isAnalyzing && !isPreparingPhoto && (
										<div className="mt-3 d-flex flex-column align-items-start">
											<img
												src={resolvedPhotoPreviewUrl || undefined}
												alt={copy('Current garden', 'Aktueller Garten', 'Trenutačni vrt')}
												className="img-thumbnail"
												style={{ maxHeight: '300px', width: 'auto', cursor: 'pointer' }}
												onClick={() => photoInputRef.current?.click()}
												role="button"
												tabIndex={0}
												onKeyDown={(e) => {
													if (e.key === 'Enter' || e.key === ' ') {
														e.preventDefault();
														photoInputRef.current?.click();
													}
												}}
												aria-label={copy('Change garden photo', 'Gartenfoto ändern', 'Promijeni fotografiju vrta')}
											/>
											<button type="button" className="btn btn-outline-secondary btn-sm mt-2" onClick={() => photoInputRef.current?.click()}>
												<i className="bi bi-arrow-repeat me-1" aria-hidden /> {copy('Change photo', 'Foto ändern', 'Promijeni fotografiju')}
											</button>
										</div>
									)}
									
									{/* Analysis results */}
									{aiAnalysis && !isAnalyzing && (
										<div className="mt-3">
											{aiAnalysis.isGarden === false ? (
												<div className="alert alert-warning mb-0">
													<h6 className="alert-heading">
														<i className="bi bi-exclamation-triangle me-2"></i>
														{copy("This doesn't look like a garden photo", 'Das sieht nicht wie ein Gartenfoto aus', 'Ovo ne izgleda kao fotografija vrta')}
													</h6>
													<p className="mb-0">
														{displayedGardenRecognitionNote?.trim() || copy("We couldn't recognize a garden or outdoor space in this photo. For the best plan, please upload a photo of your garden, yard, terrace, or balcony.", 'Wir konnten auf diesem Foto keinen Garten oder Außenbereich erkennen. Für den besten Plan laden Sie bitte ein Foto Ihres Gartens, Hofs, Ihrer Terrasse oder Ihres Balkons hoch.', 'Na ovoj fotografiji nismo prepoznali vrt ili vanjski prostor. Za najbolji plan učitajte fotografiju svog vrta, dvorišta, terase ili balkona.')}
													</p>
													<p className="mb-0 mt-2 small">
														{copy('You can continue anyway, try another photo, or report that this is a garden for review. Our team will look into it.', 'Sie können trotzdem fortfahren, ein anderes Foto versuchen oder melden, dass es sich um einen Garten handelt. Unser Team prüft das.', 'Možete svejedno nastaviti, pokušati s drugom fotografijom ili prijaviti da je ovo vrt kako bismo to provjerili. Naš tim će to pregledati.')}
													</p>
													<div className="mt-3 d-flex flex-wrap gap-2">
														<button
															type="button"
															className="btn btn-outline-primary btn-sm"
															onClick={() => setShowReportModal(true)}
														>
															<i className="bi bi-flag me-1" aria-hidden />
															{copy('Report as garden', 'Als Garten melden', 'Prijavi kao vrt')}
														</button>
													</div>
												</div>
											) : (
												<div className="alert alert-success">
													<h6 className="alert-heading">
														<i className="bi bi-magic me-2"></i>
														{copy('Analysis complete', 'Analyse abgeschlossen', 'Analiza je završena')}
													</h6>
													{aiAnalysis.detectedSize && aiAnalysis.detectedSize.confidence > 0.3 && (
														<div className="mb-2">
															<strong>{copy('Estimated Size:', 'Geschätzte Größe:', 'Procijenjena veličina:')}</strong> ~{Math.round(aiAnalysis.detectedSize.value)} m²
														</div>
													)}
													{aiAnalysis.spaceType && aiAnalysis.spaceType !== 'unknown' && (
														<div className="mb-2">
															<strong>{copy('Space Type:', 'Art des Bereichs:', 'Vrsta prostora:')}</strong>{' '}
															{formatAnalysisValue(String(aiAnalysis.spaceType))}
														</div>
													)}
													{displayedDetectedFeatures.length > 0 && (
														<div className="mb-2">
															<strong>{copy('Detected Features:', 'Erkannte Merkmale:', 'Prepoznate značajke:')}</strong>
															<ul className="mb-0 mt-1">
																{displayedDetectedFeatures
																	.filter(f => f.confidence > 0.5)
																	.map((feature, idx) => (
																		<li key={idx}>
																			{formatAnalysisText(feature.name)}
																			{feature.position && ` (${formatAnalysisText(feature.position)})`}
																		</li>
																	))}
															</ul>
														</div>
													)}
													{displayedLayoutComplexityWarning && (
														<div className="mb-2">
															<strong>{copy('Layout note:', 'Hinweis zum Layout:', 'Napomena o rasporedu:')}</strong> {formatAnalysisText(displayedLayoutComplexityWarning)}
														</div>
													)}
													{aiAnalysis.constraints && (aiAnalysis.constraints.slope || aiAnalysis.constraints.lighting || aiAnalysis.constraints.drainage) && (
														<div className="mb-0">
															<strong>{copy('Constraints:', 'Einschränkungen:', 'Ograničenja:')}</strong>
															<ul className="mb-0 mt-1">
																{aiAnalysis.constraints.slope && (
																	<li>
																		{copy('Slope:', 'Gefälle:', 'Nagib:')}{' '}
																		{formatAnalysisValue(String(aiAnalysis.constraints.slope))}
																	</li>
																)}
																{aiAnalysis.constraints.lighting && (
																	<li>
																		{copy('Lighting:', 'Licht:', 'Svjetlo:')}{' '}
																		{formatAnalysisValue(String(aiAnalysis.constraints.lighting))}
																	</li>
																)}
																{aiAnalysis.constraints.drainage && (
																	<li>
																		{copy('Drainage:', 'Drainage:', 'Drenaža:')}{' '}
																		{formatAnalysisValue(String(aiAnalysis.constraints.drainage))}
																	</li>
																)}
															</ul>
														</div>
													)}
												</div>
											)}
											{/* Comment / correct the analysis */}
											<div className="mt-3">
												<label className="form-label small mb-1">
													<i className="bi bi-chat-square-text me-1" aria-hidden></i>
													{copy('Correct the analysis (optional)', 'Analyse korrigieren (optional)', 'Ispravite analizu (opcionalno)')}
												</label>
												<textarea
													className="form-control form-control-sm"
													rows={3}
													placeholder={copy("e.g. There's no fence in my garden - There is slope at the back - Size is about 80m2 - Lighting is partial sun", 'z. B. In meinem Garten gibt es keinen Zaun - Hinten gibt es ein Gefälle - Die Größe beträgt etwa 80 m2 - Das Licht ist halbschattig', 'npr. U mom vrtu nema ograde - Straga postoji nagib - Veličina je oko 80 m2 - Osvjetljenje je djelomično sunčano')}
													value={form.aiAnalysisComments ?? ''}
													onChange={(e) => setForm({ ...form, aiAnalysisComments: e.target.value })}
													aria-label={copy('Your corrections or comments on the analysis', 'Ihre Korrekturen oder Kommentare zur Analyse', 'Vaši ispravci ili komentari na analizu')}
												/>
												<div className="form-text small">
													{copy('If something was detected incorrectly (wrong features, size, slope, lighting, etc.), add a note here so we can use it when generating your plan.', 'Wenn etwas falsch erkannt wurde (Merkmale, Größe, Gefälle, Licht usw.), fügen Sie hier eine Notiz hinzu, damit wir sie bei der Erstellung Ihres Plans berücksichtigen können.', 'Ako je nešto pogrešno prepoznato (značajke, veličina, nagib, svjetlo itd.), dodajte ovdje napomenu kako bismo to uzeli u obzir pri izradi plana.')}
												</div>
											</div>
										</div>
									)}
								</div>
							</>
						)}

						{/* Step 2: Preferred style only */}
						{currentStep === 2 && (
							<>
								<div className="col-12">
									<p className="text-muted mb-4">
										{copy(
											'Pick the look and feel for your garden. We will use this for plants, materials, and layout — you can refine features on the next steps.',
											'Wählen Sie Look und Stil für Ihren Garten. Wir nutzen dies für Pflanzen, Materialien und Layout — Details folgen in den nächsten Schritten.',
											'Odaberite izgled i dojam vašeg vrta. Koristit ćemo to za biljke, materijale i raspored — značajke birate u sljedećim koracima.'
										)}
									</p>
									{errors.styleKey && (
										<div className="alert alert-danger py-2 mb-0" role="alert">
											{errors.styleKey}
										</div>
									)}
								</div>

								{/* Preferred Style with AI + hardcoded recommendations */}
								<div className="col-12 questionnaire-section-card" ref={preferredStyleSectionRef}>
									<h6 className="section-title">{copy('Preferred Style', 'Bevorzugter Stil', 'Preferirani stil')}</h6>
									<label className="form-label questionnaire-question-label">
										{copy('Choose a style', 'Wählen Sie einen Stil', 'Odaberite stil')} <span className="text-danger">*</span>
										<HelpTooltip text="The style sets the overall aesthetic and guides plant selection, materials, and layout choices for your garden." />
									</label>
									<div className="row g-3">
										{stylePresets.map((style) => (
											<div className="col-12 col-md-6 col-lg-4" key={style.id}>
												<div
													className={`card h-100 ${form.styleKey === style.key ? 'border-success border-2 shadow-sm' : ''}`}
													onClick={() => setForm({ ...form, styleKey: style.key })}
													style={{ cursor: 'pointer', transition: 'all 0.2s' }}
												>
													{style.imageUrl && (
														<img 
															src={style.imageUrl} 
															alt={localizeStyleTitle(style.key, style.title)}
															className="card-img-top"
															style={{ height: '150px', objectFit: 'cover' }}
															onError={(e) => {
																e.currentTarget.style.display = 'none';
															}}
														/>
													)}
													<div className="card-body">
														<div className="d-flex align-items-center justify-content-between mb-1">
															<div className="fw-bold">{localizeStyleTitle(style.key, style.title)}</div>
															{recommendedStyleKeys.includes(style.key) && (
																<span className="badge bg-primary" style={{ fontSize: '0.7rem' }}>
																	<i className="bi bi-star-fill me-1" aria-hidden />
																	{copy('Recommended', 'Empfohlen', 'Preporučeno')}
																</span>
															)}
														</div>
														<div className="text-muted small">{localizeStyleDescription(style.key, style.description)}</div>
													</div>
												</div>
											</div>
										))}
									</div>
									{/* AI + hardcoded recommendations for selected style */}
									{form.styleKey && (
										<div className="mt-3 alert alert-light border">
											<h6 className="alert-heading mb-2 d-flex align-items-center gap-2">
												<i className="bi bi-lightbulb me-1 text-warning" aria-hidden />
												<span>
													{recommendedStyleKeys.length > 0 && recommendedStyleKeys.includes(form.styleKey) ? (
														<>
															{copy('Based on your photo, we recommend', 'Basierend auf Ihrem Foto empfehlen wir', 'Na temelju vaše fotografije preporučujemo')}{' '}
															<strong>{localizeStyleTitle(form.styleKey, stylePresetsByKey[form.styleKey]?.title)}</strong>
														</>
													) : (
														<>{copy('Recommendations for', 'Empfehlungen für', 'Preporuke za')} {localizeStyleTitle(form.styleKey, stylePresetsByKey[form.styleKey]?.title)}</>
													)}
												</span>
											</h6>
											<p className="mb-0 small">{translatedStyleRecommendationText || localizeStyleRecommendationText(rawStyleRecommendationText)}</p>
											<p className="mb-0 mt-2 small text-muted">
												<em>
													{recommendedStyleKeys.length > 0
														? copy(
															'These are suggestions based on your garden photo — you can still pick any style you like.',
															'Dies sind Vorschläge basierend auf Ihrem Gartenfoto — Sie können weiterhin jeden Stil wählen, den Sie möchten.',
															'Ovo su prijedlozi na temelju fotografije vašeg vrta — i dalje možete odabrati bilo koji stil koji želite.'
														)
														: copy(
															'These are suggestions — feel free to choose what you prefer.',
															'Dies sind Vorschläge — wählen Sie frei, was Ihnen am meisten entspricht.',
															'Ovo su prijedlozi — slobodno odaberite ono što vam najviše odgovara.'
														)}
												</em>
											</p>
										</div>
									)}
								</div>
							</>
						)}

						{/* Step 3: Size, location, garden use, and vision text */}
						{currentStep === 3 && (
							<>
								<div className="col-12">
									<p className="text-muted mb-4">
										{copy(
											'Tell us about your space and how you will use it. This helps us size features and suggest the right components on the next step.',
											'Erzählen Sie uns von Ihrem Raum und der Nutzung. So können wir Elemente passend dimensionieren und auf dem nächsten Schritt vorschlagen.',
											'Recite nam o prostoru i načinu korištenja. To nam pomaže prilagoditi veličine i predložiti komponente u sljedećem koraku.'
										)}
									</p>
								</div>

								<div className="col-12 questionnaire-section-card">
									<h6 className="section-title">{copy('Size & location', 'Größe & Standort', 'Veličina i lokacija')}</h6>
									<div className="row g-3">
								<div className="col-12 col-md-6">
									<label className="form-label questionnaire-question-label">
										{copy('Garden Size', 'Gartengröße', 'Veličina vrta')} <span className="text-danger">*</span>
										<HelpTooltip text="The size helps us calculate material quantities, plant spacing, and design proportions for your garden." />
									</label>
									<div className="input-group">
										<input
											type="number"
											className={`form-control ${errors.gardenSize ? 'is-invalid' : ''}`}
											placeholder="50"
											min="1"
											value={form.gardenSize || ''}
											onChange={(e) => setForm({ ...form, gardenSize: parseFloat(e.target.value) || 0 })}
											aria-invalid={!!errors.gardenSize}
											ref={(el) => {
												if (errors.gardenSize) firstInvalidRef.current = el;
											}}
										/>
										<span className="input-group-text">m²</span>
									</div>
									{errors.gardenSize && <div className="invalid-feedback d-block">{errors.gardenSize}</div>}
								</div>

								<div className="col-12 col-md-6">
									<label className="form-label questionnaire-question-label">
										{copy('Location / City', 'Standort / Stadt', 'Lokacija / Grad')} <span className="text-danger">*</span>
										<HelpTooltip text="Your location helps us select climate-appropriate plants and understand local growing conditions. It also helps gardening companies find and respond to projects near them in the marketplace." />
									</label>
									<div className="input-group">
										<input
											type="text"
											className={`form-control ${errors.location ? 'is-invalid' : ''}`}
											placeholder="Berlin"
											value={form.location}
											onChange={(e) => setForm({ ...form, location: e.target.value })}
											aria-invalid={!!errors.location}
											ref={(el) => {
												if (errors.location) firstInvalidRef.current = el;
											}}
										/>
										{isGettingLocation && (
											<span className="input-group-text bg-white">
												<span className="spinner-border spinner-border-sm text-success" role="status" aria-hidden="true"></span>
											</span>
										)}
										<button
											type="button"
											className="btn btn-outline-success"
											onClick={() => handleUseMyLocation()}
											title={copy('Use my current location', 'Meinen aktuellen Standort verwenden', 'Koristi moju trenutnu lokaciju')}
											disabled={isGettingLocation}
										>
											<i className="bi bi-geo-alt-fill"></i>
										</button>
									</div>
									{permissionState === 'prompt' && !form.location && (
										<small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
											<i className="bi bi-info-circle me-1"></i>
											{copy('Click the location icon above to automatically detect your location', 'Klicken Sie oben auf das Standortsymbol, um Ihren Standort automatisch zu erkennen', 'Kliknite ikonu lokacije iznad za automatsko prepoznavanje vaše lokacije')}
										</small>
									)}
									{errors.location && <div className="invalid-feedback d-block">{errors.location}</div>}
								</div>

								<div className="col-12 col-md-6">
									<label className="form-label questionnaire-question-label">
										{copy('How many people should the garden accommodate? (Optional)', 'Für wie viele Personen soll der Garten ausgelegt sein? (Optional)', 'Koliko ljudi vrt treba primiti? (Opcionalno)')}
										<HelpTooltip text="Include everyone who will regularly use the garden — e.g. household members, or future family if you're planning ahead. This helps us scale the design and choose safe, suitable features." />
									</label>
									<input
										type="number"
										className="form-control"
										placeholder="2"
										min="0"
										max="20"
										value={form.gardenUsers ?? form.householdSize ?? ''}
										onChange={(e) => {
											const raw = e.target.value;
											const v = raw === '' ? undefined : parseInt(raw, 10);
											setForm((f) => ({ ...f, gardenUsers: v, householdSize: v }));
										}}
									/>
									<div className="form-text questionnaire-helper-text">{copy('Include everyone who will regularly use the garden (household or future family)', 'Beziehen Sie alle ein, die den Garten regelmäßig nutzen (Haushalt oder zukünftige Familie)', 'Uključite sve koji će redovito koristiti vrt (kućanstvo ili buduća obitelj)')}</div>
								</div>

								<div className="col-12 col-md-6">
									<label className="form-label questionnaire-question-label">
										{copy('What is your project focus?', 'Was ist Ihr Projektfokus?', 'Koji je fokus vašeg projekta?')}
									</label>
									<select
										className="form-select"
										value={form.projectFocus ?? ''}
										onChange={(e) =>
											setForm((f) => ({
												...f,
												projectFocus: (e.target.value || undefined) as QuestionnaireData['projectFocus']
											}))
										}
									>
										<option value="">{copy('Select project focus (optional)', 'Projektfokus wählen (optional)', 'Odaberite fokus projekta (opcionalno)')}</option>
										<option value="planting_only">{localizeProjectFocusLabel('planting_only')}</option>
										<option value="construction_hardscape_only">{localizeProjectFocusLabel('construction_hardscape_only')}</option>
										<option value="mixed_planting_construction">{localizeProjectFocusLabel('mixed_planting_construction')}</option>
									</select>
									<div className="form-text questionnaire-helper-text">
										{copy('Helps gardeners/companies understand if you want only planting, only hardscape, or both.', 'Hilft Gärtnern/Firmen zu verstehen, ob Sie nur Bepflanzung, nur Hardscape oder beides möchten.', 'Pomaže vrtlarima/tvrtkama razumjeti želite li samo sadnju, samo hardscape ili oboje.')}
									</div>
								</div>

								<div className="col-12">
									<label className="form-label questionnaire-question-label">
										{copy('What matters most in your garden? (Optional)', 'Was ist Ihnen im Garten am wichtigsten? (Optional)', 'Što vam je u vrtu najvažnije? (Opcionalno)')}
										<HelpTooltip text="Select all that apply. This helps us prioritise the right features (e.g. seating and circulation for hosting)." />
									</label>
									<div className="form-text questionnaire-helper-text mb-2">{copy('Select all that apply', 'Mehrfachauswahl möglich', 'Odaberite sve što vrijedi')}</div>
									<div className="row g-2 g-md-3">
										{GARDEN_USE_OPTIONS.map((opt) => {
											const isSelected = (form.gardenUses ?? []).includes(opt.id);
											return (
												<div key={opt.id} className="col-6 col-md-4 col-lg">
													<div
														role="button"
														tabIndex={0}
														onClick={() => {
															const next = isSelected
																? (form.gardenUses ?? []).filter((k) => k !== opt.id)
																: [...(form.gardenUses ?? []), opt.id];
															setForm((f) => ({
																...f,
																gardenUses: next,
																...(opt.id === 'hosting' && isSelected ? { hostingMaxPeople: undefined } : {}),
																...(opt.id === 'kids_pets' && isSelected ? { kidsCount: undefined, kidsAges: undefined, petsDescription: undefined } : {})
															}));
														}}
														onKeyDown={(e) => {
															if (e.key === 'Enter' || e.key === ' ') {
																e.preventDefault();
																const next = isSelected
																	? (form.gardenUses ?? []).filter((k) => k !== opt.id)
																	: [...(form.gardenUses ?? []), opt.id];
																setForm((f) => ({
																	...f,
																	gardenUses: next,
																	...(opt.id === 'hosting' && isSelected ? { hostingMaxPeople: undefined } : {}),
																	...(opt.id === 'kids_pets' && isSelected ? { kidsCount: undefined, kidsAges: undefined, petsDescription: undefined } : {})
																}));
															}
														}}
														className={`d-flex align-items-center gap-2 p-3 rounded-3 border text-decoration-none text-body ${
															isSelected
																? 'border-success border-2 bg-success bg-opacity-10'
																: 'border-secondary border-opacity-25 bg-light bg-opacity-50'
														}`}
														style={{ cursor: 'pointer', transition: 'all 0.2s ease' }}
													>
														<i className={`${opt.icon} flex-shrink-0 ${isSelected ? 'text-success' : 'text-muted'}`} style={{ fontSize: '1.35rem' }} aria-hidden />
														<span className="small fw-medium">{localizeGardenUseShortLabel(opt.id, opt.shortLabel)}</span>
														{isSelected && (
															<span className="ms-auto text-success" aria-hidden>
																<i className="bi bi-check-circle-fill" style={{ fontSize: '1rem' }} />
															</span>
														)}
													</div>
												</div>
											);
										})}
									</div>
									{(form.gardenUses ?? []).includes('hosting') && (
										<div className="mt-3 col-12 col-sm-6 col-md-4">
											<label className="form-label small text-muted mb-1">{copy('When hosting, up to how many people?', 'Bei Veranstaltungen, bis zu wie viele Personen?', 'Kod druženja, do koliko ljudi?')}</label>
											<input
												type="number"
												className="form-control"
												placeholder={copy('e.g. 15 or 20', 'z. B. 15 oder 20', 'npr. 15 ili 20')}
												min="1"
												max="100"
												value={form.hostingMaxPeople ?? ''}
												onChange={(e) => setForm({ ...form, hostingMaxPeople: parseInt(e.target.value) || undefined })}
											/>
										</div>
									)}
									{(form.gardenUses ?? []).includes('kids_pets') && (
										<div className="mt-3 col-12">
											<label className="form-label small text-muted mb-1">{copy('Tell us a bit more (optional)', 'Erzählen Sie uns etwas mehr (optional)', 'Recite nam još malo (opcionalno)')}</label>
											<p className="form-text small text-muted mb-2">{copy('Helps us suggest the right features (e.g. play area size, dog house, pet-safe plants).', 'Hilft uns, passende Elemente vorzuschlagen (z. B. Größe des Spielbereichs, Hundehütte, haustiersichere Pflanzen).', 'Pomaže nam predložiti prave značajke (npr. veličina igrališta, kućica za psa, biljke sigurne za kućne ljubimce).')}</p>
											<div className="row g-2 g-md-3">
												<div className="col-12 col-sm-6 col-md-3">
													<label className="form-label small mb-0">{copy('Number of kids', 'Anzahl der Kinder', 'Broj djece')}</label>
													<input
														type="number"
														className="form-control form-control-sm"
														placeholder={copy('e.g. 2', 'z. B. 2', 'npr. 2')}
														min="0"
														max="15"
														value={form.kidsCount ?? ''}
														onChange={(e) => setForm({ ...form, kidsCount: parseInt(e.target.value) || undefined })}
													/>
												</div>
												<div className="col-12 col-sm-6 col-md-4">
													<label className="form-label small mb-0">{copy('Kids\' ages', 'Alter der Kinder', 'Dob djece')}</label>
													<input
														type="text"
														className="form-control form-control-sm"
														placeholder={copy('e.g. 3 and 7', 'z. B. 3 und 7', 'npr. 3 i 7')}
														value={form.kidsAges ?? ''}
														onChange={(e) => setForm({ ...form, kidsAges: e.target.value || undefined })}
													/>
												</div>
												<div className="col-12 col-md-5">
													<label className="form-label small mb-0">{copy('Pets', 'Haustiere', 'Kućni ljubimci')}</label>
													<input
														type="text"
														className="form-control form-control-sm"
														placeholder={copy('e.g. 1 dog, would like a dog house', 'z. B. 1 Hund, wir möchten eine Hundehütte', 'npr. 1 pas, želimo kućicu za psa')}
														value={form.petsDescription ?? ''}
														onChange={(e) => setForm({ ...form, petsDescription: e.target.value || undefined })}
													/>
												</div>
											</div>
										</div>
									)}
								</div>

									</div>
								</div>

								<div className="col-12 questionnaire-section-card">
									<h6 className="section-title">{copy('Your vision', 'Ihre Vision', 'Vaša vizija')}</h6>
									<div className="col-12">
										<label className="form-label questionnaire-question-label">
											{copy('Describe your garden (optional)', 'Beschreiben Sie Ihren Garten (optional)', 'Opišite svoj vrt (opcionalno)')}
											<HelpTooltip text="If you already know what you want, write it here. If you are not sure yet, leave this blank and pick features on the next step." />
										</label>
										<p className="text-muted small mb-2">
											{copy(
												'Already know what you want? Describe it here and we will use it for your plan. Not sure yet? Leave blank and choose features on the next step.',
												'Sie wissen schon, was Sie möchten? Beschreiben Sie es hier. Noch unsicher? Leer lassen und Elemente im nächsten Schritt wählen.',
												'Već znate što želite? Opišite ovdje. Još niste sigurni? Ostavite prazno i odaberite značajke u sljedećem koraku.'
											)}
										</p>
										<textarea
											className="form-control"
											rows={4}
											placeholder={copy('What do you want from your garden? (e.g., seating area, play space for kids, vegetable garden, low maintenance, privacy...)', 'Was wünschen Sie sich für Ihren Garten? (z. B. Sitzbereich, Spielfläche, Gemüsegarten, pflegeleicht, Privatsphäre...)', 'Što želite od svog vrta? (npr. prostor za sjedenje, igralište, povrtnjak, malo održavanja, privatnost...)')}
											value={form.needsWants}
											onChange={(e) => setForm({ ...form, needsWants: e.target.value })}
										/>
										<p className="form-text questionnaire-example-text mb-0">
											{copy('e.g. seating area, play space for kids, vegetable garden, low maintenance, privacy', 'z. B. Sitzbereich, Spielfläche, Gemüsegarten, pflegeleicht, Privatsphäre', 'npr. prostor za sjedenje, igralište, povrtnjak, malo održavanja, privatnost')}
										</p>
										<p className="form-text questionnaire-helper-text mt-2 mb-0">
											{copy('You can set maintenance and planting preferences on the final step under Extra details.', 'Pflege- und Pflanzpräferenzen legen Sie im letzten Schritt unter Zusätzliche Details fest.', 'Preferencije održavanja i sadnje postavljate u zadnjem koraku pod Dodatni detalji.')}
										</p>
									</div>
								</div>
							</>
						)}

						{currentStep === 4 && (
							<>
								<div className="col-12">
									<p className="text-muted mb-3">
										{copy(
											'Choose the features you want. Tap a card to select; use Photos to see examples.',
											'Wählen Sie gewünschte Elemente. Tippen Sie eine Karte zum Auswählen; „Fotos“ zeigt Beispiele.',
											'Odaberite željene značajke. Dodirnite karticu za odabir; „Fotografije“ prikazuje primjere.'
										)}
									</p>
									{(form.needsWants || '').trim().length > 0 && (
										<div className="alert alert-light border mb-4">
											<div className="small text-muted mb-1">{copy('From your description', 'Aus Ihrer Beschreibung', 'Iz vašeg opisa')}</div>
											<p className="mb-0 small">{form.needsWants.trim()}</p>
										</div>
									)}
								</div>

								<div className="col-12 questionnaire-section-card">
									<h6 className="section-title">{copy('Garden components', 'Gartenkomponenten', 'Komponente vrta')}</h6>
								<div className="col-12">
									<label className="form-label questionnaire-question-label d-block mb-0">
										{copy('Select the components you\'d like in your garden', 'Wählen Sie die Komponenten, die Sie in Ihrem Garten möchten', 'Odaberite komponente koje želite u svom vrtu')}
										<HelpTooltip text="Choose which features you'd like in your garden. You can select multiple components." />
									</label>

									<div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mt-2 mb-3 gap-2">
										<div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-2">
											<div className="small text-muted d-flex flex-wrap align-items-center gap-3">
												{aiAnalysis?.recommendedComponents && aiAnalysis.recommendedComponents.length > 0 && (
													<span className="d-inline-flex align-items-center gap-1">
														<i className="bi bi-stars" style={{ color: '#198754' }} aria-hidden />
														<span>{copy('Photo analysis suggested some components for you.', 'Die Fotoanalyse hat Ihnen einige Komponenten vorgeschlagen.', 'Analiza fotografije predložila vam je neke komponente.')}</span>
													</span>
												)}
												<span className="d-inline-flex align-items-center gap-1">
													<i className="bi bi-star-fill" style={{ color: '#198754' }} aria-hidden />
													<span>{copy('Photo', 'Foto', 'Foto')}</span>
												</span>
												<span className="d-inline-flex align-items-center gap-1">
													<i className="bi bi-star-fill" style={{ color: '#28a86a' }} aria-hidden />
													<span>{copy('Style', 'Stil', 'Stil')}</span>
												</span>
												<span className="d-inline-flex align-items-center gap-1">
													<i className="bi bi-star-fill" style={{ color: '#8fd19e' }} aria-hidden />
													<span>{copy('Use', 'Nutzung', 'Namjena')}</span>
												</span>
											</div>

											{hasAnyRecommendations && (
												<div className="d-flex flex-wrap gap-2">
													<button
														type="button"
														className={`btn btn-sm ${
															isAllRecommendationFilterActive ? 'btn-success' : 'btn-outline-success'
														}`}
														onClick={handleSelectAllRecommendedComponents}
													>
														{copy('All', 'Alle', 'Sve')}
													</button>
													{hasPhotoRecommendations && (
														<button
															type="button"
															className={`btn btn-sm ${
																activeRecommendationFilters.photo ? 'btn-success' : 'btn-outline-success'
															}`}
															onClick={handleSelectPhotoRecommendedComponents}
														>
															{copy('Photo', 'Foto', 'Foto')}
														</button>
													)}
													{hasStyleRecommendations && (
														<button
															type="button"
															className={`btn btn-sm ${
																activeRecommendationFilters.style ? 'btn-success' : 'btn-outline-success'
															}`}
															onClick={handleSelectStyleRecommendedComponents}
														>
															{copy('Style', 'Stil', 'Stil')}
														</button>
													)}
													{hasUseRecommendations && (
														<button
															type="button"
															className={`btn btn-sm ${
																activeRecommendationFilters.use ? 'btn-success' : 'btn-outline-success'
															}`}
															onClick={handleSelectUseRecommendedComponents}
														>
															{copy('Use', 'Nutzung', 'Namjena')}
														</button>
													)}
												</div>
											)}
										</div>
									</div>
									{COMPONENT_GROUPS.map((group) => {
										const componentsInGroup = GARDEN_COMPONENTS.filter((c) => c.group === group.id);
										if (componentsInGroup.length === 0) return null;
										return (
											<div key={group.id} className="mb-4">
												<h6 className="text-muted mb-2 d-flex align-items-center gap-2">
													<span style={{ fontSize: '1.25rem' }}>{group.icon}</span>
													{localizeComponentGroupLabel(group.id, group.label)}
												</h6>
												<div className="row g-3">
													{componentsInGroup.map((component) => {
														const isSelected = form.selectedComponents?.includes(component.id) || false;
														const photoRecommendation = aiAnalysis?.recommendedComponents?.find(
															(rec) => rec.componentId === component.id
														);
														const translatedPhotoRecommendation = translatedRecommendedComponentsById.get(component.id);
														const feasibility = componentFeasibilityMap.get(component.id);
														const translatedFeasibility = translatedFeasibilityById.get(component.id);
														const isTightFit = feasibility?.fit === 'tight';
														const isNotRecommendedFit = feasibility?.fit === 'not_recommended';
														const isPhotoRecommended = !!photoRecommendation;
														const isStyleRecommended = styleRecommendedComponentIds.includes(component.id);
														const isUseRecommended = useRecommendedComponentIds.includes(component.id);
														const componentCost = form.gardenSize > 0 ? getComponentCost(component.id, form.gardenSize) : null;
														const hasComponentPreview = (COMPONENT_PREVIEW_URLS[component.id]?.length ?? 0) > 0;
														const heroImageUrl = getComponentHeroImageUrl(component.id);
														return (
															<div className="col-12 col-md-6 col-lg-4" key={component.id}>
																<div
																	className={`card h-100 border border-2 position-relative questionnaire-component-picker-card overflow-hidden ${
																		isSelected ? 'border-success shadow-sm is-selected' : ''
																	}`}
																	style={{
																		cursor: 'pointer',
																		transition: 'all 0.2s',
																		...(isSelected ? {} : { borderColor: 'rgba(25, 135, 84, 0.35)' })
																	}}
																	onClick={() => {
																		const current = form.selectedComponents || [];
																		if (isSelected) {
																			setForm({ ...form, selectedComponents: current.filter(id => id !== component.id) });
																		} else {
																		if (isNotRecommendedFit) {
																			const suggestedAltId = (feasibility?.suggestedAlternative || '').trim();
																			const altComponent = GARDEN_COMPONENTS.find((c) => c.id === suggestedAltId);
																			setFeasibilityModal({
																				componentId: component.id,
																				componentLabel: localizeComponentLabel(component.id, component.label),
																				reasoning: translatedFeasibility?.reasoning || feasibility?.reasoning || copy(
																					'This component may not be realistic in your current space.',
																					'Diese Komponente ist in Ihrem aktuellen Raum möglicherweise nicht realistisch.',
																					'Ova komponenta možda nije realna u vašem trenutnom prostoru.'
																				),
																				alternativeId: altComponent?.id,
																				alternativeLabel: localizeComponentLabel(altComponent?.id, altComponent?.label)
																			});
																			return;
																		}
																		setForm({ ...form, selectedComponents: [...current, component.id] });
																		}
																	}}
																>
													{heroImageUrl ? (
														<div
															className="questionnaire-component-picker-media"
															style={{
																backgroundImage: `url(${heroImageUrl})`,
																backgroundPosition: getComponentHeroBackgroundPosition(component.id)
															}}
															aria-hidden
														>
															<div className="questionnaire-component-picker-scrim" />
															{(isPhotoRecommended || isStyleRecommended || isUseRecommended) && (
																<div className="questionnaire-component-picker-stars">
																	{isPhotoRecommended && (
																		<i className="bi bi-star-fill" style={{ color: '#198754' }} aria-hidden />
																	)}
																	{isStyleRecommended && (
																		<i className="bi bi-star-fill" style={{ color: '#28a86a' }} aria-hidden />
																	)}
																	{isUseRecommended && (
																		<i className="bi bi-star-fill" style={{ color: '#8fd19e' }} aria-hidden />
																	)}
																</div>
															)}
															{hasComponentPreview && (
																<button
																	type="button"
																	className="questionnaire-component-picker-preview-btn"
																	onClick={(e) => {
																		e.stopPropagation();
																		openComponentPreview(component.id);
																	}}
																	aria-label={`${copy('Preview examples for', 'Beispiele ansehen für', 'Pregled primjera za')} ${localizeComponentLabel(component.id, component.label)}`}
																>
																	<i className="bi bi-images" aria-hidden />
																	<span className="visually-hidden">{copy('Photos', 'Fotos', 'Fotografije')}</span>
																</button>
															)}
															{isSelected && (
																<span className="questionnaire-component-picker-check" aria-hidden>
																	<i className="bi bi-check-circle-fill" />
																</span>
															)}
														</div>
													) : (
														<div className="questionnaire-component-picker-media questionnaire-component-picker-media--fallback" aria-hidden>
															<span className="questionnaire-component-picker-fallback-icon">{component.icon}</span>
														</div>
													)}
													<div className="card-body questionnaire-component-picker-body">
														<div className="d-flex align-items-start gap-2">
															<div className="flex-grow-1 min-w-0">
																<div className="fw-bold">{localizeComponentLabel(component.id, component.label)}</div>
																<div className="d-flex flex-wrap gap-1 mt-1">
																	{isNotRecommendedFit && (
																		<span className="badge text-bg-danger">{copy('Not recommended', 'Nicht empfohlen', 'Nije preporučeno')}</span>
																	)}
																	{isTightFit && (
																		<span className="badge text-bg-warning">{copy('Tight fit', 'Knapp passend', 'Tijesno uklapanje')}</span>
																	)}
																</div>
																{photoRecommendation && (
																	<div className="small text-muted mt-1" style={{ fontSize: '0.85rem' }}>
																		{translatedPhotoRecommendation?.reasoning || photoRecommendation.reasoning}
																	</div>
																)}
																{feasibility?.reasoning && (
																	<div className={`small mt-1 ${isNotRecommendedFit ? 'text-danger' : isTightFit ? 'text-warning-emphasis' : 'text-muted'}`}>
																		{translatedFeasibility?.reasoning || feasibility.reasoning}
																		{(translatedFeasibility?.suggestedAlternative || feasibility.suggestedAlternative) ? ` ${copy('Try', 'Versuchen Sie', 'Pokušajte')}: ${localizeComponentLabel(translatedFeasibility?.suggestedAlternative || feasibility.suggestedAlternative, translatedFeasibility?.suggestedAlternative || feasibility.suggestedAlternative)}.` : ''}
																	</div>
																)}
																{componentCost && (
																	<div className="small mt-2">
																		<strong className="text-muted">{copy('Est. cost:', 'Geschätzte Kosten:', 'Procijenjeni trošak:')}</strong>{' '}
																		€{Math.round(componentCost.mid).toLocaleString()}
																		<span className="text-muted ms-1">
																			(€{Math.round(componentCost.min).toLocaleString()} - €{Math.round(componentCost.max).toLocaleString()})
																		</span>
																	</div>
																)}
																{!heroImageUrl && hasComponentPreview && (
																	<button
																		type="button"
																		className="btn btn-link btn-sm px-0 mt-1"
																		onClick={(e) => {
																			e.stopPropagation();
																			openComponentPreview(component.id);
																		}}
																	>
																		{copy('View photos', 'Fotos ansehen', 'Pogledaj fotografije')}
																	</button>
																)}
															</div>
															{isSelected && !heroImageUrl && (
																<i className="bi bi-check-circle-fill text-success flex-shrink-0" style={{ fontSize: '1.35rem' }} aria-hidden />
															)}
														</div>
													</div>
																</div>
															</div>
														);
													})}
												</div>
											</div>
										);
									})}
									{isOverMajorCapacity && (
										<div className="alert alert-warning mt-3 mb-0">
											<strong>Space warning:</strong> You selected {selectedMajorComponentsCount} large components, but this space is estimated to handle around {recommendedMaxMajorComponents}. The final design may look crowded or unrealistically scaled.
										</div>
									)}
									{isOverTotalDensity && (
										<div className="alert alert-warning mt-3 mb-0">
											<strong>Density warning:</strong> For a {Math.round(form.gardenSize)} m² space, selecting {selectedComponentIds.length} components is likely to create a cluttered or unrealistic layout. Consider focusing on a smaller set of priorities.
										</div>
									)}
									{hasRedundantWaterCombo && (
										<div className="alert alert-info mt-3 mb-0">
											<strong>Possible overlap:</strong> Selecting both a pool and a hot tub is often redundant in compact gardens. You may get a cleaner plan by choosing one as the primary water feature.
										</div>
									)}
									{selectedNotRecommendedComponents.length > 0 && (
										<div className="alert alert-warning mt-3 mb-0">
											<strong>{copy('Some selections may not be realistic for this space:', 'Einige Auswahlen sind für diesen Raum möglicherweise nicht realistisch:', 'Neki odabiri možda nisu realni za ovaj prostor:')}</strong>{' '}
											{selectedNotRecommendedComponents
												.map((entry) => {
													const label = GARDEN_COMPONENTS.find((c) => c.id === entry.id)?.label ?? entry.id;
													return entry.feasibility?.suggestedAlternative
														? `${label} (try ${entry.feasibility.suggestedAlternative})`
														: label;
												})
												.join(', ')}
										</div>
									)}
									
									{/* Dynamic Budget Display */}
									{SHOW_BUDGET_QUESTIONS && form.selectedComponents && form.selectedComponents.length > 0 && form.gardenSize > 0 && (() => {
										const costEstimate = calculateTotalCost(form.selectedComponents, form.gardenSize);
										const budgetStatus = form.budgetRange ? getBudgetStatus(costEstimate, form.budgetRange) : null;
										
										return (
											<div className="mt-3">
												<div className={`alert ${
													!form.budgetRange ? 'alert-secondary' :
													budgetStatus?.status === 'within' ? 'alert-success' :
													budgetStatus?.status === 'close' ? 'alert-warning' :
													budgetStatus?.status === 'over' ? 'alert-warning' :
													'alert-danger'
												} mb-0`}>
													<h6 className="alert-heading">
														<i className={`bi ${
															!form.budgetRange ? 'bi-info-circle' :
															budgetStatus?.status === 'within' ? 'bi-check-circle' :
															budgetStatus?.status === 'close' ? 'bi-exclamation-triangle' :
															'bi-x-circle'
														} me-2`}></i>
														Estimated Cost: €{Math.round(costEstimate.mid).toLocaleString()} 
														<span className="text-muted ms-2">
															(€{Math.round(costEstimate.min).toLocaleString()} - €{Math.round(costEstimate.max).toLocaleString()})
														</span>
													</h6>
													<div className="mb-2">
														<strong>Selected:</strong> {form.selectedComponents.length} component{form.selectedComponents.length !== 1 ? 's' : ''}
													</div>
													{form.budgetRange && budgetStatus && (
														<>
															<p className="mb-2">
																<strong>Your Budget:</strong> €{form.budgetRange.min.toLocaleString()} - €{form.budgetRange.max.toLocaleString()}
															</p>
															<p className="mb-2">{budgetStatus.message}</p>
															{budgetStatus.suggestions.length > 0 && (
																<div>
																	<strong>Suggestions:</strong>
																	<ul className="mb-0 mt-1">
																		{budgetStatus.suggestions.map((suggestion, idx) => (
																			<li key={idx}>{suggestion}</li>
																		))}
																	</ul>
																</div>
															)}
														</>
													)}
													{!form.budgetRange && (
														<p className="mb-0 small text-muted">
															<em>Use the budget section below to see if this estimate fits your budget.</em>
														</p>
													)}
												</div>
											</div>
										);
									})()}
								</div>
								</div>

								{/* 4. Budget & project details */}
								{SHOW_BUDGET_QUESTIONS && (
								<div className="col-12 questionnaire-section-card">
									<h6 className="section-title">Budget & project details</h6>
									{/* Budget preference slider */}
									<div className="col-12 mb-4">
										<label className="form-label d-block">
											Budget preference
											<HelpTooltip text="Slide left for more budget-friendly options (value materials, simpler designs). Slide right for premium options (higher-end materials, more elaborate features)." />
										</label>
										<div className="d-flex align-items-center gap-3 flex-wrap">
											<span className="small text-muted" style={{ minWidth: '90px' }}>Value-focused</span>
											<input
												type="range"
												className="form-range flex-grow-1"
												min={0}
												max={100}
												value={form.budgetPreference ?? 50}
												onChange={(e) => setForm({ ...form, budgetPreference: parseInt(e.target.value, 10) })}
												style={{ maxWidth: '280px' }}
												aria-label="Budget preference from value-focused to premium"
											/>
											<span className="small text-muted" style={{ minWidth: '70px' }}>Premium</span>
										</div>
										<div className="form-text mt-1">
											<strong>Value-focused</strong> = we prioritise cost-effective materials and simpler solutions.{' '}
											<strong>Premium</strong> = we suggest higher-end materials and more elaborate features. You can also enter an exact budget below.
										</div>
									</div>

									{/* Single budget input (optional) */}
									<div className="col-12 col-md-6 mb-4">
										<label className="form-label">
											Budget in € (optional)
											<HelpTooltip text="If you have a specific total budget in mind, enter it in euros. We'll use this together with your preference slider to tailor the plan." />
										</label>
										<input
											type="number"
											className="form-control"
											placeholder="Total budget €"
											min="0"
											value={form.budgetRange?.min || ''}
											onChange={(e) => {
												const value = parseFloat(e.target.value);
												if (!value || value <= 0) {
													setForm({
														...form,
														budgetRange: undefined,
													});
												} else {
													setForm({
														...form,
														budgetRange: {
															min: value,
															max: value,
														},
													});
												}
											}}
										/>
										<div className="form-text">Leave blank if you prefer to decide later or rely on the slider above.</div>
										
										{/* Budget Feedback when budget is set */}
										{form.budgetRange && form.budgetRange.min > 0 && form.selectedComponents && form.selectedComponents.length > 0 && form.gardenSize > 0 && (() => {
											const costEstimate = calculateTotalCost(form.selectedComponents, form.gardenSize);
											const budgetStatus = getBudgetStatus(costEstimate, form.budgetRange);
											
											return (
												<div className="mt-3">
													<div className={`alert ${
														budgetStatus.status === 'within' ? 'alert-success' :
														budgetStatus.status === 'close' ? 'alert-warning' :
														budgetStatus.status === 'over' ? 'alert-warning' :
														'alert-danger'
													} mb-0`}>
														<h6 className="alert-heading">
															<i className={`bi ${
																budgetStatus.status === 'within' ? 'bi-check-circle' :
																budgetStatus.status === 'close' ? 'bi-exclamation-triangle' :
																'bi-x-circle'
															} me-2`}></i>
															Budget check
														</h6>
														<p className="mb-2">
															<strong>Your budget:</strong> €{form.budgetRange.min.toLocaleString()}<br />
															<strong>Estimated cost:</strong> €{Math.round(costEstimate.mid).toLocaleString()}{' '}
															<span className="text-muted"> (€{Math.round(costEstimate.min).toLocaleString()} – €{Math.round(costEstimate.max).toLocaleString()})</span>
														</p>
														<p className="mb-2">{budgetStatus.message}</p>
														{budgetStatus.suggestions.length > 0 && (
															<div>
																<strong>To fit your budget, consider:</strong>
																<ul className="mb-0 mt-1">
																	{budgetStatus.suggestions.map((suggestion, idx) => (
																		<li key={idx}>{suggestion}</li>
																	))}
																</ul>
															</div>
														)}
														{budgetStatus.status !== 'within' && (
															<p className="mb-0 mt-2 small">
																<em>We'll try to adjust the design to fit your budget; you can also move the budget preference slider toward &quot;Value-focused&quot;.</em>
															</p>
														)}
													</div>
												</div>
											);
										})()}
									</div>

									<div className="row g-3">
										<div className="col-12 col-md-6">
											<label className="form-label">
												Earliest start date (optional)
												<HelpTooltip text="The earliest date you're okay for work on the garden to begin. Companies will see this when making offers." />
											</label>
											<div className="input-group">
												<span
													className="input-group-text bg-white"
													role="button"
													onClick={() => {
														const input = projectStartDateInputRef.current;
														if (!input) return;
														// Focus the input so keyboard users aren't surprised
														input.focus();
														// Use native date picker where supported
														const withPicker = input as HTMLInputElement & {
															showPicker?: () => void;
														};
														withPicker.showPicker?.();
													}}
												>
													<i className="bi bi-calendar3 text-muted"></i>
												</span>
												<input
													type="date"
													className="form-control"
													min={new Date().toISOString().split('T')[0]}
													value={form.projectStartDate || ''}
													onChange={(e) => setForm({ ...form, projectStartDate: e.target.value || undefined })}
													ref={projectStartDateInputRef}
												/>
											</div>
											<div className="form-text">When you're okay for work to begin — leave blank if flexible</div>
										</div>

										{SHOW_CONTACT_EMAIL_QUESTION && (
										<div className="col-12 col-md-6">
											<label className="form-label">
												Contact Email <span className="text-danger">*</span>
												<HelpTooltip text="We'll use this to send your garden plan and any updates." />
											</label>
											<input
												type="email"
												className={`form-control ${errors.contactEmail ? 'is-invalid' : ''}`}
												placeholder="you@example.com"
												value={contactEmail}
												onChange={(e) => setContactEmail(e.target.value)}
												aria-invalid={!!errors.contactEmail}
												ref={(el) => {
													if (errors.contactEmail) firstInvalidRef.current = el;
												}}
											/>
											{errors.contactEmail && <div className="invalid-feedback d-block">{errors.contactEmail}</div>}
										</div>
										)}
									</div>
								</div>
								)}

							</>
						)}

						{/* Step 5: Optional follow-up questions */}
						{currentStep === 5 && (
							<>
								<div className="col-12">
									<h5 className="questionnaire-optional-header">✨ {copy('Want to personalize it a bit more?', 'Möchten Sie es noch etwas persönlicher machen?', 'Želite ga još malo personalizirati?')}</h5>
									<p className="questionnaire-optional-sub">{copy('Takes ~30 seconds · Totally optional', 'Dauert ~30 Sekunden · Komplett optional', 'Traje ~30 sekundi · Potpuno opcionalno')}</p>
									<p className="text-muted small mb-4">
										{copy(
											'These questions help create a more detailed plan tailored to you. You can skip any or all of them and we\'ll still choose sensible options based on what you\'ve shared so far.',
											'Diese Fragen helfen, einen detaillierteren, auf Sie zugeschnittenen Plan zu erstellen. Sie können einzelne oder alle Fragen überspringen, und wir wählen trotzdem sinnvolle Optionen basierend auf Ihren bisherigen Angaben.',
											'Ova pitanja pomažu izraditi detaljniji plan prilagođen vama. Možete preskočiti neka ili sva pitanja, a mi ćemo i dalje odabrati razumne opcije na temelju onoga što ste do sada podijelili.'
										)}
									</p>
									
									{/* Style Recommendations */}
									{form.styleKey && (
										<div className="alert alert-success mb-4">
											<h6 className="alert-heading mb-1">
												{copy('Style recommendations for', 'Stilempfehlungen für', 'Preporuke stila za')} {localizeStyleTitle(form.styleKey, stylePresetsByKey[form.styleKey]?.title)}
											</h6>
											<p className="mb-0 small">{translatedStyleRecommendationText || localizeStyleRecommendationText(rawStyleRecommendationText)}</p>
											<p className="mb-0 mt-2 small text-muted">
												<em>{copy('These are suggestions - feel free to choose what you prefer!', 'Dies sind Vorschläge - wählen Sie gerne, was Ihnen am besten passt!', 'Ovo su prijedlozi - slobodno odaberite ono što vam najviše odgovara!')}</em>
											</p>
										</div>
									)}
									
									{isGeneratingQuestions && (
										<div className="mb-4">
											<MagnifierAnimation 
												message={copy('Generating your follow-up questions...', 'Zusatzfragen werden erstellt...', 'Generiram dodatna pitanja...')}
												subMessage={copy('Based on your photo and selections', 'Basierend auf Ihrem Foto und Ihren Auswahloptionen', 'Na temelju vaše fotografije i odabira')}
												imageUrl={photoPreviewUrl || undefined}
											/>
										</div>
									)}
									
									{questionsForDisplay.length > 0 ? (
										<>
											<div className="questionnaire-step3-intro mb-4">
												<div className="questionnaire-step3-intro-inner">
													<span className="questionnaire-step3-intro-badge">{copy('Optional', 'Optional', 'Opcionalno')}</span>
													<h6 className="questionnaire-step3-intro-title mb-1">{copy('Pick the topics you care about', 'Wählen Sie die Themen, die Ihnen wichtig sind', 'Odaberite teme koje su vam važne')}</h6>
													<p className="questionnaire-step3-intro-text mb-0">
														{copy('Open a card to answer a few quick questions, or skip everything — your plan will still work.', 'Öffnen Sie eine Karte, beantworten Sie ein paar kurze Fragen oder überspringen Sie alles — Ihr Plan funktioniert trotzdem.', 'Otvorite karticu i odgovorite na nekoliko kratkih pitanja, ili sve preskočite — vaš plan će i dalje raditi.')}
													</p>
												</div>
											</div>
											<div className="row g-3">
											{(() => {
												// Render component question cards only for the components that are
												// currently selected on the components step. We keep `allQuestions` (and its
												// `ai_q_{idx}` indices) stable and only filter what the user sees.
												const componentsToAddNow = filterComponentsToAdd(
													form.selectedComponents || [],
													form.needsWants,
													aiAnswers
												);
												const activeComponentSet = new Set(componentsToAddNow);
												const groupsForDisplay = getQuestionGroups(questionsForDisplay).filter(grp => {
													return grp.groupId === 'ai' || activeComponentSet.has(grp.groupId);
												});
												return groupsForDisplay.map((grp) => (
												<div className="col-12 col-md-6" key={grp.groupId}>
													{(() => {
														const visibleItems = grp.items.filter((item) => isQuestionVisible(item, grp.gateIndex));
														const isExpanded = !!expandedQuestionGroups[grp.groupId];
														const emoji = STEP3_GROUP_EMOJI[grp.groupId] ?? '📌';
														const groupHeroUrl = grp.groupId === 'ai' ? undefined : getComponentHeroImageUrl(grp.groupId);
														const baseLabel = grp.groupId === 'ai'
															? copy('Extra details', 'Zusätzliche Details', 'Dodatni detalji')
															: grp.groupLabel;
														const answeredCount = visibleItems.reduce((count, { index: idx }) => {
															const value = aiAnswers[`ai_q_${idx}`];
															if (Array.isArray(value)) {
																return count + (value.some((v) => String(v ?? '').trim().length > 0) ? 1 : 0);
															}
															if (typeof value === 'string') {
																return count + (value.trim().length > 0 ? 1 : 0);
															}
															return count + (value !== undefined && value !== null ? 1 : 0);
														}, 0);
														const extraDetailAnsweredCount =
															grp.groupId === 'ai'
																? [form.maintenanceLevel, form.plantDensityPreference].filter(Boolean).length
																: 0;
														const hasComment = (aiQuestionComments[`group_${grp.groupId}`] || '').trim().length > 0;
														const effectiveAnsweredCount = answeredCount + extraDetailAnsweredCount;
														const hasProgress = effectiveAnsweredCount > 0 || hasComment;
														const totalOptionalCount = visibleItems.length + (grp.groupId === 'ai' ? 2 : 0);
														const unansweredCount = Math.max(0, totalOptionalCount - effectiveAnsweredCount);
														const progressLabel =
															effectiveAnsweredCount > 0 && hasComment
																? `${effectiveAnsweredCount} answered + note`
																: effectiveAnsweredCount > 0
																	? `${effectiveAnsweredCount} answered`
																	: copy('Note added', 'Notiz hinzugefügt', 'Napomena dodana');
														return (
															<div
																className={`questionnaire-step3-group-card h-100 ${isExpanded ? 'is-expanded' : ''} ${hasProgress ? 'is-complete' : ''}`}
															>
																<button
																	type="button"
																	className="questionnaire-step3-group-toggle"
																	onClick={() =>
																		setExpandedQuestionGroups((prev) => ({
																			...prev,
																			[grp.groupId]: !prev[grp.groupId]
																		}))
																	}
																	aria-expanded={isExpanded}
																	aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${baseLabel}`}
																>
																	{groupHeroUrl ? (
																		<span
																			className="questionnaire-step3-group-icon questionnaire-step3-group-icon--photo"
																			style={{
																				backgroundImage: `url(${groupHeroUrl})`,
																				backgroundPosition: getComponentHeroBackgroundPosition(grp.groupId)
																			}}
																			aria-hidden
																		/>
																	) : (
																		<span className="questionnaire-step3-group-icon" aria-hidden>
																			{emoji}
																		</span>
																	)}
																	<span className="questionnaire-step3-group-text text-start">
																		<span className="questionnaire-step3-group-title">{baseLabel}</span>
																		<span className="questionnaire-step3-group-sub">
																			{totalOptionalCount}{' '}
																			{totalOptionalCount === 1
																				? copy('question', 'Frage', 'pitanje')
																				: copy('questions', 'Fragen', 'pitanja')}{' '}
																			· {copy('optional', 'optional', 'opcionalno')}
																		</span>
																	</span>
																	<span className="questionnaire-step3-group-meta">
																		{!isExpanded && hasProgress && (
																			<span className="questionnaire-step3-group-status">
																				<i className="bi bi-check2-circle" aria-hidden></i>
																				{progressLabel}
																			</span>
																		)}
																		{!isExpanded && unansweredCount > 0 && (
																			<span className="questionnaire-step3-group-pending">
																				{unansweredCount} left
																			</span>
																		)}
																		<span className="questionnaire-step3-group-count">
																			{totalOptionalCount}
																		</span>
																		<i
																			className="bi bi-chevron-down questionnaire-step3-group-chevron"
																			aria-hidden
																		></i>
																	</span>
																</button>
																{isExpanded && (
																	<div className="questionnaire-step3-group-body">
																		<div className="form-text questionnaire-helper-text mb-3">
																			{copy('Not sure? Leave answers blank — we\'ll pick sensible defaults.', 'Unsicher? Lassen Sie Antworten leer — wir wählen sinnvolle Standardwerte.', 'Niste sigurni? Ostavite odgovore prazne — odabrat ćemo razumne zadane vrijednosti.')}
																		</div>
																		{grp.groupId === 'ai' && (
																			<div className="questionnaire-question-card mb-4">
																				<div className="row g-3">
																					<div className="col-12 col-md-6">
																						<label className="form-label questionnaire-question-label">
																							{copy('Maintenance Level (Optional)', 'Pflegeaufwand (Optional)', 'Razina održavanja (Opcionalno)')}
																							<HelpTooltip text="This helps us select plants and features that match the time you can commit to garden upkeep." />
																						</label>
																						<select
																							className="form-select"
																							value={form.maintenanceLevel || ''}
																							onChange={(e) => setForm({ ...form, maintenanceLevel: e.target.value as QuestionnaireData['maintenanceLevel'] || undefined })}
																						>
																							<option value="">{copy('Select maintenance level...', 'Pflegeaufwand auswählen...', 'Odaberite razinu održavanja...')}</option>
																							<option value="low">{copy('Low - Minimal upkeep, self-sufficient plants', 'Niedrig - Minimaler Aufwand, robuste Pflanzen', 'Niska - Minimalno održavanje, samoodržive biljke')}</option>
																							<option value="medium">{copy('Medium - Regular care, balanced effort', 'Mittel - Regelmäßige Pflege, ausgewogener Aufwand', 'Srednja - Redovita njega, uravnotežen trud')}</option>
																							<option value="high">{copy('High - Intensive care, hands-on gardening', 'Hoch - Intensive Pflege, aktive Gartenarbeit', 'Visoka - Intenzivna njega, aktivno vrtlarenje')}</option>
																						</select>
																					</div>
																					<div className="col-12 col-md-6">
																						<label className="form-label questionnaire-question-label">
																							{copy('Plant Density Preference (Optional)', 'Pflanzdichte (Optional)', 'Gustoća sadnje (Opcionalno)')}
																							<HelpTooltip text="How much planting would you like? This helps us balance plants and flowers with open space in your garden design." />
																						</label>
																						<select
																							className="form-select"
																							value={form.plantDensityPreference || ''}
																							onChange={(e) => setForm({ ...form, plantDensityPreference: e.target.value as QuestionnaireData['plantDensityPreference'] || undefined })}
																						>
																							<option value="">{copy('Select preference...', 'Präferenz auswählen...', 'Odaberite preferenciju...')}</option>
																							<option value="minimal">{copy('Minimal - More open space, less plants and flowers', 'Minimal - Mehr offene Fläche, weniger Pflanzen und Blumen', 'Minimalna - Više otvorenog prostora, manje biljaka i cvijeća')}</option>
																							<option value="moderate">{copy('Moderate - Balanced mix of plants and open space', 'Mittel - Ausgewogene Mischung aus Pflanzen und offenem Raum', 'Umjerena - Uravnotežen omjer biljaka i otvorenog prostora')}</option>
																							<option value="dense">{copy('Dense - Lots of plants, flowers, and bushes - lush and full', 'Dicht - Viele Pflanzen, Blumen und Sträucher', 'Gusta - Puno biljaka, cvijeća i grmova')}</option>
																						</select>
																					</div>
																				</div>
																			</div>
																		)}
																		{visibleItems.map(({ question, index: idx }) => (
															<div className="mb-4 questionnaire-question-card" key={idx}>
																<label className="form-label d-block questionnaire-question-label">
																	{question.question}
																	{question.reasoning && (
																		<HelpTooltip text={question.reasoning} />
																	)}
																</label>
																{question.allowMultiple && (
																	<div className="form-text questionnaire-helper-text">
																		{copy('Select all options that apply.', 'Wählen Sie alle passenden Optionen.', 'Odaberite sve opcije koje vrijede.')}
																	</div>
																)}
																{(() => {
																	const rec = Array.isArray(question.recommendedAnswers)
																		? question.recommendedAnswers.filter(a => String(a).trim().toLowerCase() !== 'let ai decide')
																		: [];
																	if (!rec.length) return null;
																	return (
																		<div className="form-text mb-2 questionnaire-helper-text">
																			<strong>{copy('Suggested:', 'Vorgeschlagen:', 'Predloženo:')}</strong>{' '}
																			{rec.join(', ')}
																		</div>
																	);
																})()}
																<div className="mt-2">
																{question.type === 'multiple_choice' && question.options && (() => {
																	const rawOpts = Array.isArray(question.options) ? question.options : [String(question.options)];
																	const opts = rawOpts.filter(o => String(o).trim().toLowerCase() !== 'let ai decide');
																	const effectiveOpts = opts.length > 0 ? opts : rawOpts;
																	const displayOpts = effectiveOpts.map((o: string) => {
																		const s = String(o).trim();
																		if (s.startsWith('[') && s.endsWith(']')) return s.slice(1, -1).trim();
																		return s;
																	});
																	const currentVal = aiAnswers[`ai_q_${idx}`];
																	const isMulti = question.allowMultiple === true;
																	const selectedSet = isMulti && Array.isArray(currentVal)
																		? new Set((currentVal as string[]).map(v => String(v).trim()))
																		: new Set([currentVal].filter(Boolean).map(v => String(v).trim()));
																	const isOptionSelected = (rawOption: string, displayText: string) =>
																		selectedSet.has(String(rawOption).trim()) || selectedSet.has(String(displayText).trim());
																	return (
																		<div className={isMulti ? 'd-flex flex-wrap gap-2' : 'btn-group flex-wrap'} role="group">
																			{displayOpts.map((displayText, optIdx) => {
																				const rawOption = effectiveOpts[optIdx];
																				const isSelected = isOptionSelected(rawOption, displayText);
																				const toggle = () => {
																					if (isMulti) {
																						const arr = (Array.isArray(currentVal) ? currentVal : []) as string[];
																						const normalized = String(rawOption).trim();
																						if (isSelected) {
																							const next = arr.filter(v => String(v).trim() !== normalized);
																							setAiAnswers(prev => ({ ...prev, [`ai_q_${idx}`]: next }));
																						} else {
																							setAiAnswers(prev => ({ ...prev, [`ai_q_${idx}`]: [...arr, rawOption] }));
																						}
																					} else {
																						if (isSelected) {
																							setAiAnswers(prev => {
																								const next = { ...prev };
																								delete next[`ai_q_${idx}`];
																								return next;
																							});
																						} else {
																							setAiAnswers(prev => ({ ...prev, [`ai_q_${idx}`]: rawOption }));
																						}
																					}
																				};
																				return (
																	<button
																	key={optIdx}
																	type="button"
																	className={`btn ${isMulti ? (isSelected ? 'btn-success' : 'btn-outline-secondary') : (isSelected ? 'btn-success' : 'btn-outline-secondary')}`}
																	onClick={toggle}
																	>
																	{displayText}
																	</button>
																				);
																			})}
																		</div>
																	);
																})()}
																{question.type === 'yes_no' && (() => {
																	const current = aiAnswers[`ai_q_${idx}`];
																	const toggleYes = () => setAiAnswers(prev => ({ ...prev, [`ai_q_${idx}`]: current === 'yes' ? undefined : 'yes' }));
																	const toggleNo = () => setAiAnswers(prev => ({ ...prev, [`ai_q_${idx}`]: current === 'no' ? undefined : 'no' }));
																	return (
																		<div className="btn-group" role="group">
																			<button
																				type="button"
																				className={`btn ${current === 'yes' ? 'btn-success' : 'btn-outline-success'}`}
																				onClick={toggleYes}
																			>
																				{copy('Yes', 'Ja', 'Da')}
																			</button>
																			<button
																				type="button"
																				className={`btn ${current === 'no' ? 'btn-danger' : 'btn-outline-danger'}`}
																				onClick={toggleNo}
																			>
																				{copy('No', 'Nein', 'Ne')}
																			</button>
																		</div>
																	);
																})()}
																{question.type === 'text' && (
																	<textarea
																		className={`form-control ${errors[`ai_q_${idx}`] ? 'is-invalid' : ''}`}
																		rows={3}
																		placeholder={copy('Your answer...', 'Ihre Antwort...', 'Vaš odgovor...')}
																		value={aiAnswers[`ai_q_${idx}`] || ''}
																		onChange={(e) => setAiAnswers(prev => ({ ...prev, [`ai_q_${idx}`]: e.target.value }))}
																		aria-invalid={!!errors[`ai_q_${idx}`]}
																		ref={(el) => {
																			if (errors[`ai_q_${idx}`]) firstInvalidRef.current = el;
																		}}
																	/>
																)}
																{question.type === 'number' && (
																	<input
																		type="number"
																		className={`form-control ${errors[`ai_q_${idx}`] ? 'is-invalid' : ''}`}
																		placeholder={copy('Enter a number...', 'Zahl eingeben...', 'Unesite broj...')}
																		value={aiAnswers[`ai_q_${idx}`] || ''}
																		onChange={(e) => setAiAnswers(prev => ({ ...prev, [`ai_q_${idx}`]: e.target.value }))}
																		aria-invalid={!!errors[`ai_q_${idx}`]}
																		ref={(el) => {
																			if (errors[`ai_q_${idx}`]) firstInvalidRef.current = el;
																		}}
																	/>
																)}
																</div>
																{errors[`ai_q_${idx}`] && <div className="invalid-feedback d-block">{errors[`ai_q_${idx}`]}</div>}
															</div>
																		))}
																		{/* One optional note per group instead of per question */}
																		<div className="mt-3 questionnaire-optional-sub">
																			<label className="form-label small text-muted mb-1">
																				<i className="bi bi-chat-left-text me-1" aria-hidden></i>
																				{copy('Anything else about this section? (optional)', 'Noch etwas zu diesem Abschnitt? (optional)', 'Još nešto o ovom odjeljku? (opcionalno)')}
																			</label>
																			<textarea
																				className="form-control form-control-sm"
																				rows={2}
																				placeholder={copy('e.g. preferences not listed above...', 'z. B. Präferenzen, die oben nicht aufgeführt sind...', 'npr. preferencije koje nisu gore navedene...')}
																				value={aiQuestionComments[`group_${grp.groupId}`] || ''}
																				onChange={(e) => setAiQuestionComments(prev => ({ ...prev, [`group_${grp.groupId}`]: e.target.value }))}
																				aria-label={`Optional note for ${grp.groupLabel}`}
																			/>
																		</div>
																		<div className="d-flex justify-content-end mt-3">
																			<button
																				type="button"
																				className="btn btn-sm btn-outline-success questionnaire-step3-done-btn"
																				onClick={() =>
																					setExpandedQuestionGroups((prev) => ({
																						...prev,
																						[grp.groupId]: false
																					}))
																				}
																			>
																				<i className="bi bi-check2 me-1" aria-hidden></i>
																				{copy('Done', 'Fertig', 'Gotovo')}
																			</button>
																		</div>
																	</div>
																)}
															</div>
														);
													})()}
												</div>
											));
											})()}
											</div>
										</>
									) : !isGeneratingQuestions ? (
										<div className="alert alert-info">
											<i className="bi bi-info-circle me-2"></i>
											{copy('No additional questions available. This usually means no photo was uploaded and no specific components were selected.', 'Keine zusätzlichen Fragen verfügbar. Meist wurde kein Foto hochgeladen und es wurden keine bestimmten Komponenten ausgewählt.', 'Nema dodatnih pitanja. To obično znači da nije učitana fotografija i da nisu odabrane određene komponente.')}
											<br />
											<small className="text-muted">{copy('You can continue to the next step.', 'Sie können mit dem nächsten Schritt fortfahren.', 'Možete nastaviti na sljedeći korak.')}</small>
										</div>
									) : null}
								</div>
							</>
						)}

					</form>
				</div>
			</div>
			</div>

			{/* Footer outside card — always visible at bottom */}
			<div className="questionnaire-footer">
				<div className="d-flex justify-content-between gap-2 align-items-center questionnaire-footer-actions">
					<div>
						{currentStep > 1 && (
								<button
								type="button"
								className="btn btn-outline-secondary"
								onClick={handleBack}
								disabled={isQuestionnaireInteractionLocked}
							>
									<i className="bi bi-arrow-left me-1"></i> {copy('Back', 'Zurück', 'Natrag')}
							</button>
						)}
					</div>
					<div className="d-flex gap-2">
						{currentStep < steps.length ? (
							<>
								{!guestMode && (currentStep > 1 || !!editingPlanId) && (
									<button
										type="button"
										className="btn btn-outline-secondary"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleSaveDraft().catch(err => {
												console.error('Error saving draft:', err);
											});
										}}
										disabled={isSaving || isQuestionnaireInteractionLocked}
									>
										{isSaving ? (
											<>
												<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
												{copy('Saving...', 'Speichern...', 'Spremanje...')}
											</>
										) : (
											editingPlanId ? copy('Save Changes', 'Änderungen speichern', 'Spremi promjene') : copy('Save Draft', 'Entwurf speichern', 'Spremi skicu')
										)}
									</button>
								)}
								<button
									type="button"
									className="btn btn-primary questionnaire-footer-primary-btn"
									onClick={handleNext}
									disabled={
										isSaving ||
										isAnalyzing ||
										isGeneratingQuestions ||
										isGeneratingPlan ||
										isPreparingGeneration ||
										isQuestionnaireInteractionLocked ||
										(currentStep === 1 && !photoFile && !photoPreviewUrl)
									}
								>
									{copy('Next', 'Weiter', 'Dalje')} <i className="bi bi-arrow-right ms-1"></i>
								</button>
							</>
						) : (
							<>
								{!guestMode && (
									<button
										type="button"
										className="btn btn-outline-secondary"
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleSaveDraft().catch(err => {
												console.error('Error saving draft:', err);
											});
										}}
										disabled={isSaving || isQuestionnaireInteractionLocked}
									>
										{isSaving ? (
											<>
												<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
												{copy('Saving...', 'Speichern...', 'Spremanje...')}
											</>
										) : (
											editingPlanId ? copy('Save Changes', 'Änderungen speichern', 'Spremi promjene') : copy('Save Draft', 'Entwurf speichern', 'Spremi skicu')
										)}
									</button>
								)}
								<button
									type="button"
									className="btn btn-primary questionnaire-footer-primary-btn"
									onClick={() => {
										void handleGeneratePlan();
									}}
									disabled={isSaving || isAnalyzing || isGeneratingQuestions || isGeneratingPlan || isPreparingGeneration || isQuestionnaireInteractionLocked}
								>
									{isSaving || isGeneratingPlan || isPreparingGeneration ? (
										<>
											<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
											{isPreparingGeneration
												? copy('Preparing your photo…', 'Ihr Foto wird vorbereitet…', 'Pripremamo vašu fotografiju…')
												: copy("We're designing your garden…", 'Wir gestalten Ihren Garten…', 'Dizajniramo vaš vrt…')}
										</>
									) : (
										<>
											🌿 {copy('Create my garden plan', 'Meinen Gartenplan erstellen', 'Kreiraj moj vrtni plan')}
										</>
									)}
								</button>
							</>
						)}
					</div>
				</div>
			</div>
			{currentStep >= 4 && (
				<div className="questionnaire-mobile-budget-above-footer d-lg-none" aria-live="polite">
					<div className="questionnaire-footer-cost-label">
						<i className="bi bi-calculator me-1" aria-hidden />
						{copy('Estimated cost', 'Geschätzte Kosten', 'Procijenjeni trošak')}
					</div>
					{topBarCostEstimate ? (
						<>
							<div className="questionnaire-footer-cost-range">
								€{Math.round(topBarCostEstimate.min).toLocaleString()} - €{Math.round(topBarCostEstimate.max).toLocaleString()}
							</div>
							<div className="questionnaire-footer-cost-mid">
								~€{Math.round(topBarCostEstimate.mid).toLocaleString()} {copy('mid-range', 'Mittelwert', 'srednji raspon')}
							</div>
						</>
					) : (
						<div className="questionnaire-footer-cost-placeholder">
							{copy('Add size and components to see estimate', 'Fügen Sie Größe und Komponenten hinzu, um eine Schätzung zu sehen', 'Dodajte veličinu i komponente za procjenu')}
						</div>
					)}
				</div>
			)}

			{/* Example garden photos (step 1) — backdrop click closes */}
			{showPhotoExamplesModal && (
				<div
					className="modal d-block"
					style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 3000 }}
					aria-modal="true"
					role="dialog"
					aria-labelledby="questionnaire-photo-examples-title"
					onClick={() => setShowPhotoExamplesModal(false)}
				>
					<div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title" id="questionnaire-photo-examples-title">
									{copy('Example photos', 'Beispielfotos', 'Primjeri fotografija')}
								</h5>
								<button
									type="button"
									className="btn-close"
									aria-label={copy('Close', 'Schließen', 'Zatvori')}
									onClick={() => setShowPhotoExamplesModal(false)}
								/>
							</div>
							<div className="modal-body">
								<div
									className="rounded-2 overflow-hidden bg-body-secondary d-flex align-items-center justify-content-center"
									style={{ minHeight: '200px' }}
								>
									<img
										src={examplePhotoCurrentUrl}
										alt={`${copy('Example garden photo', 'Beispiel-Gartenfoto', 'Primjer fotografije vrta')} ${examplePhotoSafeIndex + 1} ${copy('of', 'von', 'od')} ${examplePhotoCount}`}
										className="img-fluid d-block"
										style={{ maxHeight: 'min(420px, 60vh)', width: '100%', objectFit: 'contain' }}
									/>
								</div>
								<div className="d-flex align-items-center justify-content-between gap-2 mt-3">
									<button
										type="button"
										className="btn btn-outline-secondary btn-sm"
										onClick={() =>
											setExamplePhotoIndex((i) => (i - 1 + examplePhotoCount) % examplePhotoCount)
										}
										disabled={examplePhotoCount === 0}
										aria-label={copy('Previous example photo', 'Vorheriges Beispielfoto', 'Prethodna primjer fotografija')}
									>
										<i className="bi bi-chevron-left" aria-hidden />
									</button>
									<span className="small text-muted" aria-live="polite">
										{examplePhotoSafeIndex + 1} / {examplePhotoCount}
									</span>
									<button
										type="button"
										className="btn btn-outline-secondary btn-sm"
										onClick={() => setExamplePhotoIndex((i) => (i + 1) % examplePhotoCount)}
										disabled={examplePhotoCount === 0}
										aria-label={copy('Next example photo', 'Nächstes Beispielfoto', 'Sljedeća primjer fotografija')}
									>
										<i className="bi bi-chevron-right" aria-hidden />
									</button>
								</div>
								<p className="small text-muted mb-0 mt-3">
									{copy(
										'Flip through these sample photos to get a feel for what works—they are not your garden, just inspiration for the kind of shot that helps us.',
										'Blättern Sie durch diese Beispielfotos, um ein Gefühl dafür zu bekommen, was gut funktioniert - es ist nicht Ihr Garten, sondern nur Inspiration für die Art von Aufnahme, die uns hilft.',
										'Prolistajte ove primjere fotografija da dobijete osjećaj što najbolje funkcionira - to nije vaš vrt, nego inspiracija za vrstu fotografije koja nam pomaže.'
									)}
								</p>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Component preview examples (step 2) */}
			{previewingComponentId && (
				<div
					className="modal d-block"
					style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 3000 }}
					aria-modal="true"
					role="dialog"
					aria-labelledby="questionnaire-component-preview-title"
					onClick={() => setPreviewingComponentId(null)}
				>
					<div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title" id="questionnaire-component-preview-title">
									{localizeComponentLabel(previewComponent?.id, previewComponent?.label)} {copy('examples', 'Beispiele', 'primjeri')}
								</h5>
								<button
									type="button"
									className="btn-close"
									aria-label={copy('Close', 'Schließen', 'Zatvori')}
									onClick={() => setPreviewingComponentId(null)}
								/>
							</div>
							<div className="modal-body">
								<div
									className="rounded-2 overflow-hidden bg-body-secondary d-flex align-items-center justify-content-center"
									style={{ minHeight: '200px' }}
								>
									<img
										src={componentPreviewCurrentUrl}
										alt={`${localizeComponentLabel(previewComponent?.id, previewComponent?.label)} ${copy('example', 'Beispiel', 'primjer')} ${componentPreviewSafeIndex + 1} ${copy('of', 'von', 'od')} ${componentPreviewCount}`}
										className="img-fluid d-block"
										style={{ maxHeight: 'min(480px, 65vh)', width: '100%', objectFit: 'contain' }}
									/>
								</div>
								<div className="d-flex align-items-center justify-content-between gap-2 mt-3">
									<button
										type="button"
										className="btn btn-outline-secondary btn-sm"
										onClick={() =>
											setComponentPreviewIndex((i) => (i - 1 + componentPreviewCount) % componentPreviewCount)
										}
										disabled={componentPreviewCount <= 1}
										aria-label={copy('Previous component example', 'Vorheriges Komponentenbeispiel', 'Prethodni primjer komponente')}
									>
										<i className="bi bi-chevron-left" aria-hidden />
									</button>
									<span className="small text-muted" aria-live="polite">
										{componentPreviewCount > 0 ? `${componentPreviewSafeIndex + 1} / ${componentPreviewCount}` : copy('No previews', 'Keine Vorschauen', 'Nema pregleda')}
									</span>
									<button
										type="button"
										className="btn btn-outline-secondary btn-sm"
										onClick={() => setComponentPreviewIndex((i) => (i + 1) % componentPreviewCount)}
										disabled={componentPreviewCount <= 1}
										aria-label={copy('Next component example', 'Nächstes Komponentenbeispiel', 'Sljedeći primjer komponente')}
									>
										<i className="bi bi-chevron-right" aria-hidden />
									</button>
								</div>
								{(translatedPreviewComponentDetails || previewComponentDetails) && (
									<div className="mt-4">
										<p className="mb-3">{(translatedPreviewComponentDetails || previewComponentDetails)?.whatItIs}</p>

										<div className="mb-3">
											<div className="small text-uppercase text-muted fw-semibold mb-1">{copy('Common types', 'Häufige Typen', 'Uobičajene vrste')}</div>
											<div className="d-flex flex-wrap gap-2">
												{(translatedPreviewComponentDetails || previewComponentDetails)?.commonTypes.map((type) => (
													<span key={type} className="badge rounded-pill text-bg-light border">
														{type}
													</span>
												))}
											</div>
										</div>

										{(translatedPreviewComponentDetails || previewComponentDetails)?.goodFor && (translatedPreviewComponentDetails || previewComponentDetails)!.goodFor!.length > 0 && (
											<div>
												<div className="small text-uppercase text-muted fw-semibold mb-1">{copy('Good for', 'Gut geeignet für', 'Dobro za')}</div>
												<div className="d-flex flex-wrap gap-2">
													{(translatedPreviewComponentDetails || previewComponentDetails)!.goodFor!.map((use) => (
														<span key={use} className="badge rounded-pill text-bg-success-subtle border border-success-subtle text-success-emphasis">
															{use}
														</span>
													))}
												</div>
											</div>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Report "garden not detected" modal */}
			{showReportModal && (
				<div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000 }} aria-modal="true" role="dialog">
					<div className="modal-dialog modal-dialog-centered">
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">{copy('Report as garden', 'Als Garten melden', 'Prijavi kao vrt')}</h5>
								<button type="button" className="btn-close" aria-label={copy('Close', 'Schließen', 'Zatvori')} onClick={() => !isSubmittingReport && setShowReportModal(false)} disabled={isSubmittingReport} />
							</div>
							<div className="modal-body">
								<p className="text-muted small mb-3">
									{copy(
										'We\'ll send this photo to our team for review. You can add an optional message (e.g. "This is my backyard" or "Rooftop terrace").',
										'Wir senden dieses Foto zur Prüfung an unser Team. Sie können eine optionale Nachricht hinzufügen (z. B. "Das ist mein Hinterhof" oder "Dachterrasse").',
										'Poslat ćemo ovu fotografiju našem timu na pregled. Možete dodati opcionalnu poruku (npr. "Ovo je moje dvorište" ili "Krovna terasa").'
									)}
								</p>
								<label className="form-label small">{copy('Message (optional)', 'Nachricht (optional)', 'Poruka (opcionalno)')}</label>
								<textarea
									className="form-control"
									rows={3}
									placeholder={copy('e.g. This is my backyard, the angle might be unusual', 'z. B. Das ist mein Hinterhof, der Winkel ist vielleicht ungewöhnlich', 'npr. Ovo je moje dvorište, kut snimanja je možda neobičan')}
									value={reportMessage}
									onChange={(e) => setReportMessage(e.target.value)}
									disabled={isSubmittingReport}
								/>
							</div>
							<div className="modal-footer">
								<button type="button" className="btn btn-outline-secondary" onClick={() => setShowReportModal(false)} disabled={isSubmittingReport}>
									{copy('Cancel', 'Abbrechen', 'Odustani')}
								</button>
								<button type="button" className="btn btn-primary" onClick={handleSubmitReport} disabled={isSubmittingReport}>
									{isSubmittingReport ? (
										<><span className="spinner-border spinner-border-sm me-2" aria-hidden /> {copy('Sending...', 'Wird gesendet...', 'Šalje se...')}</>
									) : (
										copy('Submit report', 'Meldung senden', 'Pošalji prijavu')
									)}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
			{feasibilityModal && (
				<div
					className="modal d-block"
					style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000 }}
					aria-modal="true"
					role="dialog"
					onClick={() => setFeasibilityModal(null)}
				>
					<div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">
									<i className="bi bi-exclamation-triangle text-warning me-2" aria-hidden />
									{copy('Might not fit well', 'Passt möglicherweise nicht gut', 'Možda se ne uklapa dobro')}
								</h5>
								<button
									type="button"
									className="btn-close"
									aria-label={copy('Close', 'Schließen', 'Zatvori')}
									onClick={() => setFeasibilityModal(null)}
								/>
							</div>
							<div className="modal-body">
								<p className="mb-2">
									<strong>{feasibilityModal.componentLabel}</strong> {copy('may not be realistic for this space.', 'ist für diesen Raum möglicherweise nicht realistisch.', 'možda nije realno za ovaj prostor.')}
								</p>
								<p className="text-muted mb-0">{feasibilityModal.reasoning}</p>
							</div>
							<div className="modal-footer d-flex justify-content-between">
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={() => setFeasibilityModal(null)}
								>
									{copy('Cancel', 'Abbrechen', 'Odustani')}
								</button>
								<div className="d-flex gap-2">
									{feasibilityModal.alternativeId && (
										<button
											type="button"
											className="btn btn-success"
											onClick={() => {
												addSelectedComponent(feasibilityModal.alternativeId!);
												setFeasibilityModal(null);
											}}
										>
											{copy('Use alternative:', 'Alternative verwenden:', 'Koristi alternativu:')} {feasibilityModal.alternativeLabel || feasibilityModal.alternativeId}
										</button>
									)}
									<button
										type="button"
										className="btn btn-warning"
										onClick={() => {
											addSelectedComponent(feasibilityModal.componentId);
											setFeasibilityModal(null);
										}}
									>
										{copy('Select anyway', 'Trotzdem auswählen', 'Svejedno odaberi')}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
			{showGenerateFitModal && (
				<div
					className="modal d-block"
					style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 3000 }}
					aria-modal="true"
					role="dialog"
					onClick={() => setShowGenerateFitModal(false)}
				>
					<div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
						<div className="modal-content">
							<div className="modal-header">
								<h5 className="modal-title">
									<i className="bi bi-exclamation-diamond text-warning me-2" aria-hidden />
									{copy('Check layout realism', 'Layout-Realismus prüfen', 'Provjerite realnost rasporeda')}
								</h5>
								<button
									type="button"
									className="btn-close"
									aria-label={copy('Close', 'Schließen', 'Zatvori')}
									onClick={() => setShowGenerateFitModal(false)}
								/>
							</div>
							<div className="modal-body">
								<p className="mb-2">
									{copy('Your current selection may create a crowded or unrealistic design for this space.', 'Ihre aktuelle Auswahl kann ein überfülltes oder unrealistisches Design für diesen Raum erzeugen.', 'Vaš trenutni odabir može stvoriti pretrpan ili nerealan dizajn za ovaj prostor.')}
								</p>
								<ul className="mb-0">
									{isOverMajorCapacity && (
										<li>
											{copy('Major components:', 'Hauptkomponenten:', 'Glavne komponente:')} {selectedMajorComponentsCount} {copy('selected', 'ausgewählt', 'odabrano')} ({copy('recommended around', 'empfohlen oko', 'preporučeno oko')} {recommendedMaxMajorComponents}).
										</li>
									)}
									{isOverTotalDensity && (
										<li>{copy('Total components:', 'Komponenten gesamt:', 'Ukupno komponenti:')} {selectedComponentIds.length} {copy('for', 'za', 'za')} {Math.round(form.gardenSize)} m².</li>
									)}
									{hasRedundantWaterCombo && (
										<li>{copy('Both pool and hot tub selected in a compact space.', 'Sowohl Pool als auch Whirlpool sind in einem kompakten Raum ausgewählt.', 'I bazen i hidromasažna kada odabrani su u kompaktnom prostoru.')}</li>
									)}
									{selectedNotRecommendedComponents.length > 0 && (
										<li>
											{copy('Includes components flagged as not recommended:', 'Enthält als nicht empfohlen markierte Komponenten:', 'Uključuje komponente označene kao nepreporučene:')} {selectedNotRecommendedComponents
												.map((entry) => localizeComponentLabel(entry.id, GARDEN_COMPONENTS.find((c) => c.id === entry.id)?.label ?? entry.id))
												.join(', ')}.
										</li>
									)}
								</ul>
							</div>
							<div className="modal-footer d-flex justify-content-between">
								<button
									type="button"
									className="btn btn-outline-secondary"
									onClick={() => setShowGenerateFitModal(false)}
								>
									{copy('Go back', 'Zurück', 'Vrati se')}
								</button>
								<div className="d-flex gap-2">
									<button
										type="button"
										className="btn btn-success"
										onClick={() => {
											setShowGenerateFitModal(false);
											void handleGeneratePlan({ force: true, autoOptimize: true });
										}}
									>
										{copy('Auto-optimize & generate', 'Automatisch optimieren & generieren', 'Automatski optimiziraj i generiraj')}
									</button>
									<button
										type="button"
										className="btn btn-warning"
										onClick={() => {
											setShowGenerateFitModal(false);
											void handleGeneratePlan({ force: true, autoOptimize: false });
										}}
									>
										{copy('Generate anyway', 'Trotzdem generieren', 'Svejedno generiraj')}
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}

			{guestMode && guestAuthGate !== 'idle' && (
				<div
					className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center px-3"
					style={{ zIndex: 2000, background: 'rgba(255, 255, 255, 0.97)' }}
					role="dialog"
					aria-modal="true"
					aria-labelledby="guest-plan-gate-title"
				>
					{guestAuthGate === 'teaser' ? (
						<div className="w-100" style={{ maxWidth: 520 }}>
							<PlanGenerationPuzzleAnimation
								beforeImageUrl={resolvedPhotoPreviewUrl || undefined}
								selectedComponents={form.selectedComponents}
								teaserMode
								subMessage="Getting things ready…"
								onTeaserComplete={() => {
									if (guestTeaserDoneRef.current) return;
									guestTeaserDoneRef.current = true;
									setGuestAuthGate('modal');
								}}
							/>
						</div>
					) : (
						<div className="card shadow border-0" style={{ maxWidth: 420, width: '100%' }}>
							<div className="card-body p-4 text-center">
								<div className="rounded-circle bg-success-subtle text-success d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 56, height: 56 }}>
									<i className="bi bi-lock-fill fs-4" aria-hidden />
								</div>
								<h2 className="h4 text-success-emphasis mb-2" id="guest-plan-gate-title">
									Unlock your garden plan
								</h2>
								<p className="text-muted small mb-4">
									Create a free account or sign in to see your personalised plan and visual—your answers are saved on this device and we&apos;ll pick up right where you left off.
								</p>
								<div className="d-grid gap-2">
									<Link
										to="/login?guestPlan=1&mode=register"
										className="btn btn-success btn-lg"
										onClick={() => suppressQuestionnaireLeaveRef.current = true}
									>
										Create account
									</Link>
									<Link
										to="/login?guestPlan=1&mode=login"
										className="btn btn-outline-success"
										onClick={() => suppressQuestionnaireLeaveRef.current = true}
									>
										I already have an account
									</Link>
									<button
										type="button"
										className="btn btn-link btn-sm text-muted"
										onClick={() => {
											guestTeaserDoneRef.current = false;
											setGuestAuthGate('idle');
										}}
									>
										Back to questionnaire
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			)}
		</div>
	);
}

